// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Heart, MessageCircle, Clock, Trophy, Bell, User, Home, BookOpen, Users, Settings, Play, Pause, RotateCcw, Edit3, Send, LogIn, LogOut, Target, Minus, Plus, Save, MapPin, FileText, X, ArrowLeft, ArrowRight, CheckCircle, Trash2, ChevronRight, Share2, UserPlus, UserCheck, UserX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';
import AuthModal from './auth/AuthModal';
import UserSettingsPage from './UserSettingsPage';
import Avatar from './common/Avatar';
import { meditationAPI, type MeditationData } from '../services/meditationAPI';
import prayerAPI, { type PrayerRecord, type PrayerStats } from '../services/prayerAPI';
import crewAPI, { type Crew } from '../services/crewAPI-new';
import { getAnnouncements, type Announcement } from '../services/notificationAPI';
import { generateUUID } from '../utils/uuid';
import { getTodayString, getTodayStart, getTodayEnd, formatDateTimeKST } from '../utils/date';
import { getRandomVerse, getNewRandomVerse, setCustomVerse as setCustomVerseManager, isCustomVerse, type Verse } from '../utils/verseManager';
import { initializeAutoLogin } from '../utils/apiInterceptor';
import MonitorDashboard from './MonitorDashboard';
import MonitorScreen from './MonitorScreen';
import { 
  GUEST_USER, 
  SAMPLE_FEED_DATA,
  DEFAULT_VERSE 
} from '../constants/sampleData';
import CrewSystem from './CrewSystem';
import MaintenanceNotice from './MaintenanceNotice';
import PrayerGoalModal from './prayer/PrayerGoalModal';
import type { 
  BaseUser, 
  ExtendedUser, 
  PrayerShareType, 
  ServerStats 
} from '../types/common';

const ChurchApp = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, accessToken, clearAuth, checkAuthExpiry } = useAuthStore();
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [activeTab, setActiveTab] = useState('home');

  // URL 파라미터 처리 상태
  const [sharedPostId, setSharedPostId] = useState<string | null>(null);
  const [pendingTabSwitch, setPendingTabSwitch] = useState<string | null>(null);
  const [sharedMemberId, setSharedMemberId] = useState<string | null>(null);
  const [sharedMeditationId, setSharedMeditationId] = useState<string | null>(null);

  // 아바타 URL을 절대 경로로 변환하는 함수
  const getAvatarUrl = (avatarPath: string | undefined) => {
    if (!avatarPath) return null;
    if (avatarPath.startsWith('http')) return avatarPath;
    // API_URL에서 /api 제거 (정적 파일은 직접 서빙되므로)
    let API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
    API_URL = API_URL.replace(/\/api$/, ''); // /api 제거
    // 레거시 경로 호환 처리
    const normalized = avatarPath.replace(/^\/api\/uploads\//, '/uploads/');
    return `${API_URL}${normalized}`;
  };
  const [prayerTimer, setPrayerTimer] = useState({ time: 0, isRunning: false, target: 60 });
  const [showTargetSetting, setShowTargetSetting] = useState(false);

  // 기도 타이머 localStorage 키
  const PRAYER_STORAGE_KEYS = {
    START_TIME: 'prayerStartTime',
    TARGET_MINUTES: 'prayerTargetMinutes',
    ACCUMULATED_TIME: 'prayerAccumulatedTime',
    IS_PAUSED: 'prayerIsPaused'
  };
  const [devotionLikes, setDevotionLikes] = useState({});
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [prayerHistory, setPrayerHistory] = useState<PrayerRecord[]>([]);
  const [prayerStats, setPrayerStats] = useState<PrayerStats | null>(null);
  const [isLoadingPrayerHistory, setIsLoadingPrayerHistory] = useState(false);
  
  // 묵상 관련 상태
  const [showMeditation, setShowMeditation] = useState(false);
  const [meditationStep, setMeditationStep] = useState(1);
  const [readingCount, setReadingCount] = useState(0);
  const [meditationData, setMeditationData] = useState({
    godsWill: '',
    growthChange: '',
    prayer: ''
  });
  const [isEditingVerse, setIsEditingVerse] = useState(false);
  // 오늘의 말씀 (랜덤 시스템)
  const [todayVerse, setTodayVerse] = useState<Verse | null>(null);
  const [customVerse, setCustomVerse] = useState({
    reference: '',
    text: ''
  });
  
  // 오늘의 묵상 데이터 (홈 화면용)
  const [todayMeditationData, setTodayMeditationData] = useState<MeditationData | null>(null);
  const [isLoadingTodayMeditation, setIsLoadingTodayMeditation] = useState(false);
  const [tempVerse, setTempVerse] = useState({
    reference: '',
    text: ''
  });
  const [prayerShareType, setPrayerShareType] = useState<'private' | 'public' | 'request'>('public');
  const [isNewMeditationSession, setIsNewMeditationSession] = useState(false); // 신규 묵상 세션 플래그
  
  // 묵상 DB 연동 상태
  const [todayMeditation, setTodayMeditation] = useState<MeditationData | null>(null);
  const [isSavingMeditation, setIsSavingMeditation] = useState(false);
  // 서버 상태(이전 연결 체크 UI 잔여 참조 안전 가드)
  const [serverStats, setServerStats] = useState<any | null>(null);
  
  // 묵상 히스토리 상태
  const [showMeditationHistory, setShowMeditationHistory] = useState(false);
  const [meditationHistory, setMeditationHistory] = useState<MeditationData[]>([]);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<MeditationData | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [meditationTotal, setMeditationTotal] = useState(0);
  const [showAllHistory, setShowAllHistory] = useState(false);

  // 기도 목표량 상태
  const [showPrayerGoalModal, setShowPrayerGoalModal] = useState(false);
  const [prayerGoals, setPrayerGoals] = useState({ dailyPrayerGoal: 30, weeklyPrayerGoal: 210 });

  // 기도 목표량 로드
  const loadPrayerGoals = async () => {
    if (!isAuthenticated || isGuestMode) return;
    
    try {
      const goals = await prayerAPI.getPrayerGoals();
      setPrayerGoals(goals);
    } catch (error) {
      console.error('기도 목표량 로드 실패:', error);
    }
  };

  // 기도 목표량 업데이트 핸들러
  const handlePrayerGoalUpdated = (goals: any) => {
    setPrayerGoals(goals);
  };

  // 피드 상태
  const [feedData, setFeedData] = useState<MeditationData[]>([]);
  const [feedPage, setFeedPage] = useState(1);
  const [hasMoreFeed, setHasMoreFeed] = useState(true);
  const [isLoadingMoreFeed, setIsLoadingMoreFeed] = useState(false);
  const [isLoadingFeed, setIsLoadingFeed] = useState(false);
  const [likedPosts, setLikedPosts] = useState<Set<string>>(new Set());
  
  // 피드 상세 모달 상태 (중복 제거)


  // 사용자 설정 페이지 상태
  const [showUserSettings, setShowUserSettings] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [initialViewportHeight, setInitialViewportHeight] = useState(0);
  
  // 알림 관련 상태
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [isLoadingAnnouncements, setIsLoadingAnnouncements] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [pendingJoinsByCrew, setPendingJoinsByCrew] = useState<any[]>([]);
  const [prayerRequests, setPrayerRequests] = useState<any[]>([]);
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  const [lastCheckedTime, setLastCheckedTime] = useState<number>(Date.now());

  const loadUserNotifications = async () => {
    if (!isAuthenticated || isGuestMode) {
      setNotificationCount(0);
      setPendingJoinsByCrew([]);
      setPrayerRequests([]);
      setHasNewNotifications(false);
      return;
    }
    try {
      // 1) 가입 요청: 내가 리더인 크루들의 PENDING 요청 수 집계
      const myCrewsList = await crewAPI.getMyCrews();
      const leaderCrews = myCrewsList.filter((c: any) => c.leaderId === user?.id || c.myRole === 'LEADER');
      const pendingPerCrew: any[] = [];
      for (const crew of leaderCrews) {
        try {
          const reqs = await crewAPI.getJoinRequests(crew.id);
          const pending = (reqs || []).filter((r: any) => r.status === 'PENDING');
          if (pending.length > 0) {
            pendingPerCrew.push({ crewId: crew.id, crewName: crew.name, count: pending.length });
          }
        } catch {}
      }

      // 2) 중보 기도 요청: 내 크루 피드 중 prayerShareType==='request'
      let prayerReqs: any[] = [];
      try {
        const feed = await meditationAPI.getMeditationFeed(1, 20, 'my-crews');
        prayerReqs = (feed.meditations || []).filter((m: any) => m.prayerShareType === 'request');
      } catch {}

      setPendingJoinsByCrew(pendingPerCrew);
      setPrayerRequests(prayerReqs);
      
      const totalCount = pendingPerCrew.reduce((s, x) => s + x.count, 0) + prayerReqs.length;
      setNotificationCount(totalCount);
      
      // 마지막 확인 시각 불러오기
      const lastChecked = parseInt(localStorage.getItem('notificationLastChecked') || '0');
      setLastCheckedTime(lastChecked);
      
      // 새 알림 여부 판단: 알림이 있고, 마지막 확인 이후 생성된 것이 있는 경우
      if (totalCount > 0) {
        // 크루 가입 요청과 기도 요청 모두 확인
        // 새로운 알림이 하나라도 있으면 배지 표시
        const hasNewJoinRequests = leaderCrews.some((crew: any) => {
          // 크루의 마지막 업데이트 시각이 lastChecked보다 이후면 새 알림
          return crew.updatedAt && new Date(crew.updatedAt).getTime() > lastChecked;
        });
        
        const hasNewPrayerRequests = prayerReqs.some((req: any) => {
          // 기도 요청의 생성 시각이 lastChecked보다 이후면 새 알림
          return req.createdAt && new Date(req.createdAt).getTime() > lastChecked;
        });
        
        setHasNewNotifications(hasNewJoinRequests || hasNewPrayerRequests || lastChecked === 0);
      } else {
        setHasNewNotifications(false);
      }
    } catch (e) {
      setNotificationCount(0);
      setHasNewNotifications(false);
    }
  };
  
  // 알림 모달 열기 - 확인한 것으로 처리
  const openNotificationModal = () => {
    setShowNotificationModal(true);
    setHasNewNotifications(false);
    const now = Date.now();
    setLastCheckedTime(now);
    localStorage.setItem('notificationLastChecked', now.toString());
    loadAnnouncements();
  };

  useEffect(() => {
    if (isAuthenticated && !isGuestMode) {
      loadUserNotifications();
      const t = setInterval(loadUserNotifications, 60000);
      return () => clearInterval(t);
    }
  }, [isAuthenticated, isGuestMode]);
  
  // 피드 상세보기 모달 상태
  const [showFeedDetailModal, setShowFeedDetailModal] = useState(false);
  const [selectedFeedItem, setSelectedFeedItem] = useState<any>(null);
  
  // 모바일 디버깅용 상태
  const [debugInfo, setDebugInfo] = useState<string[]>([]);
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [inputDebugInfo, setInputDebugInfo] = useState<any>({});

  // 간단 입력 모달 상태 (2/3/4단계용) - 전역(홈/묵상 공용)
  const [showSimpleInput, setShowSimpleInput] = useState(false);
  const [simpleField, setSimpleField] = useState<'godsWill' | 'growthChange' | 'prayer' | null>(null);
  const [simpleTitle, setSimpleTitle] = useState('');
  const [simplePlaceholder, setSimplePlaceholder] = useState('');
  const [simpleValue, setSimpleValue] = useState(''); // Controlled input value
  const simpleInputRef = useRef<HTMLTextAreaElement | null>(null);
  const [simpleCanClose, setSimpleCanClose] = useState(false);
  

  // 말씀 편집 모달 상태
  const [showVerseEditModal, setShowVerseEditModal] = useState(false);
  const [editingVerseReference, setEditingVerseReference] = useState('');
  const [editingVerseText, setEditingVerseText] = useState('');

  // 묵상 입력 모달 상태
  const [showMeditationInputModal, setShowMeditationInputModal] = useState(false);
  const [meditationInputField, setMeditationInputField] = useState<'godsWill' | 'growthChange' | 'prayer' | null>(null);
  const [meditationInputTitle, setMeditationInputTitle] = useState('');
  const [meditationInputPlaceholder, setMeditationInputPlaceholder] = useState('');
  const [meditationInputValue, setMeditationInputValue] = useState('');

  // 크루 상세 모달 상태
  

  // 현재 사용자의 크루 정보
  const [userCrew, setUserCrew] = useState<Crew | null>(null);
  const [isJoiningCrew, setIsJoiningCrew] = useState(false);
  
  // 사용자 크루 정보 로드
  const loadUserCrew = useCallback(async () => {
    if (isGuestMode || !isAuthenticated) {
      console.log('🎭 게스트 모드 또는 미인증 상태 - 크루 정보 로드 건너뛰기');
      return null;
    }
    
    try {
      console.log('🔄 사용자 크루 정보 로드 시작...');
      console.log('🔐 인증 상태:', { isAuthenticated, isGuestMode });
      console.log('🎫 토큰 존재:', !!localStorage.getItem('accessToken'));
      
      const crew = await crewAPI.getMyCrew();
      
      console.log('📊 API 응답 데이터:', crew);
      console.log('📊 크루 ID:', crew?.id);
      console.log('📊 크루 이름:', crew?.name);
      console.log('📊 크루 타입:', typeof crew);
      console.log('📊 크루 전체 데이터:', JSON.stringify(crew, null, 2));
      
      setMyCrew(crew);
      console.log('✅ 사용자 크루 정보 로드 완료:', crew?.name || '크루 없음');
      
      return crew;
    } catch (error) {
      console.error('❌ 사용자 크루 정보 로드 오류:', error);
      console.error('❌ 에러 타입:', typeof error);
      console.error('❌ 에러 메시지:', error instanceof Error ? error.message : String(error));
      setMyCrew(null);
      return null;
    }
  }, [isGuestMode, isAuthenticated]);


  // 인증 상태 변경 시 사용자 크루 정보 로드
  useEffect(() => {
    if (isAuthenticated && !isGuestMode) {
      console.log('🔄 인증 상태 변경 - 사용자 크루 정보 로드 시작');
      loadUserCrew();
    } else {
      console.log('🔄 인증 해제 - 사용자 크루 정보 초기화');
      setMyCrew(null);
    }
  }, [isAuthenticated, isGuestMode, loadUserCrew]);

  // 크루 탭 활성화 시 사용자 크루 정보 다시 로드
  useEffect(() => {
    if (activeTab === 'crews' && isAuthenticated && !isGuestMode) {
      console.log('👥 크루 탭 활성화 - 사용자 크루 정보 다시 로드');
      loadUserCrew().then(() => {
        console.log('✅ 크루 탭 - 사용자 크루 정보 로드 완료');
      });
    }
  }, [activeTab, isAuthenticated, isGuestMode, loadUserCrew]);

  // 오늘의 묵상 데이터 로드 (홈 화면용)
  const loadTodayMeditation = async () => {
    if (isLoadingTodayMeditation) return;
    
    try {
      setIsLoadingTodayMeditation(true);
      console.log('📖 오늘의 묵상 데이터 로드 시작');
      console.log('🔍 현재 상태:', { 
        isAuthenticated, 
        isGuestMode, 
        user: user?.name, 
        provider: user?.provider,
        accessToken: !!accessToken 
      });
      
      if (isGuestMode) {
        // 게스트 모드 - 샘플 데이터 또는 null
        console.log('🎭 게스트 모드: 오늘의 묵상 없음');
        setTodayMeditationData(null);
        return;
      }
      
      // 로그인 상태 확인
      if (!user || !accessToken) {
        console.log('🔐 로그인 필요: 오늘의 묵상 데이터 로드 건너뛰기');
        setTodayMeditationData(null);
        return;
      }
      
      // 오늘 날짜 기준으로 가장 최근 묵상 데이터 조회 (한국 시간 기준)
      const todayStart = getTodayStart(); // 한국 시간 00:00:00
      const todayEnd = getTodayEnd(); // 한국 시간 23:59:59
      const todayString = getTodayString(); // KST 기준 YYYY-MM-DD
      
      console.log('📅 [오늘의 말씀] 날짜 범위 (KST):', { 
        todayString,
        startUTC: todayStart.toISOString(),
        endUTC: todayEnd.toISOString(),
        startKST: formatDateTimeKST(todayStart),
        endKST: formatDateTimeKST(todayEnd)
      });
      
      const meditationHistoryResponse = await meditationAPI.getMeditationHistory();
      console.log('📚 묵상 히스토리 응답:', meditationHistoryResponse);
      
      // 오늘 날짜의 묵상 중 가장 최근 것 찾기 (step2 이상 완료된 것만)
      console.log('🔍 [오늘의 말씀 디버깅] 전체 묵상 데이터:', meditationHistoryResponse.meditations.map(m => ({
        id: m.id,
        date: m.date,
        dateType: typeof m.date,
        step2Complete: m.step2Complete,
        step4Complete: m.step4Complete,
        verseReference: m.verseReference
      })));
      
      const todayMeditations = meditationHistoryResponse.meditations.filter(meditation => {
        console.log('🔍 [필터링 체크]', {
          id: meditation.id,
          date: meditation.date,
          step2Complete: meditation.step2Complete,
          dateObj: new Date(meditation.date),
          todayStart,
          todayEnd,
          isInRange: new Date(meditation.date) >= todayStart && new Date(meditation.date) < todayEnd
        });
        
        if (!meditation.step2Complete) {
          console.log('❌ step2Complete 실패');
          return false;
        }
        
        // date 필드가 ISO-8601 DateTime 또는 YYYY-MM-DD 문자열일 수 있음
        const meditationDate = new Date(meditation.date);
        const isInRange = meditationDate >= todayStart && meditationDate < todayEnd;
        
        if (!isInRange) {
          console.log('❌ 날짜 범위 밖:', { meditationDate, todayStart, todayEnd });
        }
        
        return isInRange;
      });
      console.log('🔍 오늘의 묵상 필터링 결과:', todayMeditations);
      
      if (todayMeditations.length > 0) {
        // 가장 최근 묵상 (updatedAt 기준)
        const latestMeditation = todayMeditations.sort((a, b) => 
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        )[0];
        
        console.log('✅ 오늘의 묵상 데이터 발견:', latestMeditation.verseReference);
        setTodayMeditationData(latestMeditation);
      } else {
        console.log('ℹ️ 오늘의 묵상 데이터 없음');
        setTodayMeditationData(null);
      }
      
    } catch (error) {
      console.error('❌ 오늘의 묵상 데이터 로드 실패:', error);
      setTodayMeditationData(null);
    } finally {
      setIsLoadingTodayMeditation(false);
    }
  };

  // 오늘의 말씀 초기화 (관리자 설정 우선)
  useEffect(() => {
    const initializeTodayVerse = async () => {
      try {
        // 먼저 관리자 설정 말씀 확인
        try {
          const response = await fetch('/api/admin/verse');
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.verse) {
              console.log('📖 관리자 설정 말씀 사용:', data.verse.reference);
              setTodayVerse({
                id: 0,
                reference: data.verse.reference,
                text: data.verse.text
              });
              return;
            }
          }
        } catch (error) {
          console.log('관리자 말씀 조회 실패, 랜덤 말씀 사용');
        }
        
        // 관리자 설정이 없으면 랜덤 말씀 사용
        const verse = getRandomVerse();
        setTodayVerse(verse);
        console.log('📖 랜덤 말씀 초기화:', verse.reference);
      } catch (error) {
        console.error('말씀 초기화 오류:', error);
        // 오류 시 기본 말씀 사용
        setTodayVerse({
          id: 0,
          reference: DEFAULT_VERSE.reference,
          text: DEFAULT_VERSE.text
        });
      }
    };

    initializeTodayVerse();
  }, []);

  // 오늘의 묵상 데이터 로드
  useEffect(() => {
    if (activeTab === 'home') {
      loadTodayMeditation();
      // 홈 화면에서 기도 통계도 함께 로드
      if (isAuthenticated && !isGuestMode) {
        loadPrayerStats();
        loadPrayerGoals();
      }
    }
  }, [activeTab, isAuthenticated, isGuestMode]);

  // 묵상 화면 닫힐 때 오늘의 말씀 데이터 새로고침
  useEffect(() => {
    if (!showMeditation && activeTab === 'home' && isAuthenticated && !isGuestMode) {
      console.log('🔄 묵상 화면 닫힘 - 오늘의 말씀 데이터 새로고침');
      loadTodayMeditation();
    }
  }, [showMeditation, activeTab, isAuthenticated, isGuestMode]);

  // 자동 로그인 초기화
  useEffect(() => {
    console.log('🚀 자동 로그인 초기화 시작');
    initializeAutoLogin();
    
    // 주기적으로 토큰 만료 확인 (5분마다)
    const interval = setInterval(() => {
      if (isAuthenticated) {
        checkAuthExpiry();
      }
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [isAuthenticated, checkAuthExpiry]);

  // 사용자 인증 상태 변경 시 크루 정보 로드
  useEffect(() => {
    if (isAuthenticated && !isGuestMode) {
      // 첫 로그인 시 데이터 로딩 순서 개선
      const initializeUserData = async () => {
        try {
          console.log('🔄 사용자 데이터 초기화 시작');
          
          // 1. 기본 데이터 로드 (순차적으로)
          await loadUserCrew();
          
          // 2. 공지사항 체크 (첫 로그인 시에만)
          const hasSeenAnnouncement = localStorage.getItem('hasSeenAnnouncement');
          if (!hasSeenAnnouncement) {
            console.log('📢 처음 로그인: 공지사항 자동 팝업 표시');
            // 2초 후 공지사항 모달 자동 오픈 (데이터 로딩 완료 후)
            setTimeout(() => {
              setShowNotificationModal(true);
              loadAnnouncements();
            }, 2000);
          }
          
          console.log('✅ 사용자 데이터 초기화 완료');
        } catch (error) {
          console.error('❌ 사용자 데이터 초기화 실패:', error);
        }
      };
      
      initializeUserData();
      
      // 로그인 후 공유된 게시물이 있다면 처리
      if (sharedPostId && pendingTabSwitch) {
        console.log('🔄 로그인 완료 후 공유 게시물 처리:', sharedPostId);
        
        // 피드 탭으로 전환
        if (pendingTabSwitch === 'feed') {
          setActiveTab('feed');
          
          // 피드 데이터 로드 후 게시물 열기
          setTimeout(() => {
            loadFeedData().then(() => {
              console.log('📱 피드 데이터 로드 완료, 공유 게시물 찾는 중...');
            });
          }, 1000);
        }
        
        // 처리 완료 후 상태 초기화
        setPendingTabSwitch(null);
      }

      // 로그인 후 pending crew invite 처리
      const pendingCrewInvite = localStorage.getItem('pendingCrewInvite');
      if (pendingCrewInvite) {
        console.log('🎉 로그인 완료 - pending 크루 초대 처리:', pendingCrewInvite);
        
        (async () => {
          try {
            // 크루 정보 조회
            const crewDetail = await crewAPI.getCrewById(pendingCrewInvite);
            console.log('✅ 초대받은 크루:', crewDetail.name);
            
            // 크루 탭으로 전환
            setActiveTab('crews');
            
            // 크루 가입 시도
            try {
              await crewAPI.joinCrew(pendingCrewInvite);
              alert(`"${crewDetail.name}" 크루에 가입 신청이 완료되었습니다!\n크루장 승인 후 멤버로 활동할 수 있습니다. 😊`);
              console.log('✅ 크루 가입 신청 완료');
            } catch (joinError: any) {
              if (joinError.message?.includes('이미')) {
                alert(`이미 "${crewDetail.name}" 크루의 멤버입니다! 😊`);
              } else {
                throw joinError;
              }
            }
          } catch (error: any) {
            console.error('❌ 크루 초대 처리 실패:', error);
            alert(`크루 가입에 실패했습니다: ${error.message || '알 수 없는 오류'}`);
          } finally {
            // localStorage에서 제거
            localStorage.removeItem('pendingCrewInvite');
          }
        })();
      }
    }
  }, [isAuthenticated, isGuestMode, loadUserCrew, sharedPostId, pendingTabSwitch]);

  const openSimpleInput = useCallback((field: 'godsWill' | 'growthChange' | 'prayer', title: string, placeholder: string) => {
    try {
      console.log('[SimpleInput] opening field:', field);
      const currentValue = (meditationData as any)[field] || '';
      setSimpleField(field);
      setSimpleTitle(title);
      setSimplePlaceholder(placeholder);
      setSimpleValue(currentValue); // Set controlled value
      setShowSimpleInput(true);
      setSimpleCanClose(false);
      
      // Allow closing after a brief delay
      setTimeout(() => setSimpleCanClose(true), 200);
    } catch (error) {
      console.error('[SimpleInput] open error', error);
    }
  }, [meditationData]);

  const handleSimpleSave = useCallback(() => {
    try {
      if (!simpleField) {
        console.warn('[SimpleInput] save skipped: no field');
        setShowSimpleInput(false);
        return;
      }
      console.log('[SimpleInput] saving', { field: simpleField, length: simpleValue.length });
      setMeditationData(prev => ({ ...prev, [simpleField]: simpleValue } as any));
      setShowSimpleInput(false);
      setSimpleValue(''); // Reset value
    } catch (error) {
      console.error('[SimpleInput] save error', error);
    }
  }, [simpleField, simpleValue]);

  // 말씀 편집 모달 열기
  const openVerseEditModal = useCallback(() => {
    const currentVerse = customVerse.reference 
      ? customVerse 
      : todayVerse 
        ? { reference: todayVerse.reference, text: todayVerse.text }
        : DEFAULT_VERSE;
    
    setEditingVerseReference(currentVerse.reference);
    setEditingVerseText(currentVerse.text);
    setShowVerseEditModal(true);
  }, [customVerse, todayVerse]);

  // 말씀 편집 저장
  const handleVerseSave = useCallback(() => {
    if (!editingVerseReference.trim() || !editingVerseText.trim()) {
      alert('성경 구절과 새기는 말씀을 모두 입력해주세요.');
      return;
    }
    
    const newCustomVerse = {
      reference: editingVerseReference.trim(),
      text: editingVerseText.trim()
    };
    
    setCustomVerse(newCustomVerse);
    
    // 말씀 매니저에도 저장 (사용자 정의 말씀으로)
    setCustomVerseManager(newCustomVerse);
    
    setShowVerseEditModal(false);
    console.log('✅ 말씀 편집 완료:', {
      reference: newCustomVerse.reference,
      textLength: newCustomVerse.text.length
    });
  }, [editingVerseReference, editingVerseText]);

  // 새로운 랜덤 말씀 가져오기
  const getNewVerse = useCallback(() => {
    try {
      const newVerse = getNewRandomVerse();
      setTodayVerse(newVerse);
      // 사용자 정의 말씀 초기화
      setCustomVerse({ reference: '', text: '' });
      console.log('🔄 새로운 말씀 선택:', newVerse.reference);
    } catch (error) {
      console.error('새 말씀 가져오기 오류:', error);
    }
  }, []);

  // 묵상 입력 모달 열기
  const openMeditationInputModal = useCallback((field: 'godsWill' | 'growthChange' | 'prayer', title: string, placeholder: string) => {
    const currentValue = (meditationData as any)[field] || '';
    setMeditationInputField(field);
    setMeditationInputTitle(title);
    setMeditationInputPlaceholder(placeholder);
    setMeditationInputValue(currentValue);
    setShowMeditationInputModal(true);
  }, [meditationData]);

  // 묵상 입력 저장
  const handleMeditationInputSave = useCallback(() => {
    if (!meditationInputField) {
      setShowMeditationInputModal(false);
      return;
    }
    
    setMeditationData(prev => ({ ...prev, [meditationInputField]: meditationInputValue.trim() }));
    
    setShowMeditationInputModal(false);
    console.log('✅ 묵상 입력 완료:', {
      field: meditationInputField,
      length: meditationInputValue.trim().length
    });
  }, [meditationInputField, meditationInputValue]);

  // 크루 상세 정보 열기
  const openCrewDetail = useCallback(async (crew: Crew) => {
    try {
      console.log('🔍 크루 상세 정보 로드 중:', crew.name);
      
      // 먼저 사용자의 최신 크루 정보를 확인
      if (!isGuestMode && isAuthenticated) {
        console.log('🔄 사용자 크루 정보 최신화...');
        await loadUserCrew();
      }
      
      if (isGuestMode || !isAuthenticated) {
        // 게스트 모드에서는 샘플 데이터 사용
        setSelectedCrewDetail(crew);
        setCrewDetailMembers(getCrewMembers(crew.id));
      } else {
        // 실제 API에서 크루 상세 정보 가져오기
        const crewDetail = await crewAPI.getCrewDetail(crew.id);
        if (crewDetail) {
          console.log('✅ 크루 상세 정보 로드 완료:', crewDetail);
          setSelectedCrewDetail(crewDetail);
          setCrewDetailMembers(crewDetail.members || []);
          
          // 크루장인 경우 가입 신청 목록도 자동으로 로드
          if (user?.id === crewDetail.leaderId) {
            console.log('👑 크루장 - 가입 신청 목록 로드 시작');
            try {
              setIsLoadingRequests(true);
              const requests = await crewAPI.getJoinRequests(crew.id);
              console.log('✅ 가입 신청 목록 로드 완료:', requests);
              setJoinRequests(requests);
            } catch (error) {
              console.error('❌ 가입 신청 목록 로드 실패:', error);
              setJoinRequests([]);
            } finally {
              setIsLoadingRequests(false);
            }
          } else {
            // 크루장이 아니면 빈 배열로 초기화
            setJoinRequests([]);
          }
        } else {
          // API 실패 시 기본 데이터 사용
          setSelectedCrewDetail(crew);
          setCrewDetailMembers([]);
          setJoinRequests([]);
        }
      }
      
      setShowCrewDetailModal(true);
    } catch (error) {
      console.error('크루 상세 정보 로드 오류:', error);
      // 오류 시 기본 데이터로 표시
      setSelectedCrewDetail(crew);
      setCrewDetailMembers([]);
      setShowCrewDetailModal(true);
    }
  }, [isGuestMode, isAuthenticated]);

  // 크루별 멤버 목록 생성 (샘플 데이터)
  const getCrewMembers = useCallback((crewId: string) => {
    // 크루별로 다른 멤버 구성
    const memberPools = {
      'crew-1': [ // 여성1크루
        { id: 'member-1', name: '김은혜', avatar: '', role: 'leader', hasSharedToday: true },
        { id: 'member-2', name: '이평안', avatar: '', role: 'member', hasSharedToday: true },
        { id: 'member-3', name: '박기쁨', avatar: '', role: 'member', hasSharedToday: false },
        { id: 'member-4', name: '최사랑', avatar: '', role: 'member', hasSharedToday: true },
        { id: 'member-5', name: '정소망', avatar: '', role: 'member', hasSharedToday: false },
        { id: 'member-6', name: '김지혜', avatar: '', role: 'member', hasSharedToday: true },
        { id: 'member-7', name: '이온유', avatar: '', role: 'member', hasSharedToday: true },
        { id: 'member-8', name: '박선영', avatar: '', role: 'member', hasSharedToday: false }
      ],
      'crew-2': [ // 남성1크루
        { id: 'leader-2', name: '박믿음', avatar: '', role: 'leader', hasSharedToday: true },
        { id: 'member-9', name: '김성실', avatar: '', role: 'member', hasSharedToday: true },
        { id: 'member-10', name: '이담대', avatar: '', role: 'member', hasSharedToday: false },
        { id: 'member-11', name: '박충성', avatar: '', role: 'member', hasSharedToday: true },
        { id: 'member-12', name: '최겸손', avatar: '', role: 'member', hasSharedToday: true },
        { id: 'member-13', name: '정인내', avatar: '', role: 'member', hasSharedToday: false }
      ],
      'crew-3': [ // 청년크루
        { id: 'leader-3', name: '이열정', avatar: '', role: 'leader', hasSharedToday: true },
        { id: 'member-14', name: '김도전', avatar: '', role: 'member', hasSharedToday: true },
        { id: 'member-15', name: '박창의', avatar: '', role: 'member', hasSharedToday: true },
        { id: 'member-16', name: '이혁신', avatar: '', role: 'member', hasSharedToday: false },
        { id: 'member-17', name: '최역동', avatar: '', role: 'member', hasSharedToday: true }
      ]
    };
    
    return memberPools[crewId as keyof typeof memberPools] || [];
  }, []);



  // 디버깅: 모달 열림 상태 변경 로깅
  useEffect(() => {
    console.log('[SimpleInput] visible =', showSimpleInput);
    if (showSimpleInput) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [showSimpleInput]);

  // 하단 버튼: 현재 단계에 맞춰 모달 여는 함수 + 중복 방지 락
  const simpleOpenLockRef = useRef(false);
  const openShareForCurrentStep = useCallback(() => {
    console.log('✨ [모달 입력] 단계:', meditationStep, '모달 방식 사용');
    
    let title = '';
    let placeholder = '';
    let field: 'godsWill' | 'growthChange' | 'prayer' | null = null;
    
    if (meditationStep === 2) {
      title = '하나님이 나에게 원하시는 것은?';
      placeholder = '이 말씀을 통해 하나님이 나에게 원하시는 것을 기록해보세요...';
      field = 'godsWill';
    } else if (meditationStep === 3) {
      title = '변화/성장해야 할 것은?';
      placeholder = '변화/성장해야 할 것을 기록해보세요...';
      field = 'growthChange';
    } else if (meditationStep === 4) {
      title = '기도할 내용은?';
      placeholder = '오늘 말씀을 통해 기도할 내용을 기록해보세요...';
      field = 'prayer';
    }
    
    if (field) {
      openMeditationInputModal(field, title, placeholder);
    }
  }, [meditationStep, openMeditationInputModal]);

  // 크루 생성 함수
  const handleCreateCrew = async () => {
    if (!crewName.trim() || !crewDescription.trim()) {
      alert('크루 이름과 설명을 모두 입력해주세요.');
      return;
    }

    setIsCreatingCrew(true);
    
    try {
      if (isGuestMode || !isAuthenticated) {
        // 게스트 모드에서는 로컬 상태에만 추가
        const newCrew = {
          id: `crew_${Date.now()}`,
          name: crewName.trim(),
          description: crewDescription.trim(),
          members: 1,
          color: `from-${['blue', 'green', 'purple', 'indigo', 'pink'][Math.floor(Math.random() * 5)]}-400`,
          leader: { name: '나' },
          memberCount: 1,
          createdAt: new Date().toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
        };
        
        // 기존 크루 목록에 추가
        setAvailableCrews(prev => [...prev, newCrew as any]);
        // SAMPLE_CREWS에도 추가 (향후 세션에서도 유지되도록)
        SAMPLE_CREWS.push(newCrew as any);
        
        alert(`✅ "${crewName}" 크루가 성공적으로 생성되었습니다!`);
      } else {
        // 실제 서버 API 호출
        console.log('🔄 서버에 크루 생성 요청 중...');
        const newCrew = await crewAPI.createCrew({
          name: crewName.trim(),
          description: crewDescription.trim()
        });
        
        console.log('✅ 서버 크루 생성 완료:', newCrew);
        
        // 즉시 UI에 반영 (서버 응답 활용)
        console.log('🔄 생성된 크루를 즉시 UI에 추가');
        setMyCrews(prev => [...prev, newCrew]);
        setMyCrew(newCrew);
        
        // 캐시 타임스탬프 초기화
        setCrewCacheTimestamp(0);
        localStorage.removeItem('crew_list_cache');
        
        // 백그라운드에서 전체 목록 새로고침 (forceRefresh = true)
        console.log('🔄 백그라운드에서 크루 목록 새로고침');
        loadCrewData(true).catch(err => console.error('백그라운드 새로고침 실패:', err));
        
        alert(`✅ "${crewName}" 크루가 성공적으로 생성되었습니다!`);
      }
      
      // 모달 닫기 및 상태 초기화
      setShowCreateCrewModal(false);
      setCrewName('');
      setCrewDescription('');
    } catch (error: any) {
      console.error('크루 생성 오류:', error);
      console.error('크루 생성 오류 상세:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // 더 구체적인 오류 메시지 표시
      let errorMessage = '크루 생성 중 오류가 발생했습니다.';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(`❌ ${errorMessage}`);
    } finally {
      setIsCreatingCrew(false);
    }
  };

  // 크루 가입 함수
  
  // 묵상 전용 디버깅 상태
  const [meditationDebugInfo, setMeditationDebugInfo] = useState<string[]>([]);
  const [showMeditationDebug, setShowMeditationDebug] = useState(false);

  // 크루 관련 상태
  const [showCrewModal, setShowCrewModal] = useState(false);
  const [showCreateCrewModal, setShowCreateCrewModal] = useState(false);
  const [showJoinCrewModal, setShowJoinCrewModal] = useState(false);
  const [showCrewDetailModal, setShowCrewDetailModal] = useState(false);
  const [showJoinRequestsModal, setShowJoinRequestsModal] = useState(false);
  const [selectedCrewForJoin, setSelectedCrewForJoin] = useState<Crew | null>(null);
  const [userCrewStatus, setUserCrewStatus] = useState<'none' | 'member'>('none');
  const [availableCrews, setAvailableCrews] = useState<Crew[]>([]);
  const [crewMembers, setCrewMembers] = useState<CrewMember[]>([]);
  const [selectedMemberMeditation, setSelectedMemberMeditation] = useState<MemberMeditation | null>(null);
  const [showMemberMeditationModal, setShowMemberMeditationModal] = useState(false);
  const [myCrew, setMyCrew] = useState<Crew | null>(null);
  const [myCrews, setMyCrews] = useState<Crew[]>([]); // 사용자가 가입한 모든 크루
  
  // 크루 로딩 및 캐싱 상태
  const [isLoadingCrews, setIsLoadingCrews] = useState(false);
  const [isLoadingCrewDetails, setIsLoadingCrewDetails] = useState(false);
  const [crewCacheTimestamp, setCrewCacheTimestamp] = useState<number | null>(null);
  
  // 크루 멤버 나눔 상태 관련 상태
  const [showCrewMemberStatusModal, setShowCrewMemberStatusModal] = useState(false);
  const [crewMemberStatuses, setCrewMemberStatuses] = useState<any[]>([]);
  const [currentModalCrew, setCurrentModalCrew] = useState<any>(null);
  
  // 멤버 묵상 상세 모달 관련 상태
  const [showMemberMeditationDetail, setShowMemberMeditationDetail] = useState(false);
  const [selectedMemberForDetail, setSelectedMemberForDetail] = useState<any>(null);
  const [memberMeditationDetail, setMemberMeditationDetail] = useState<any>(null);
  
  // 크루 관리 관련 상태 (크루장 전용)
  const [showCrewEditModal, setShowCrewEditModal] = useState(false);
  const [editingCrew, setEditingCrew] = useState<any>(null);
  const [isUpdatingCrew, setIsUpdatingCrew] = useState(false);
  const [crewEditData, setCrewEditData] = useState({
    name: '',
    description: '',
    image: ''
  });
  
  // 크루 권한 이관 관련 상태
  const [showLeadershipTransferModal, setShowLeadershipTransferModal] = useState(false);
  const [selectedCrewForTransfer, setSelectedCrewForTransfer] = useState<any>(null);
  const [crewMembersForTransfer, setCrewMembersForTransfer] = useState<CrewMember[]>([]);
  const [shouldLeaveAfterTransfer, setShouldLeaveAfterTransfer] = useState(false); // 권한 이관 후 나가기 플래그
  const [selectedNewLeader, setSelectedNewLeader] = useState<string>('');
  const [isTransferringLeadership, setIsTransferringLeadership] = useState(false);
  
  // 묵상 수정 관련 상태
  const [showMeditationEditModal, setShowMeditationEditModal] = useState(false);
  const [editingMeditation, setEditingMeditation] = useState<MeditationData | null>(null);
  const [isUpdatingMeditation, setIsUpdatingMeditation] = useState(false);

  // 음성 읽기 관련 상태
  const [isVoiceReading, setIsVoiceReading] = useState(false);
  const [voiceSynthesis, setVoiceSynthesis] = useState<SpeechSynthesis | null>(null);
  const [voiceReadingCount, setVoiceReadingCount] = useState(0);
  const [voiceReadingSpeed, setVoiceReadingSpeed] = useState(0.8);
  const [maxVoiceReadingCount, setMaxVoiceReadingCount] = useState(100);
  const [isReadingVerseOnly, setIsReadingVerseOnly] = useState(false); // 본문만 읽기 옵션
  const [showVoiceSettings, setShowVoiceSettings] = useState(false);

  // 묵상 완료 데이터를 기반으로 참여빈도 계산 (개선된 버전)
  const calculateParticipationRate = useCallback((crewId: string) => {
    console.log('📊 참여율 계산 시작:', { 
      crewId, 
      isGuestMode, 
      availableCrewsCount: availableCrews.length,
      availableCrews: availableCrews.map(c => ({ 
        id: c.id, 
        name: c.name, 
        hasMembers: !!c.members,
        memberCount: c.members?.length || 0,
        hasSharedTodayCount: c.members?.filter(m => m.hasSharedToday).length || 0
      }))
    });
    
    if (isGuestMode) {
      // 게스트 모드에서는 샘플 데이터 사용
      const members = getCrewMembers(crewId);
      if (members.length === 0) return 0;
      
      const completedCount = members.filter(member => member.hasSharedToday).length;
      const rate = Math.floor((completedCount / members.length) * 100);
      console.log('🎭 게스트 모드 참여율:', { crewId, members: members.length, completed: completedCount, rate });
      return rate;
    } else {
      // 인증된 사용자는 실제 데이터 사용
      const crew = availableCrews.find(c => c.id === crewId);
      console.log('🔍 크루 찾기 결과:', { 
        crewId, 
        found: !!crew, 
        crewName: crew?.name,
        hasMembers: !!crew?.members, 
        memberCount: crew?.members?.length,
        availableCrewIds: availableCrews.map(c => c.id),
        membersPreview: crew?.members ? crew.members.slice(0, 2).map(m => ({ name: m.name, hasSharedToday: m.hasSharedToday })) : 'no members',
        allCrewsWithMembers: availableCrews.map(c => ({
          id: c.id,
          name: c.name,
          hasMembers: !!c.members,
          memberCount: c.members?.length || 0,
          hasSharedTodayCount: c.members?.filter(m => m.hasSharedToday).length || 0
        }))
      });
      
      if (!crew) {
        console.log('❌ 크루를 찾을 수 없음:', { crewId, availableCrewIds: availableCrews.map(c => c.id) });
        return 0;
      }
      
      if (!crew.members || crew.members.length === 0) {
        console.log('❌ 크루 멤버 정보 없음:', { 
          crewId, 
          crewName: crew.name,
          hasMembers: !!crew.members,
          memberCount: crew.members?.length,
          crewData: crew
        });
        return 0;
      }
      
      const completedCount = crew.members.filter(member => member.hasSharedToday).length;
      const rate = Math.floor((completedCount / crew.members.length) * 100);
      console.log('✅ 실제 데이터 참여율:', { 
        crewId, 
        crewName: crew.name,
        totalMembers: crew.members.length, 
        completedMembers: completedCount, 
        rate,
        membersWithStatus: crew.members.map(m => ({ 
          name: m.name, 
          hasSharedToday: m.hasSharedToday,
          completedAt: m.completedAt 
        }))
      });
      return rate;
    }
  }, [getCrewMembers, isGuestMode, availableCrews]);
  const [meditationEditData, setMeditationEditData] = useState({
    verseReference: '',
    verseText: '',
    godsWill: '',
    growthChange: '',
    prayer: ''
  });

  // 크루 수정 관련 상태 (중복 제거됨)
  
  // 한글 입력 상태 관리
  const [isComposing, setIsComposing] = useState(false);
  
  // textarea 참조
  const textareaRefs = useRef<{[key: string]: HTMLTextAreaElement | null}>({});

  // 모바일 키보드 감지 및 뷰포트 조정
  useEffect(() => {
    // 초기 뷰포트 높이 저장
    setInitialViewportHeight(window.innerHeight);

    const handleResize = () => {
      const currentHeight = window.innerHeight;
      const heightDifference = initialViewportHeight - currentHeight;
      
      // 키보드가 올라왔다고 판단하는 임계값 (150px)
      if (heightDifference > 150) {
        setKeyboardVisible(true);
        // 뷰포트 높이를 CSS 변수로 설정
        document.documentElement.style.setProperty('--vh', `${currentHeight * 0.01}px`);
      } else {
        setKeyboardVisible(false);
        document.documentElement.style.setProperty('--vh', `${initialViewportHeight * 0.01}px`);
      }
    };

    const handleVisualViewportChange = () => {
      if (window.visualViewport) {
        const viewport = window.visualViewport;
        const heightDifference = initialViewportHeight - viewport.height;
        
        if (heightDifference > 150) {
          setKeyboardVisible(true);
          document.documentElement.style.setProperty('--vh', `${viewport.height * 0.01}px`);
        } else {
          setKeyboardVisible(false);
          document.documentElement.style.setProperty('--vh', `${initialViewportHeight * 0.01}px`);
        }
      }
    };

    // 이벤트 리스너 등록
    window.addEventListener('resize', handleResize);
    
    // Visual Viewport API 지원 시 사용 (iOS Safari 등)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportChange);
    }

    // 초기 CSS 변수 설정
    document.documentElement.style.setProperty('--vh', `${initialViewportHeight * 0.01}px`);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewportChange);
      }
    };
  }, [initialViewportHeight]);
  
  // activeTab 변경 시 모든 모달 닫기
  useEffect(() => {
    console.log('🔄 탭 변경 감지:', activeTab);
    
    // 크루 생성 모달 닫기
    if (showCreateCrewModal) {
      console.log('🔴 크루 생성 모달 자동 닫기');
      setShowCreateCrewModal(false);
      setCrewName('');
      setCrewDescription('');
    }
    
    // 크루 가입 모달 닫기
    if (showJoinCrewModal) {
      console.log('🔴 크루 가입 모달 자동 닫기');
      setShowJoinCrewModal(false);
      setSelectedCrewForJoin(null);
    }
    
    // 크루 상세 모달 닫기
    if (showCrewDetailModal) {
      console.log('🔴 크루 상세 모달 자동 닫기');
      setShowCrewDetailModal(false);
      setSelectedCrewDetail(null);
      setCrewDetailMembers([]);
    }
    
    // 가입 신청 관리 모달 닫기
    if (showJoinRequestsModal) {
      console.log('🔴 가입 신청 관리 모달 자동 닫기');
      setShowJoinRequestsModal(false);
      setJoinRequests([]);
    }
  }, [activeTab]);
  
  // 공지사항 모달 상태
  const [showNoticeModal, setShowNoticeModal] = useState(false);
  
  // 한글 입력 처리 (간소화됨)
  
  // 입력 필드 포커스 시 스크롤 조정
  const handleInputFocus = useCallback((element: HTMLElement) => {
    try {
      addDebugInfo(`handleInputFocus called for ${element.tagName}`);
      
      // 키보드 감지를 위한 지연
      setTimeout(() => {
        try {
          const rect = element.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const elementTop = rect.top + scrollTop;
          const viewportHeight = window.visualViewport?.height || window.innerHeight;
          
          addDebugInfo(`Scroll calculation: elementTop=${elementTop}, viewportHeight=${viewportHeight}`);
          
          // 키보드 높이를 고려한 스크롤 위치 계산
          const keyboardHeight = Math.max(0, initialViewportHeight - viewportHeight);
          const targetScrollTop = elementTop - (viewportHeight - keyboardHeight) / 2;
          
          if (keyboardHeight > 100) { // 키보드가 실제로 올라온 경우만
            window.scrollTo({
              top: Math.max(0, targetScrollTop),
              behavior: 'smooth'
            });
            addDebugInfo(`Scrolled to: ${Math.max(0, targetScrollTop)}`);
          }
        } catch (error) {
          addDebugInfo(`Scroll error: ${error}`);
        }
      }, 300); // 키보드 애니메이션 완료 후 스크롤
    } catch (error) {
      addDebugInfo(`handleInputFocus error: ${error}`);
    }
  }, [keyboardVisible, initialViewportHeight]);
  
  // 피드 상세보기 모달 열기
  const openFeedDetail = useCallback((feedItem: any) => {
    setSelectedFeedItem(feedItem);
    setShowFeedDetailModal(true);
  }, []);
  
  // 모바일 디버깅 함수들
  const addDebugInfo = useCallback((message: string) => {
    const timestamp = new Date().toLocaleTimeString('ko-KR');
    setDebugInfo(prev => [...prev.slice(-19), `[${timestamp}] ${message}`]);
  }, []);

  // 묵상 전용 디버깅 함수
  const addMeditationDebugInfo = useCallback((info: string) => {
    const timestamp = new Date().toLocaleTimeString('ko-KR');
    setMeditationDebugInfo(prev => [...prev.slice(-19), `[${timestamp}] ${info}`]);
  }, []);

  // 모바일 전용 textarea 이벤트 핸들러
  const createMobileTextareaHandlers = useCallback((fieldName: string, setValue: (value: string) => void) => {
    return {
      onFocus: (e: React.FocusEvent<HTMLTextAreaElement>) => {
        try {
          addMeditationDebugInfo(`${fieldName} FOCUS - Mobile`);
          // 모바일에서 포커스 시 스크롤 방지
          e.preventDefault();
          setTimeout(() => {
            e.target.focus();
            addMeditationDebugInfo(`${fieldName} DELAYED FOCUS`);
          }, 100);
        } catch (error) {
          addMeditationDebugInfo(`${fieldName} FOCUS ERROR: ${error}`);
        }
      },
      onBlur: (e: React.FocusEvent<HTMLTextAreaElement>) => {
        try {
          addMeditationDebugInfo(`${fieldName} BLUR - value length: ${e.target.value.length}`);
          setValue(e.target.value);
        } catch (error) {
          addMeditationDebugInfo(`${fieldName} BLUR ERROR: ${error}`);
        }
      },
      onTouchStart: (e: React.TouchEvent<HTMLTextAreaElement>) => {
        try {
          addMeditationDebugInfo(`${fieldName} TOUCH START`);
          // 터치 이벤트 전파 방지
          e.stopPropagation();
        } catch (error) {
          addMeditationDebugInfo(`${fieldName} TOUCH START ERROR: ${error}`);
        }
      },
      onTouchEnd: (e: React.TouchEvent<HTMLTextAreaElement>) => {
        try {
          addMeditationDebugInfo(`${fieldName} TOUCH END`);
          // 터치 후 포커스 강제 설정
          setTimeout(() => {
            (e.target as HTMLTextAreaElement).focus();
            addMeditationDebugInfo(`${fieldName} TOUCH FOCUS SET`);
          }, 50);
        } catch (error) {
          addMeditationDebugInfo(`${fieldName} TOUCH END ERROR: ${error}`);
        }
      },
      onInput: (e: React.FormEvent<HTMLTextAreaElement>) => {
        try {
          const target = e.target as HTMLTextAreaElement;
          addMeditationDebugInfo(`${fieldName} INPUT: "${target.value}"`);
        } catch (error) {
          addMeditationDebugInfo(`${fieldName} INPUT ERROR: ${error}`);
        }
      },
      onCompositionStart: (e: React.CompositionEvent<HTMLTextAreaElement>) => {
        try {
          addMeditationDebugInfo(`${fieldName} COMPOSITION START`);
          setIsComposing(true);
        } catch (error) {
          addMeditationDebugInfo(`${fieldName} COMPOSITION START ERROR: ${error}`);
        }
      },
      onCompositionEnd: (e: React.CompositionEvent<HTMLTextAreaElement>) => {
        try {
          addMeditationDebugInfo(`${fieldName} COMPOSITION END: "${e.data}"`);
          setIsComposing(false);
        } catch (error) {
          addMeditationDebugInfo(`${fieldName} COMPOSITION END ERROR: ${error}`);
        }
      }
    };
  }, [addMeditationDebugInfo, setIsComposing]);

  // 크루 관련 함수들
  const loadCrewData = useCallback(async (forceRefresh = false) => {
    try {
      console.log('🏢 크루 데이터 로딩 시작 - 인증됨:', isAuthenticated, '게스트 모드:', isGuestMode);
      const startTime = Date.now();
      
      // 게스트 모드이거나 인증되지 않은 경우 샘플 데이터 사용
      if (isGuestMode || !isAuthenticated) {
        console.log('🎭 게스트 모드: 샘플 크루 데이터 로딩');
        setUserCrewStatus('none');
        setAvailableCrews([...SAMPLE_CREWS] as any);
        console.log('🎭 샘플 크루 개수:', SAMPLE_CREWS.length);
        return;
      }

      // 캐시 확인 (5분 유효)
      const CACHE_KEY = 'crew_list_cache';
      const CACHE_DURATION = 5 * 60 * 1000; // 5분
      
      if (!forceRefresh && crewCacheTimestamp && (Date.now() - crewCacheTimestamp < CACHE_DURATION)) {
        console.log('📦 캐시된 크루 데이터 사용 (남은 시간:', Math.floor((CACHE_DURATION - (Date.now() - crewCacheTimestamp)) / 1000), '초)');
        
        // 로컬 스토리지에서 캐시된 데이터 가져오기
        const cachedData = localStorage.getItem(CACHE_KEY);
        if (cachedData) {
          try {
            const { crews, myCrewsData } = JSON.parse(cachedData);
            console.log('✅ 캐시 복원:', crews.length, '개 크루');
            
            setAvailableCrews(crews);
            setMyCrews(myCrewsData);
            setMyCrew(myCrewsData.length > 0 ? myCrewsData[0] : null);
            
            if (myCrewsData.length > 0) {
              setUserCrewStatus('member');
              // 멤버 정보는 백그라운드에서 로드
              crewAPI.getMyCrewMembersToday().then(members => {
                setCrewMembers(members);
              });
            } else {
              setUserCrewStatus('none');
            }
            
            console.log('⚡ 캐시 로딩 완료 (소요 시간:', Date.now() - startTime, 'ms)');
            return;
          } catch (e) {
            console.warn('⚠️ 캐시 파싱 실패, 새로 로드합니다:', e);
          }
        }
      }

      // 실제 서버에서 크루 데이터 로딩
      console.log('🔄 서버에서 크루 데이터 로딩 중...');
      setIsLoadingCrews(true);

      // 내 모든 크루 정보 확인
      console.log('🔄 loadCrewData에서 내 모든 크루 정보 로드...');
      const myCrewsData = await crewAPI.getMyCrews();
      console.log('📊 loadCrewData - 내 모든 크루 정보:', myCrewsData);
      
      // 모든 크루 정보 저장
      setMyCrews(myCrewsData);
      
      // 첫 번째 크루를 기본 크루로 설정 (기존 로직 유지)
      const myCrewInfo = myCrewsData.length > 0 ? myCrewsData[0] : null;
      setMyCrew(myCrewInfo);
      console.log('✅ myCrew 상태 업데이트 완료:', { 
        id: myCrewInfo?.id, 
        name: myCrewInfo?.name,
        hasCrewInfo: !!myCrewInfo,
        totalCrews: myCrewsData.length
      });
      
      // 모든 크루 목록 로드 (이제 멤버 정보 포함!)
      console.log('🚀 크루 목록 + 멤버 정보 한 번에 조회...');
      const crews = await crewAPI.getAvailableCrews();
      console.log('✅ API에서 받은 크루 데이터 (멤버 포함):', crews.length, '개');
      console.log('📊 각 크루 멤버 정보:', crews.map(c => ({
        name: c.name,
        memberCount: c.memberCount,
        actualMembers: c.members?.length || 0,
        hasMembers: !!c.members
      })));
      
      // 참여율 기준으로 정렬 (가입된 크루 우선, 그 다음 참여율 높은 순)
      const sortedCrews = crews.sort((a, b) => {
        const isMyCrewA = myCrewsData.some(crew => crew.id === a.id);
        const isMyCrewB = myCrewsData.some(crew => crew.id === b.id);
        
        // 1순위: 가입된 크루가 위로
        if (isMyCrewA && !isMyCrewB) return -1;
        if (!isMyCrewA && isMyCrewB) return 1;
        
        // 2순위: 가입된 크루 내에서 참여율 높은 순
        if (isMyCrewA && isMyCrewB) {
          const participationA = a.members?.length > 0 
            ? Math.floor((a.members.filter(m => m.hasSharedToday).length / a.members.length) * 100)
            : 0;
          const participationB = b.members?.length > 0 
            ? Math.floor((b.members.filter(m => m.hasSharedToday).length / b.members.length) * 100)
            : 0;
          
          if (participationA !== participationB) {
            return participationB - participationA; // 참여율 높은 순
          }
        }
        
        // 3순위: 가입되지 않은 크루는 참여율 높은 순
        if (!isMyCrewA && !isMyCrewB) {
          const participationA = a.members?.length > 0 
            ? Math.floor((a.members.filter(m => m.hasSharedToday).length / a.members.length) * 100)
            : 0;
          const participationB = b.members?.length > 0 
            ? Math.floor((b.members.filter(m => m.hasSharedToday).length / b.members.length) * 100)
            : 0;
          
          if (participationA !== participationB) {
            return participationB - participationA; // 참여율 높은 순
          }
        }
        
        return 0; // 나머지는 기존 순서 유지
      });
      
      console.log('📋 정렬된 크루 데이터:', sortedCrews.map(c => {
        const isMyCrew = myCrewsData.some(crew => crew.id === c.id);
        const participationRate = c.members?.length > 0 
          ? Math.floor((c.members.filter(m => m.hasSharedToday).length / c.members.length) * 100)
          : 0;
        
        return {
          id: c.id,
          name: c.name,
          isMyCrew,
          memberCount: c.members?.length || 0,
          hasSharedTodayCount: c.members?.filter(m => m.hasSharedToday).length || 0,
          participationRate
        };
      }));
      
      // 상태 업데이트
      setAvailableCrews(sortedCrews);
      setIsLoadingCrews(false);
      
      // 캐시 저장
      const cacheData = {
        crews: sortedCrews,
        myCrewsData,
        timestamp: Date.now()
      };
      
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
        setCrewCacheTimestamp(Date.now());
        console.log('💾 크루 데이터 캐시 저장 완료');
      } catch (e) {
        console.warn('⚠️ 캐시 저장 실패 (로컬 스토리지 용량 초과?):', e);
      }
      
      const elapsed = Date.now() - startTime;
      console.log('✅ 크루 데이터 로딩 완료 (소요 시간:', elapsed, 'ms)');
      console.log('📊 최종 상태:', {
        crewCount: sortedCrews.length,
        myCrewCount: myCrewsData.length,
        totalMembers: sortedCrews.reduce((sum, c) => sum + (c.members?.length || 0), 0),
        totalSharedToday: sortedCrews.reduce((sum, c) => 
          sum + (c.members?.filter(m => m.hasSharedToday).length || 0), 0
        )
      });
      
      if (myCrewInfo) {
        // 크루 가입 상태 - 멤버 나눔 상태도 로드
        console.log('✅ 내 크루 정보:', myCrewInfo);
        setUserCrewStatus('member');
        const members = await crewAPI.getMyCrewMembersToday();
        setCrewMembers(members);
      } else {
        setUserCrewStatus('none');
      }
    } catch (error) {
      console.error('❌ 크루 데이터 로드 오류:', error);
      setIsLoadingCrews(false);
      addMeditationDebugInfo(`크루 데이터 로드 오류: ${error}`);
      
      // 서버 연결 실패 시 사용자에게 알림
      if (error instanceof Error && (error.message.includes('fetch') || error.message.includes('Network'))) {
        alert('서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.');
        setShowCrewModal(false);
      }
    }
  }, [addMeditationDebugInfo, isAuthenticated, isGuestMode, crewCacheTimestamp]);


  const openMemberMeditation = useCallback(async (member: any) => {
    try {
      // 게스트 모드일 경우 샘플 묵상 데이터 사용
      if (isGuestMode) {
        const sampleMeditation = sampleMemberMeditations[member.id];
        if (sampleMeditation) {
          setSelectedMemberMeditation(sampleMeditation);
          setShowMemberMeditationModal(true);
        } else {
          alert('해당 멤버의 묵상 내용이 없습니다.');
        }
        return;
      }

      const meditation = await crewAPI.getMemberMeditationToday(member.id);
      setSelectedMemberMeditation(meditation);
      setShowMemberMeditationModal(true);
    } catch (error: any) {
      alert(error.message);
    }
  }, []);
  
  const logInputEvent = useCallback((eventType: string, target: any, value?: string) => {
    const info = {
      type: eventType,
      tagName: target.tagName,
      id: target.id,
      className: target.className,
      value: value || target.value,
      selectionStart: target.selectionStart,
      selectionEnd: target.selectionEnd,
      timestamp: new Date().toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
    };
    
    setInputDebugInfo(prev => ({
      ...prev,
      [eventType]: info
    }));
    
    addDebugInfo(`${eventType}: ${target.tagName} value="${value || target.value}" cursor=${target.selectionStart}-${target.selectionEnd}`);
  }, [addDebugInfo]);
  
  // 전역 오류 캐처
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      addDebugInfo(`ERROR: ${event.message} at ${event.filename}:${event.lineno}`);
      setShowDebugModal(true);
    };
    
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      addDebugInfo(`PROMISE ERROR: ${event.reason}`);
      setShowDebugModal(true);
    };
    
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [addDebugInfo]);
  const handleCompositionStart = () => {
    setIsComposing(true);
  };
  
  const handleCompositionEnd = () => {
    setIsComposing(false);
  };




  // 크루 가입 신청 처리 함수
  const handleJoinCrew = useCallback(async (crew: Crew) => {
    if (isJoiningCrew) return;
    
    setIsJoiningCrew(true);
    
    try {
      console.log('📝 크루 가입 신청 시작:', crew.name);
      
      // 게스트 모드이거나 인증되지 않은 경우 로컬 상태만 업데이트
      if (isGuestMode || !isAuthenticated) {
        setUserCrew(crew);
        console.log('✅ 게스트 모드 크루 가입 완료:', crew.name);
        alert(`${crew.name}에 가입되었습니다!`);
        setShowJoinCrewModal(false);
        setShowCrewDetailModal(false);
        return;
      }

      // 실제 API 호출 (가입 신청)
      const response = await crewAPI.joinCrew(crew.id);
      console.log('✅ API 크루 가입 신청 완료:', response);
      
      // 크루 목록 다시 로드
      await loadCrewData();
      
      console.log('✅ 크루 가입 신청 완료:', crew.name);
      alert(`✅ ${crew.name}에 가입 신청이 완료되었습니다!\n\n크루장의 승인을 기다려주세요.`);
      
      // 모달 닫기
      setShowJoinCrewModal(false);
      setShowCrewDetailModal(false);
      
    } catch (error: any) {
      console.error('크루 가입 신청 오류:', error);
      console.error('크루 가입 신청 오류 상세:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // 서버에서 반환하는 구체적인 오류 메시지 표시
      let errorMessage = '크루 가입 신청에 실패했습니다. 다시 시도해주세요.';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // "이미 신청" 또는 "이미 가입" 에러인 경우, 크루 정보 다시 로드
      if (errorMessage.includes('이미') && (errorMessage.includes('신청') || errorMessage.includes('가입'))) {
        console.log('🔄 이미 가입/신청 상태 - 크루 정보 다시 로드');
        try {
          await loadUserCrew();
          await loadCrewData();
          console.log('✅ 크루 정보 재로드 완료');
        } catch (reloadError) {
          console.error('❌ 크루 정보 재로드 실패:', reloadError);
        }
      }
      
      alert(`❌ ${errorMessage}`);
    } finally {
      setIsJoiningCrew(false);
    }
  }, [isJoiningCrew, isGuestMode, isAuthenticated, setUserCrew, setShowJoinCrewModal, setShowCrewDetailModal]);

  // 게스트 모드용 더미 사용자 데이터 (분리된 상수 사용)
  const guestUser = GUEST_USER;

  // 게스트 모드용 샘플 크루 데이터
  const sampleCrews = [
    {
      id: 'crew-1',
      name: '여성1크루',
      description: '20-30대 여성 성도들의 따뜻한 말씀 나눔과 기도 제목 공유 💕',
      image: null,
      leaderId: 'leader-1',
      leader: { name: '김은혜', avatar: null },
      memberCount: 12,
      maxMembers: 20,
      createdAt: new Date().toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
    },
    {
      id: 'crew-2', 
      name: '남성1크루',
      description: '30-40대 남성 성도들의 깊이 있는 묵상과 삶의 적용 🔥',
      image: null,
      leaderId: 'leader-2',
      leader: { name: '박요한', avatar: null },
      memberCount: 8,
      maxMembers: 15,
      createdAt: new Date().toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
    },
    {
      id: 'crew-3',
      name: '청년크루',
      description: '20대 청년들의 활기찬 말씀 나눔과 신앙 성장 여행 ✨',
      image: null,
      leaderId: 'leader-3',
      leader: { name: '이다윗', avatar: null },
      memberCount: 15,
      maxMembers: 25,
      createdAt: new Date().toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
    },
    {
      id: 'crew-4',
      name: '새가족크루',
      description: '새로 오신 가족들을 위한 따뜻한 환영과 신앙 기초 다지기 🌱',
      image: null,
      leaderId: 'leader-4',
      leader: { name: '최마리아', avatar: null },
      memberCount: 7,
      maxMembers: 12,
      createdAt: new Date().toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
    },
    {
      id: 'crew-5',
      name: '직장인크루',
      description: '바쁜 직장 생활 속에서도 함께하는 새벽 말씀과 저녁 기도 ⏰',
      image: null,
      leaderId: 'leader-5',
      leader: { name: '정베드로', avatar: null },
      memberCount: 11,
      maxMembers: 18,
      createdAt: new Date().toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
    },
    {
      id: 'crew-6',
      name: '주부크루',
      description: '육아와 가정 사역 중에도 말씀으로 힘을 얻는 어머니들 👩‍👧‍👦',
      image: null,
      leaderId: 'leader-6',
      leader: { name: '한마르다', avatar: null },
      memberCount: 9,
      maxMembers: 15,
      createdAt: new Date().toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
    },
    {
      id: 'crew-7',
      name: '시니어크루',
      description: '인생의 연륜과 함께 더욱 깊어지는 말씀 묵상과 지혜 나눔 🌳',
      image: null,
      leaderId: 'leader-7',
      leader: { name: '김아브라함', avatar: null },
      memberCount: 6,
      maxMembers: 10,
      createdAt: new Date().toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
    }
  ];

  // 게스트 모드용 샘플 크루 멤버 데이터
  const sampleCrewMembers = [
    {
      id: 'member-1',
      name: '김은혜',
      avatar: null,
      hasSharedToday: true,
      isLeader: true,
      position: '크루 리더'
    },
    {
      id: 'member-2',
      name: '이사라',
      avatar: null,
      hasSharedToday: true,
      isLeader: false,
      position: '성도'
    },
    {
      id: 'member-3',
      name: '박한나',
      avatar: null,
      hasSharedToday: true,
      isLeader: false,
      position: '집사'
    },
    {
      id: 'member-4',
      name: '최다니엘',
      avatar: null,
      hasSharedToday: false,
      isLeader: false,
      position: '청년부'
    },
    {
      id: 'member-5',
      name: '정요셉',
      avatar: null,
      hasSharedToday: false,
      isLeader: false,
      position: '새신자'
    },
    {
      id: 'member-6',
      name: '송마리아',
      avatar: null,
      hasSharedToday: true,
      isLeader: false,
      position: '권사'
    },
    {
      id: 'member-7',
      name: '홍베드로',
      avatar: null,
      hasSharedToday: true,
      isLeader: false,
      position: '안수집사'
    },
    {
      id: 'member-8',
      name: '윤에스더',
      avatar: null,
      hasSharedToday: false,
      isLeader: false,
      position: '성도'
    }
  ];

  // 게스트 모드용 샘플 멤버 묵상 데이터
  const sampleMemberMeditations = {
    'member-1': {
      id: 'meditation-1',
      verseReference: '요한복음 3장 16절',
      verseText: '하나님이 세상을 이처럼 사랑하사 독생자를 주셨으니 이는 그를 믿는 자마다 멸망하지 않고 영생을 얻게 하려 하심이라',
      readingCount: 100,
      godsWill: '하나님께서는 저를 통해 주변 사람들에게 사랑을 전하기를 원하십니다. 특히 아직 복음을 모르는 가족들에게 말보다는 삶으로 하나님의 사랑을 보여드리고 싶습니다.',
      growthChange: '더 많은 인내와 온유함을 가져야겠습니다. 때로는 급한 마음에 말을 앞세우곤 했는데, 하나님의 사랑을 닮아 더욱 부드럽고 따뜻한 사람이 되어야겠어요.',
      prayer: '하나님, 오늘도 주님의 사랑으로 하루를 시작하게 하소서. 제 마음에 있는 모든 미움과 원망을 내려놓고, 주님께서 저를 사랑하신 것처럼 다른 이들도 사랑할 수 있게 도와주세요.',
      prayerShareType: '공유',
      createdAt: new Date().toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
    },
    'member-2': {
      id: 'meditation-2',
      verseReference: '빌립보서 4장 13절',
      verseText: '내게 능력 주시는 자 안에서 내가 모든 것을 할 수 있느니라',
      readingCount: 100,
      godsWill: '하나님께서는 제가 어려운 상황에서도 포기하지 않고 끝까지 믿음으로 살아가기를 원하십니다. 지금의 시험도 하나님의 계획 안에 있음을 믿습니다.',
      growthChange: '더 큰 믿음을 가져야겠습니다. 환경을 보고 낙심하기보다는 하나님의 능력을 의지하며 담대하게 나아가야겠어요.',
      prayer: '주님, 제 힘으로는 할 수 없는 일들이 너무 많습니다. 하지만 주님께서 함께하시니 두렵지 않습니다. 오늘도 주님의 능력으로 승리하게 하소서.',
      prayerShareType: '중보기도',
      createdAt: new Date().toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
    },
    'member-3': {
      id: 'meditation-3',
      verseReference: '시편 23장 1절',
      verseText: '여호와는 나의 목자시니 내게 부족함이 없으리로다',
      readingCount: 100,
      godsWill: '하나님께서는 저를 푸른 초장과 쉴 만한 물가로 인도하십니다. 바쁜 일상 속에서도 하나님과의 교제를 통해 참된 안식을 누리기를 원하십니다.',
      growthChange: '더 많은 시간을 하나님과 함께 보내야겠습니다. 세상의 염려보다는 하나님의 돌보심을 더 깊이 경험하며 살아가야겠어요.',
      prayer: '선한 목자 되신 하나님, 오늘도 저를 인도해 주세요. 제가 길을 잃지 않도록 붙잡아 주시고, 주님의 음성을 분별할 수 있는 영적인 귀를 열어주세요.',
      prayerShareType: '공유',
      createdAt: new Date().toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
    },
    'member-6': {
      id: 'meditation-6',
      verseReference: '잠언 3장 5-6절',
      verseText: '너는 마음을 다하여 여호와를 신뢰하고 네 명철을 의지하지 말라 너는 범사에 그를 인정하라 그리하면 네 길을 지도하시리라',
      readingCount: 100,
      godsWill: '하나님께서는 제가 모든 상황에서 주님을 의지하며 살아가기를 원하십니다. 제 지혜나 판단보다는 하나님의 뜻을 구하며 순종하는 삶을 살기 원하십니다.',
      growthChange: '더 많이 기도하고 말씀을 통해 하나님의 뜻을 구해야겠습니다. 급한 결정을 내리기 전에 먼저 하나님께 여쭤보는 습관을 가져야겠어요.',
      prayer: '전능하신 하나님, 제 삶의 모든 영역에서 주님이 주인이 되어주세요. 제 고집과 욕심을 내려놓고 주님의 인도하심을 따르게 하소서.',
      prayerShareType: '공유',
      createdAt: new Date().toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
    },
    'member-7': {
      id: 'meditation-7',
      verseReference: '로마서 8장 28절',
      verseText: '우리가 알거니와 하나님을 사랑하는 자 곧 그의 뜻대로 부르심을 입은 자들에게는 모든 것이 합력하여 선을 이루느니라',
      readingCount: 100,
      godsWill: '하나님께서는 현재 제가 겪고 있는 어려움도 결국 선한 목적을 위해 사용하실 것을 믿습니다. 모든 상황에서 하나님의 선하심을 신뢰하며 살아가기를 원하십니다.',
      growthChange: '어려운 상황에서도 불평하지 않고 감사하는 마음을 가져야겠습니다. 하나님의 큰 그림을 보며 인내하는 믿음이 필요해요.',
      prayer: '주님, 때로는 이해할 수 없는 일들이 일어나지만 주님의 계획이 가장 좋은 것임을 믿습니다. 끝까지 신뢰하며 따르게 하소서.',
      prayerShareType: '중보기도',
      createdAt: new Date().toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
    }
  };

  // 게스트 모드용 샘플 피드 데이터
  const sampleFeedData = [
    {
      id: 'feed-1',
      user: {
        id: 'user-1',
        name: '김은혜',
        avatar: null,
        crew: '여성1크루'
      },
      verseReference: '요한복음 3장 16절',
      verse: '하나님이 세상을 이처럼 사랑하사 독생자를 주셨으니 이는 그를 믿는 자마다 멸망하지 않고 영생을 얻게 하려 하심이라',
      content: '하나님의 사랑이 얼마나 크신지 다시 한 번 깨닫게 됩니다. 오늘도 이 사랑으로 살아가겠습니다.',
      time: '2시간 전',
      likes: 12,
      prayerShareType: '공유',
      createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 'feed-2',
      user: {
        id: 'user-2',
        name: '박요한',
        avatar: null,
        crew: '남성1크루'
      },
      verseReference: '빌립보서 4장 13절',
      verse: '내게 능력 주시는 자 안에서 내가 모든 것을 할 수 있느니라',
      content: '어려운 상황에서도 하나님의 능력을 의지하며 담대하게 나아가겠습니다.',
      time: '4시간 전',
      likes: 8,
      prayerShareType: '중보기도',
      createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 'feed-3',
      user: {
        id: 'user-3',
        name: '이다윗',
        avatar: null,
        crew: '청년크루'
      },
      verseReference: '시편 23장 1절',
      verse: '여호와는 나의 목자시니 내게 부족함이 없으리로다',
      content: '하나님의 돌보심 안에서 참된 안식을 누리며 살아가고 싶습니다.',
      time: '6시간 전',
      likes: 15,
      prayerShareType: '공유',
      createdAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 'feed-4',
      user: {
        id: 'user-4',
        name: '최마리아',
        avatar: null,
        crew: '새가족크루'
      },
      verseReference: '마태복음 11장 28절',
      verse: '수고하고 무거운 짐 진 자들아 다 내게로 오라 내가 너희를 쉬게 하리라',
      content: '주님께서 주시는 참된 안식을 경험하며 하루를 보냈습니다. 감사합니다.',
      time: '8시간 전',
      likes: 6,
      prayerShareType: '비공유',
      createdAt: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 'feed-5',
      user: {
        id: 'user-5',
        name: '정베드로',
        avatar: null,
        crew: '직장인크루'
      },
      verseReference: '로마서 8장 28절',
      verse: '우리가 알거니와 하나님을 사랑하는 자 곧 그의 뜻대로 부르심을 입은 자들에게는 모든 것이 합력하여 선을 이루느니라',
      content: '모든 상황에서 하나님의 선하신 뜻을 발견하며 감사하는 마음으로 살아가겠습니다.',
      time: '12시간 전',
      likes: 10,
      prayerShareType: '중보기도',
      createdAt: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 'feed-6',
      user: {
        id: 'user-6',
        name: '이사라',
        avatar: null,
        crew: '여성1크루'
      },
      verseReference: '잠언 3장 5-6절',
      verse: '너는 마음을 다하여 여호와를 신뢰하고 네 명철을 의지하지 말라 너는 범사에 그를 인정하라 그리하면 네 길을 지도하시리라',
      content: '내 생각보다는 하나님의 지혜를 구하며 오늘 하루를 살아가겠습니다.',
      time: '1일 전',
      likes: 9,
      prayerShareType: '공유',
      createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
    }
  ];

  // 앱 시작 시 버전 정보 출력 및 URL 파라미터 처리
  useEffect(() => {
    console.log('🚀 e2b Church Platform v3.6.1 - Frontend (말씀 주소 형식 개선)');
    console.log('📅 Update Time:', new Date().toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' }));
    console.log('📖 새로운 변경: 말씀 주소 형식 변경 (00:00 → 00장 00절) - 음성 인식 개선');
    console.log('🔗 Backend API:', import.meta.env.VITE_API_BASE_URL || 'https://e2b-backend.onrender.com');
    console.log('👤 인증 상태:', isAuthenticated ? '로그인됨' : '로그인 필요');
    console.log('🎭 게스트 모드:', isGuestMode ? '활성화됨' : '비활성화됨');
    if (user) {
      console.log('✅ 사용자:', user.name, '(', user.role, ')');
    }
    
    // URL 파라미터 확인 (공유 링크 처리)
    const urlParams = new URLSearchParams(window.location.search);
    const postId = urlParams.get('post');
    const tabParam = urlParams.get('tab');
    const memberId = urlParams.get('member');
    const meditationId = urlParams.get('meditation');
    
    if (postId) {
      console.log('🔗 공유된 게시물 ID 발견:', postId);
      setSharedPostId(postId);
      
      // 1) 인증 사용자: 피드 탭 전환 + 단건 조회 후 모달 오픈
      // 2) 비인증 사용자: 게스트 모드로 전환 후 공개 단건 조회로 모달 오픈
      (async () => {
        try {
          if (!isAuthenticated) {
            if (!isGuestMode) {
              console.log('🎭 비인증 접근 - 게스트 모드 활성화');
              setIsGuestMode(true);
            }
            const data = await meditationAPI.getPublicMeditation(postId);
            console.log('✅ 공개 단건 조회 성공');
          setActiveTab('feed');
            setTimeout(() => openFeedDetailModal(data as any), 300);
            // 중복 처리 방지
            setSharedPostId(null);
          } else {
            console.log('🔐 인증 사용자 - 피드 탭 전환 후 단건 조회');
            setActiveTab('feed');
            try {
              const data = await meditationAPI.getPublicMeditation(postId);
              setTimeout(() => openFeedDetailModal(data as any), 300);
              // 중복 처리 방지
              setSharedPostId(null);
            } catch (e) {
              console.warn('⚠️ 공개 단건 조회 실패, 로컬 피드에서 탐색 시도');
            }
          }
        } catch (e) {
          console.error('❌ 공유 링크 처리 실패:', e);
          // 첫 입장 시 알림 노출하지 않고 조용히 무시
          setSharedPostId(null);
        } finally {
      // URL에서 파라미터 제거 (깔끔한 URL 유지)
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
        }
      })();
    }
    
    // 멤버 묵상 공유 링크 처리
    if (memberId && meditationId) {
      console.log('🔗 공유된 멤버 묵상 발견:', memberId, meditationId);
      setSharedMemberId(memberId);
      setSharedMeditationId(meditationId);
      
      if (tabParam === 'crews') {
        console.log('📋 크루 탭으로 전환');
        setActiveTab('crews');
      }
      
      // URL에서 파라미터 제거 (깔끔한 URL 유지)
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }

    // 크루 초대 링크 처리
    const inviteCrewId = urlParams.get('inviteCrew');
    if (inviteCrewId) {
      console.log('🎉 크루 초대 링크 발견:', inviteCrewId);
      
      if (!isAuthenticated) {
        // 비인증 사용자: localStorage에 저장 후 로그인/회원가입 유도
        console.log('💾 크루 초대 정보 저장 (로그인 후 처리)');
        localStorage.setItem('pendingCrewInvite', inviteCrewId);
        
        // URL 파라미터 유지하지 않고 제거 (localStorage에 저장했으므로)
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
        
        alert('크루에 가입하려면 로그인이 필요합니다.\n로그인 후 자동으로 크루 가입이 진행됩니다! 😊');
      } else {
        // 인증된 사용자: 바로 크루 가입 처리
        console.log('🔐 인증된 사용자 - 크루 가입 진행');
        
        (async () => {
          try {
            // 크루 정보 조회
            const crewDetail = await crewAPI.getCrewById(inviteCrewId);
            console.log('✅ 초대받은 크루:', crewDetail.name);
            
            // 크루 탭으로 전환
            setActiveTab('crews');
            
            // 크루 가입 시도
            try {
              await crewAPI.joinCrew(inviteCrewId);
              alert(`"${crewDetail.name}" 크루에 가입 신청이 완료되었습니다!\n크루장 승인 후 멤버로 활동할 수 있습니다. 😊`);
              console.log('✅ 크루 가입 신청 완료');
            } catch (joinError: any) {
              if (joinError.message?.includes('이미')) {
                alert(`이미 "${crewDetail.name}" 크루의 멤버입니다! 😊`);
              } else {
                throw joinError;
              }
            }
          } catch (error: any) {
            console.error('❌ 크루 초대 처리 실패:', error);
            alert(`크루 가입에 실패했습니다: ${error.message || '알 수 없는 오류'}`);
          } finally {
            // URL에서 파라미터 제거
            const newUrl = window.location.pathname;
            window.history.replaceState({}, '', newUrl);
          }
        })();
      }
    }
  }, [isAuthenticated, user, isGuestMode]);

  // 공유된 멤버 묵상 자동 로드
  useEffect(() => {
    if (sharedMemberId && sharedMeditationId) {
      console.log('🔄 공유된 멤버 묵상 자동 로드 시작');
      loadSharedMemberMeditation(sharedMemberId, sharedMeditationId);
    }
  }, [sharedMemberId, sharedMeditationId]);

  // 페이지 방문 기록 및 서버 상태 확인
  useEffect(() => {
    const recordPageVisit = async () => {
      try {
        // 새로운 분석 API 사용 (영속성 보장)
        const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://e2b-backend.onrender.com';
        await fetch(`${API_BASE_URL}/api/analytics/page-visit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            page: 'login',
            userAgent: navigator.userAgent,
            referrer: document.referrer,
            timestamp: new Date().toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
          })
        });
      } catch (error) {
        // 폴백: 기존 API 사용
        try {
          const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://e2b-backend.onrender.com';
          await fetch(`${API_BASE_URL}/api/page-visit`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              page: 'login',
              userAgent: navigator.userAgent,
              timestamp: new Date().toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
            })
          });
        } catch (fallbackError) {
          console.log('페이지 방문 기록 실패:', fallbackError);
        }
      }
    };

    const checkServerStats = async () => {
      try {
        // 새로운 분석 API 사용 (영속성 보장)
        const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://e2b-backend.onrender.com';
        const response = await fetch(`${API_BASE_URL}/api/analytics/stats/realtime`);
        const data = await response.json();
        setServerStats({
          status: data.status === 'OK' ? 'connected' : 'error',
          totalVisitors: data.totalVisitors || 0,
          serverStatus: data.serverStatus || 'disconnected'
        });
      } catch (error) {
        // 폴백: 기존 API 사용
        try {
          const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://e2b-backend.onrender.com';
          const fallbackResponse = await fetch(`${API_BASE_URL}/api/stats`);
          const fallbackData = await fallbackResponse.json();
          setServerStats({
            status: fallbackData.status === 'OK' ? 'connected' : 'error',
            totalVisitors: fallbackData.totalVisitors || 0,
            serverStatus: fallbackData.serverStatus || 'disconnected'
          });
        } catch (fallbackError) {
          setServerStats({
            status: 'error',
            totalVisitors: 0,
            serverStatus: 'disconnected'
          });
        }
      }
    };

    // 페이지 방문 기록 (최초 1회만)
    if (!isAuthenticated && !isGuestMode) {
      recordPageVisit();
    }

    checkServerStats();
    // 30초마다 갱신
    const interval = setInterval(checkServerStats, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, isGuestMode]);

  // 오늘의 묵상 데이터 불러오기 (묵상 화면용)
  const loadTodayMeditationForScreen = async () => {
    if (!isAuthenticated || isGuestMode) return;
    
    // 신규 묵상 세션이면 서버에서 불러오지 않음
    if (isNewMeditationSession) {
      console.log('🆕 신규 묵상 세션 - 서버 데이터 불러오지 않음');
      return;
    }
    
    // 로그인 상태 확인
    if (!user || !accessToken) {
      console.log('🔐 로그인 필요: 오늘의 묵상 불러오기 건너뛰기');
      return;
    }
    
    try {
      console.log('📚 오늘의 묵상 불러오기 시작');
      const meditation = await meditationAPI.getTodayMeditation();
      console.log('📚 불러온 묵상 데이터:', meditation);
      
      setTodayMeditation(meditation);
      
      // 완료된 묵상이면 신규 세션으로 시작 (단, 오늘의 말씀 데이터는 유지)
      if (meditation.step4Complete) {
        console.log('✅ 완료된 묵상 발견 - 신규 세션으로 전환 (오늘의 말씀은 유지)');
        setIsNewMeditationSession(true);
        resetMeditationForNewSession();
        // 오늘의 말씀 데이터 로드는 별도 useEffect에서 처리되므로 여기서는 return하지 않음
        return;
      }
      
      // 상태에 반영 (미완료 묵상만)
      if (meditation.verseReference && meditation.verseText) {
        setCustomVerse({
          reference: meditation.verseReference,
          text: meditation.verseText
        });
      }
      
      setReadingCount(meditation.readingCount || 0);
      setMeditationData({
        godsWill: meditation.godsWill || '',
        growthChange: meditation.growthChange || '',
        prayer: meditation.prayer || ''
      });
      setPrayerShareType((meditation.prayerShareType as 'private' | 'public' | 'request') || 'private');
      
      // 미완료 단계 확인
      if (meditation.step3Complete) {
        setMeditationStep(4);
      } else if (meditation.step2Complete) {
        setMeditationStep(3);
      } else if (meditation.readingCount > 0) {
        setMeditationStep(2);
      } else {
        setMeditationStep(1);
      }
      
    } catch (error) {
      console.error('묵상 데이터 불러오기 실패:', error);
    }
  };

  // 신규 묵상 세션을 위한 초기화 함수
  const resetMeditationForNewSession = () => {
    setMeditationStep(1);
    setReadingCount(0);
    setMeditationData({
      godsWill: '',
      growthChange: '',
      prayer: ''
    });
    setCustomVerse({
      reference: '',
      text: ''
    });
    setPrayerShareType('private');
    setIsNewMeditationSession(true); // 신규 세션 플래그 설정
    console.log('🔄 묵상 데이터 초기화 완료 - 신규 묵상 준비');
  };

  // 묵상 데이터 저장
  const saveMeditationData = async (stepData?: any, stepNumber?: number) => {
    if (!isAuthenticated || isSavingMeditation) return;
    
    setIsSavingMeditation(true);
    try {
      console.log('💾 묵상 데이터 저장 시작');
      
      const saveData = {
        verseReference: customVerse.reference || todayVerse?.reference || DEFAULT_VERSE.reference,
        verseText: customVerse.text || todayVerse?.text || DEFAULT_VERSE.text,
        readingCount,
        godsWill: meditationData.godsWill,
        growthChange: meditationData.growthChange,
        prayer: meditationData.prayer,
        step1Complete: meditationStep >= 1,
        step2Complete: meditationStep >= 2,
        step3Complete: meditationStep >= 3,
        step4Complete: meditationStep >= 4,
        prayerShareType: prayerShareType, // 기도 공유 설정 추가
        ...stepData
      };
      
      const response = await meditationAPI.saveMeditation(saveData);
      console.log('💾 묵상 저장 완료:', response);
      
      setTodayMeditation(response.meditation);
      
    } catch (error) {
      console.error('묵상 데이터 저장 실패:', error);
    } finally {
      setIsSavingMeditation(false);
    }
  };

  // 특정 단계 완료 처리
  const completeStep = async (step: number, data: any) => {
    console.log('🔍 completeStep 호출:', { step, data, isAuthenticated, isSavingMeditation, user: user?.name, provider: user?.provider });
    
    if (!isAuthenticated || isSavingMeditation) {
      console.log('❌ completeStep 조건 실패:', { isAuthenticated, isSavingMeditation });
      return;
    }
    
    setIsSavingMeditation(true);
    try {
      console.log(`✅ ${step}단계 완료 처리 시작:`, data);
      
      const response = await meditationAPI.completeStep(step, data);
      console.log(`✅ ${step}단계 완료 성공:`, response);
      
      setTodayMeditation(response.meditation);
      
    } catch (error) {
      console.error(`❌ ${step}단계 완료 처리 실패:`, error);
      console.error('에러 상세:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
    } finally {
      setIsSavingMeditation(false);
    }
  };

  // 묵상 히스토리 불러오기
  const loadMeditationHistory = async () => {
    if (!isAuthenticated || isGuestMode || isLoadingHistory) return;
    
    setIsLoadingHistory(true);
    try {
      console.log('📚 묵상 히스토리 불러오기 시작');
      // 조건부 limit: 전체 보기 모드면 9999, 아니면 20개
      const limit = showAllHistory ? 9999 : 20;
      const response = await meditationAPI.getMeditationHistory(1, limit);
      console.log('📚 불러온 히스토리:', response);
      
      setMeditationHistory(response.meditations);
      setMeditationTotal(response.pagination.total);
      
    } catch (error) {
      console.error('묵상 히스토리 불러오기 실패:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // 묵상 히스토리 모달 열기
  const openMeditationHistory = () => {
    setShowMeditationHistory(true);
    loadMeditationHistory();
  };

  // 전체 보기 버튼 클릭
  const handleShowAllHistory = () => {
    setShowAllHistory(true);
    loadMeditationHistory();
  };

  // 묵상 기록 삭제
  const deleteMeditationRecord = async (meditationId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // 부모 클릭 이벤트 방지
    
    if (!confirm('이 묵상 기록을 삭제하시겠습니까?')) {
      return;
    }

    try {
      console.log('🗑️ 묵상 삭제 시작:', meditationId);
      
      await meditationAPI.deleteMeditation(meditationId);
      
      // 목록에서 제거
      setMeditationHistory(prev => prev.filter(item => item.id !== meditationId));
      
      console.log('✅ 묵상 삭제 완료');
      alert('묵상 기록이 삭제되었습니다.');
      
    } catch (error) {
      console.error('❌ 묵상 삭제 실패:', error);
      alert('묵상 기록 삭제에 실패했습니다.');
    }
  };

  // 현재 사용자가 특정 크루의 멤버인지 확인
  const isCurrentUserMemberOfCrew = useCallback((crewId: string) => {
    if (isGuestMode) {
      console.log('🎭 게스트 모드 - 멤버십 확인 불가:', crewId);
      return false;
    }
    
    const isMember = myCrews.some(crew => crew.id === crewId);
    console.log('🔍 멤버십 확인:', {
      crewId,
      myCrewsCount: myCrews.length,
      myCrewsIds: myCrews.map(c => c.id),
      isMember
    });
    
    return isMember;
  }, [isGuestMode, myCrews]);

  // 크루 나가기 함수 (개선된 디버깅 및 오류 처리)
  const handleLeaveCrew = async (crew: any) => {
    console.log('🚪 크루 나가기 함수 시작:', {
      crewName: crew.name,
      crewId: crew.id,
      isGuestMode,
      isAuthenticated,
      currentMyCrew: myCrew,
      userCrewStatus
    });

    // 크루장인지 확인
    const isCrewLeader = user?.id === crew.leaderId;
    
    if (isCrewLeader) {
      // 크루장인 경우 권한 이관 필요
      alert('크루장은 직접 나갈 수 없습니다.\n먼저 다른 멤버에게 권한을 이관한 후 나가주세요.');
      return;
    }

    if (!confirm(`정말로 "${crew.name}" 크루에서 나가시겠습니까?`)) {
      console.log('❌ 사용자가 나가기를 취소함');
      return;
    }

    try {
      console.log('🚪 크루 나가기 진행 시작:', crew.name, crew.id);
      
      if (isGuestMode) {
        console.log('🎭 게스트 모드 - 로컬 상태만 업데이트');
        // 게스트 모드 - 로컬 상태만 업데이트
        setMyCrew(null);
        setUserCrewStatus('none');
        alert(`"${crew.name}" 크루에서 나갔습니다. (게스트 모드)`);
        return;
      }

      // 인증 상태 확인
      if (!isAuthenticated) {
        console.error('❌ 인증되지 않은 상태에서 나가기 시도');
        alert('로그인이 필요합니다.');
        return;
      }
      
      console.log('📡 크루 나가기 API 호출 시작...');
      // 실제 API 호출
      await crewAPI.leaveCrew(crew.id);
      console.log('✅ 크루 나가기 API 완료');
      
      // 상태 업데이트 (API 성공 후)
      console.log('🔄 로컬 상태 업데이트 시작...');
      setMyCrew(null);
      setUserCrewStatus('none');
      console.log('✅ 로컬 상태 업데이트 완료');
      
      // 크루 목록 새로고침
      console.log('🔄 크루 데이터 새로고침 시작...');
      try {
        await loadCrewData();
        console.log('✅ 크루 데이터 새로고침 완료');
      } catch (loadError) {
        console.error('❌ 크루 데이터 새로고침 실패:', loadError);
      }
      
      // 사용자 크루 정보 새로고침
      console.log('🔄 사용자 크루 정보 새로고침 시작...');
      try {
        await loadUserCrew();
        console.log('✅ 사용자 크루 정보 새로고침 완료');
      } catch (loadError) {
        console.error('❌ 사용자 크루 정보 새로고침 실패:', loadError);
      }
      
      // 모든 모달 닫기
      console.log('🔄 모달 닫기 시작...');
      setShowCrewDetailModal(false);
      setShowJoinCrewModal(false);
      
      // 크루 상세 정보 초기화
      setSelectedCrewDetail(null);
      setCrewDetailMembers([]);
      console.log('✅ 모달 및 상태 초기화 완료');
      
      console.log('✅ 크루 나가기 완료 - 모든 상태 업데이트됨');
      alert(`"${crew.name}" 크루에서 나갔습니다.`);
      
    } catch (error: any) {
      console.error('❌ 크루 나가기 실패:', {
        error: error,
        message: error.message,
        stack: error.stack,
        crewId: crew.id,
        crewName: crew.name
      });
      
      // 서버에서 반환하는 구체적인 오류 메시지 표시
      let errorMessage = '크루 나가기에 실패했습니다. 다시 시도해주세요.';
      
      if (error.message) {
        console.log('📝 오류 메시지 파싱:', error.message);
        errorMessage = error.message;
      }
      
      // 특정 오류 타입별 처리
      if (error.message && error.message.includes('크루장은 나갈 수 없습니다')) {
        console.log('🚫 크루장 나가기 시도 오류');
        const shouldTransferLeadership = confirm(
          '크루장은 일반 나가기가 불가능합니다.\n\n' +
          '다른 멤버에게 크루장 권한을 이관한 후 나가시겠습니까?\n\n' +
          '✅ 확인: 권한 이관 후 자동으로 나갑니다.\n' +
          '❌ 취소: 나가기를 중단합니다.'
        );
        
        if (shouldTransferLeadership) {
          // 권한 이관 모달 열기 (나가기 플래그 설정)
          console.log('🔄 권한 이관 후 나가기 모드로 모달 열기');
          setShouldLeaveAfterTransfer(true);
          setSelectedCrewForTransfer(crew);
          await loadCrewMembersForTransfer(crew.id);
          setShowLeadershipTransferModal(true);
          return;
        }
        
        errorMessage = '크루장은 나갈 수 없습니다. 다른 멤버에게 리더 권한을 양도하거나 크루를 삭제해주세요.';
      } else if (error.message && error.message.includes('해당 크루의 멤버가 아닙니다')) {
        console.log('🔄 멤버가 아님 - 상태 동기화 시작');
        errorMessage = '이미 나갔거나 크루의 멤버가 아닙니다.';
        // 상태 동기화
        setMyCrew(null);
        setUserCrewStatus('none');
        try {
          await loadCrewData();
          await loadUserCrew();
          console.log('✅ 상태 동기화 완료');
        } catch (syncError) {
          console.error('❌ 상태 동기화 실패:', syncError);
        }
      } else if (error.message && error.message.includes('Network Error')) {
        console.log('🌐 네트워크 오류');
        errorMessage = '네트워크 연결을 확인하고 다시 시도해주세요.';
      } else if (error.message && error.message.includes('500')) {
        console.log('🖥️ 서버 오류');
        errorMessage = '서버에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
      }
      
      console.log('🚨 최종 오류 메시지:', errorMessage);
      alert(`❌ ${errorMessage}`);
    }
  };

  // 멤버 묵상 상세 정보 로드
  const loadMemberMeditationDetail = async (member: any) => {
    try {
      console.log('📖 멤버 묵상 상세 정보 로드:', member.name);
      
      if (isGuestMode) {
        // 게스트 모드 - 샘플 데이터
        const sampleMeditation = {
          id: '1',
          date: new Date().toISOString().split('T')[0],
          verseReference: '요한복음 3장 16절',
          verseText: '하나님이 세상을 이처럼 사랑하사 독생자를 주셨으니 이는 그를 믿는 자마다 멸망하지 않고 영생을 얻게 하려 하심이라',
          godsWill: '하나님께서는 우리를 사랑하시며, 구원의 길을 열어주셨다는 것을 깨달았습니다.',
          growthChange: '더욱 겸손한 마음으로 하나님의 사랑에 응답하며 살아가겠습니다.',
          prayer: '하나님의 무한한 사랑에 감사드리며, 오늘 하루도 주님의 뜻대로 살아가게 도와주세요.',
          prayerShareType: 'public'
        };
        setMemberMeditationDetail(sampleMeditation);
        setSelectedMemberForDetail(member);
        setShowMemberMeditationDetail(true);
        return;
      }

      // 인증된 사용자 - 실제 API 호출
      if (!member.hasSharedToday) {
        alert('이 멤버는 아직 오늘 묵상을 완료하지 않았습니다.');
        return;
      }

      // 실제 API 호출로 멤버의 묵상 내용 가져오기
      console.log('🔄 API 호출: 멤버 묵상 상세 정보 로드 중...');
      const memberMeditation = await crewAPI.getMemberMeditationToday(member.id);
      console.log('✅ API 응답 받음:', memberMeditation);
      
      setMemberMeditationDetail(memberMeditation);
      setSelectedMemberForDetail(member);
      setShowMemberMeditationDetail(true);
      
    } catch (error: any) {
      console.error('❌ 멤버 묵상 상세 정보 로드 실패:', error);
      
      // 에러 메시지를 사용자에게 보여주기
      const errorMessage = error.message || '멤버의 묵상 내용을 불러오는데 실패했습니다.';
      alert(errorMessage);
    }
  };

  // 크루 멤버 나눔 상태 로드 (오늘 기준)
  const loadCrewMemberStatuses = async (crew?: any) => {
    try {
      console.log('📊 크루 멤버 나눔 상태 로드 시작 (오늘 기준)');
      
      if (isGuestMode) {
        // 게스트 모드 - 샘플 데이터 (완료 시간 포함)
        const now = new Date();
        const sampleStatuses = [
          { 
            id: '1', 
            name: '김철수', 
            avatar: null, 
            hasSharedToday: true, 
            role: 'leader',
            completedAt: new Date(now.getTime() - 2 * 60 * 60 * 1000) // 2시간 전
          },
          { 
            id: '2', 
            name: '박영희', 
            avatar: null, 
            hasSharedToday: false, 
            role: 'member',
            completedAt: null
          },
          { 
            id: '3', 
            name: '이민수', 
            avatar: null, 
            hasSharedToday: true, 
            role: 'member',
            completedAt: new Date(now.getTime() - 30 * 60 * 1000) // 30분 전
          },
        ];
        setCrewMemberStatuses(sampleStatuses);
        return;
      }

      // 크루 정보 확인 (매개변수 또는 상태에서)
      const targetCrew = crew || myCrew;
      if (!targetCrew?.id) {
        console.log('❌ 가입된 크루가 없음');
        setCrewMemberStatuses([]);
        return;
      }

      console.log('📊 오늘 기준 크루 멤버 상태 로드 - 크루 ID:', targetCrew.id);
      
      // 오늘 기준 멤버 나눔 상태 API 호출
      const memberStatuses = await crewAPI.getMyCrewMembersToday();
      console.log('✅ 오늘 기준 크루 멤버 상태 로드 완료:', memberStatuses);
      
      setCrewMemberStatuses(memberStatuses || []);
      
    } catch (error) {
      console.error('❌ 크루 멤버 상태 로드 실패:', error);
      setCrewMemberStatuses([]);
    }
  };

  // 중복 함수 제거됨 - 아래쪽에 동일한 함수가 있음

  // 현재 사용자가 크루장인지 확인
  const isCurrentUserCrewLeader = useCallback((crew: any) => {
    if (isGuestMode) {
      console.log('🎭 게스트 모드 - 크루장 권한 없음');
      return false;
    }
    
    const isLeader = user?.id === crew?.leaderId;
    console.log('🔍 크루장 권한 확인:', {
      userId: user?.id,
      crewLeaderId: crew?.leaderId,
      crewName: crew?.name,
      isLeader,
      userInfo: user
    });
    
    return isLeader;
  }, [isGuestMode, user?.id, user]);

  // 크루 수정 모달 열기
  const openCrewEditModal = (crew: any) => {
    console.log('✏️ 크루 수정 모달 열기:', {
      crewName: crew.name,
      crewId: crew.id,
      userId: user?.id,
      crewLeaderId: crew.leaderId,
      isLeader: user?.id === crew.leaderId
    });
    setEditingCrew(crew);
    setCrewEditData({
      name: crew.name || '',
      description: crew.description || '',
      image: crew.image || ''
    });
    setShowCrewEditModal(true);
  };

  // 크루 정보 수정 함수
  const handleUpdateCrew = async () => {
    if (!editingCrew || !crewEditData.name.trim()) {
      alert('크루명은 필수입니다.');
      return;
    }

    try {
      setIsUpdatingCrew(true);
      console.log('✏️ 크루 정보 수정 시작:', editingCrew.id, crewEditData);

      const updatedCrew = await crewAPI.updateCrew(editingCrew.id, crewEditData);
      console.log('✅ 크루 정보 수정 완료:', updatedCrew);

      // 상태 업데이트
      setMyCrew(updatedCrew);
      
      // 크루 데이터 새로고침
      await loadCrewData();
      await loadUserCrew();

      // 선택된 크루 상세 정보도 업데이트
      if (selectedCrewDetail?.id === updatedCrew.id) {
        setSelectedCrewDetail(updatedCrew);
      }

      setShowCrewEditModal(false);
      alert('크루 정보가 성공적으로 수정되었습니다.');
      
    } catch (error: any) {
      console.error('❌ 크루 정보 수정 실패:', error);
      alert(`❌ ${error.message || '크루 정보 수정에 실패했습니다.'}`);
    } finally {
      setIsUpdatingCrew(false);
    }
  };

  // 크루 멤버 목록 로드 (권한 이관용)
  const loadCrewMembersForTransfer = async (crewId: string) => {
    try {
      console.log('🔄 크루 멤버 목록 로드 시작:', crewId);
      const crewDetail = await crewAPI.getCrewDetail(crewId);
      if (crewDetail && crewDetail.members) {
        // 현재 사용자(크루장) 제외한 멤버만 필터링
        const otherMembers = crewDetail.members.filter((member: any) => member.user.id !== user?.id);
        setCrewMembersForTransfer(otherMembers);
        console.log('✅ 크루 멤버 목록 로드 완료:', otherMembers.length, '명');
      }
    } catch (error) {
      console.error('❌ 크루 멤버 목록 로드 실패:', error);
      setCrewMembersForTransfer([]);
    }
  };

  // 권한 이관 모달 열기
  const openLeadershipTransferModal = async (crew: any) => {
    console.log('🔄 권한 이관 모달 열기:', crew.name);
    setSelectedCrewForTransfer(crew);
    setSelectedNewLeader('');
    setShouldLeaveAfterTransfer(false); // 일반 권한 이관은 나가지 않음
    await loadCrewMembersForTransfer(crew.id);
    setShowLeadershipTransferModal(true);
  };

  // 권한 이관 실행
  const handleTransferLeadership = async () => {
    if (!selectedCrewForTransfer || !selectedNewLeader) {
      alert('새로운 크루장을 선택해주세요.');
      return;
    }

    const newLeaderName = crewMembers.find(m => m.id === selectedNewLeader)?.name;
    
    let confirmMessage = `"${selectedCrewForTransfer.name}" 크루의 크루장 권한을 이관하시겠습니까?\n\n` +
      `새로운 크루장: ${newLeaderName}\n\n` +
      `권한 이관 후에는 일반 멤버가 됩니다.`;
    
    // 나가기 플래그가 설정되어 있으면 메시지 추가
    if (shouldLeaveAfterTransfer) {
      confirmMessage += '\n\n⚠️ 권한 이관 후 자동으로 크루에서 나갑니다.';
    }

    const confirmed = confirm(confirmMessage);

    if (!confirmed) return;

    try {
      setIsTransferringLeadership(true);
      console.log('🔄 크루 권한 이관 시작:', {
        crewId: selectedCrewForTransfer.id,
        newLeaderId: selectedNewLeader,
        shouldLeaveAfterTransfer
      });

      // 권한 이관 후 나가기 플래그가 설정되어 있으면 새 API 사용 (타이밍 이슈 완전 해결)
      if (shouldLeaveAfterTransfer) {
        console.log('🚪 권한 이관 + 나가기 (하나의 트랜잭션으로 처리)');
        
        try {
          // 새로운 API: 권한 이관 + 나가기를 한 번에 처리
          const result = await crewAPI.transferLeadershipAndLeave(
            selectedCrewForTransfer.id,
            selectedNewLeader
          );
          
          console.log('✅ 권한 이관 + 나가기 완료:', result);
          
          // 상태 업데이트
          setMyCrew(null);
          setUserCrewStatus('none');
          setShowLeadershipTransferModal(false);
          setShouldLeaveAfterTransfer(false);
          setSelectedCrewForTransfer(null);
          
          // 크루 데이터 새로고침
          await loadCrewData();
          await loadUserCrew();
          
          // 모든 모달 닫기
          setShowCrewDetailModal(false);
          setShowJoinCrewModal(false);
          setSelectedCrewDetail(null);
          setCrewDetailMembers([]);
          
          alert(`✅ 크루장 권한을 "${newLeaderName}"님에게 이관하고\n"${selectedCrewForTransfer.name}" 크루에서 나갔습니다.`);
          
        } catch (leaveError: any) {
          console.error('❌ 권한 이관 + 나가기 실패:', leaveError);
          alert(`❌ ${leaveError.message || '권한 이관 + 나가기에 실패했습니다.'}`);
          
          // 실패 시에도 모달은 닫고 상태 새로고침
          setShowLeadershipTransferModal(false);
          setShouldLeaveAfterTransfer(false);
          await loadCrewData();
          await loadUserCrew();
        }
        
      } else {
        // 권한 이관만 수행 (나가기 없음)
        const updatedCrew = await crewAPI.transferLeadership(selectedCrewForTransfer.id, selectedNewLeader);
        console.log('✅ 크루 권한 이관 완료:', updatedCrew);
        
        setMyCrew(updatedCrew);
        setShowLeadershipTransferModal(false);
        
        // 크루 데이터 새로고침
        await loadCrewData();
        await loadUserCrew();

        alert('크루장 권한이 성공적으로 이관되었습니다.\n이제 일반 멤버로 나갈 수 있습니다.');
      }
      
    } catch (error: any) {
      console.error('❌ 크루 권한 이관 실패:', error);
      alert(`❌ ${error.message || '크루 권한 이관에 실패했습니다.'}`);
    } finally {
      setIsTransferringLeadership(false);
    }
  };

  // 크루 삭제 함수
  const handleDeleteCrew = async (crew: any) => {
    const confirmed = confirm(
      `"${crew.name}" 크루를 완전히 삭제하시겠습니까?\n\n` +
      `⚠️ 이 작업은 되돌릴 수 없습니다.\n` +
      `모든 멤버가 자동으로 나갑니다.`
    );

    if (!confirmed) return;

    try {
      console.log('🗑️ 크루 삭제 시작:', crew.name);
      await crewAPI.deleteCrew(crew.id);
      console.log('✅ 크루 삭제 완료');

      // 상태 초기화
      setMyCrew(null);
      setUserCrewStatus('none');
      setShowCrewDetailModal(false);
      setSelectedCrewDetail(null);
      
      // 크루 데이터 새로고침
      await loadCrewData();
      await loadUserCrew();

      alert('크루가 성공적으로 삭제되었습니다.');
      
    } catch (error: any) {
      console.error('❌ 크루 삭제 실패:', error);
      alert(`❌ ${error.message || '크루 삭제에 실패했습니다.'}`);
    }
  };

  // 크루 공유 함수 (모든 멤버 가능)
  const handleShareCrew = async (crew: any) => {
    try {
      console.log('🔗 크루 공유 시작:', crew.name);
      const shareData = await crewAPI.shareCrew(crew.id);
      
      // 클립보드에 복사
      await navigator.clipboard.writeText(shareData.shareUrl);
      
      alert(
        `✅ "${shareData.crewName}" 크루 공유 링크가 클립보드에 복사되었습니다!\n\n` +
        `📋 링크: ${shareData.shareUrl}\n\n` +
        `💡 이 링크를 다른 사용자에게 공유하면 가입 신청할 수 있습니다.`
      );
    } catch (error: any) {
      console.error('❌ 크루 공유 실패:', error);
      alert(`❌ 크루 공유에 실패했습니다: ${error.message}`);
    }
  };

  // 가입 신청 목록 조회 (크루장만)
  const loadJoinRequests = async (crewId: string) => {
    try {
      setIsLoadingRequests(true);
      console.log('📋 가입 신청 목록 조회 시작:', crewId);
      const requests = await crewAPI.getJoinRequests(crewId);
      setJoinRequests(requests);
      console.log('✅ 가입 신청 목록 조회 완료:', requests.length, '건');
    } catch (error: any) {
      console.error('❌ 가입 신청 목록 조회 실패:', error);
      alert(`❌ 가입 신청 목록 조회에 실패했습니다: ${error.message}`);
    } finally {
      setIsLoadingRequests(false);
    }
  };

  // 가입 신청 관리 모달 열기
  const openJoinRequestsModal = async (crew: any) => {
    setSelectedCrewDetail(crew);
    setShowJoinRequestsModal(true);
    await loadJoinRequests(crew.id);
  };

  // 가입 신청 승인/거부 처리
  const handleJoinRequestAction = async (requestId: string, status: 'APPROVED' | 'REJECTED', userName: string) => {
    try {
      console.log('✅ 가입 신청 처리 시작:', { requestId, status, userName });
      
      const result = await crewAPI.handleJoinRequest(selectedCrewDetail.id, requestId, status);
      console.log('✅ 가입 신청 처리 완료:', result);

      const actionText = status === 'APPROVED' ? '승인' : '거부';
      alert(`✅ ${userName}님의 가입 신청을 ${actionText}했습니다.`);

      // 가입 신청 목록 새로고침
      await loadJoinRequests(selectedCrewDetail.id);
      
      // 크루 상세 정보 새로고침 (멤버 목록 업데이트)
      if (status === 'APPROVED') {
        console.log('🔄 승인 후 크루 상세 정보 새로고침...');
        const crewDetail = await crewAPI.getCrewDetail(selectedCrewDetail.id);
        if (crewDetail) {
          setSelectedCrewDetail(crewDetail);
          setCrewDetailMembers(crewDetail.members || []);
        }
      }
      
      // 크루 데이터 새로고침 (멤버 수 업데이트)
      await loadCrewData();
      await loadUserCrew();
      
    } catch (error: any) {
      console.error('❌ 가입 신청 처리 실패:', error);
      alert(`❌ 가입 신청 처리에 실패했습니다: ${error.message}`);
    }
  };


  // 음성 읽기 함수 (개선된 버전)
  const handleVoiceReading = () => {
    console.log('🎧 음성 읽기 버튼 클릭:', { isVoiceReading, voiceSynthesis: !!voiceSynthesis });
    
    if (!voiceSynthesis) {
      console.log('🔧 SpeechSynthesis 초기화 중...');
      setVoiceSynthesis(window.speechSynthesis);
      // 초기화 후 바로 시작하지 않고 사용자가 다시 클릭하도록 함
      return;
    }

    if (isVoiceReading) {
      // 음성 읽기 중지
      console.log('🔇 음성 읽기 중지 요청');
      window.speechSynthesis.cancel();
      setIsVoiceReading(false);
      setVoiceReadingCount(0);
      console.log('🔇 음성 읽기 중지 완료');
    } else {
      // 음성 읽기 시작
      console.log('🔊 음성 읽기 시작 요청');
      startVoiceReading();
    }
  };

  // 말씀 주소를 음성 읽기용으로 포맷팅하는 함수
  const formatVerseReferenceForSpeech = (reference: string) => {
    if (!reference) return '';
    
    // "4장 13절" -> "사장 십삼절"로 변환
    return reference
      .replace(/(\d+)장/g, (match, num) => {
        const numbers = ['영', '일', '이', '삼', '사', '오', '육', '칠', '팔', '구'];
        const tens = ['', '십', '이십', '삼십', '사십', '오십'];
        const ones = ['', '일', '이', '삼', '사', '오', '육', '칠', '팔', '구'];
        
        const n = parseInt(num);
        if (n < 10) return numbers[n] + '장';
        if (n < 100) {
          const ten = Math.floor(n / 10);
          const one = n % 10;
          return (ten > 1 ? tens[ten] : '') + (one > 0 ? ones[one] : '') + '장';
        }
        return num + '장';
      })
      .replace(/(\d+)절/g, (match, num) => {
        const numbers = ['영', '일', '이', '삼', '사', '오', '육', '칠', '팔', '구'];
        const tens = ['', '십', '이십', '삼십', '사십', '오십'];
        const ones = ['', '일', '이', '삼', '사', '오', '육', '칠', '팔', '구'];
        
        const n = parseInt(num);
        if (n < 10) return numbers[n] + '절';
        if (n < 100) {
          const ten = Math.floor(n / 10);
          const one = n % 10;
          return (ten > 1 ? tens[ten] : '') + (one > 0 ? ones[one] : '') + '절';
        }
        return num + '절';
      });
  };

  // 음성 읽기 시작 함수 (개선된 연속 읽기)
  const startVoiceReading = () => {
    console.log('🎯 startVoiceReading 함수 호출');
    
    const currentVerseReference = customVerse.reference || todayVerse?.reference || DEFAULT_VERSE.reference;
    const currentVerseText = customVerse.text || todayVerse?.text || DEFAULT_VERSE.text;
    
    console.log('📖 말씀 정보:', { 
      reference: currentVerseReference, 
      text: currentVerseText,
      customVerse,
      todayVerse,
      DEFAULT_VERSE
    });
    
    // 주소와 내용을 함께 읽기 (말씀 주소를 올바르게 읽도록 수정)
    const formattedReference = formatVerseReferenceForSpeech(currentVerseReference);
    const fullText = `${formattedReference}. ${currentVerseText}`;
    console.log('🔊 읽을 텍스트:', fullText);
    
    // SpeechSynthesis 지원 확인
    if (!window.speechSynthesis) {
      console.error('❌ SpeechSynthesis를 지원하지 않는 브라우저입니다.');
      alert('이 브라우저는 음성 읽기 기능을 지원하지 않습니다.');
      return;
    }
    
    console.log('✅ SpeechSynthesis 지원 확인됨');
    
    // 기존 음성 중지
    window.speechSynthesis.cancel();
    
    // 첫 번째 읽기 시작
    console.log('🚀 첫 번째 읽기 시작');
    
    // 잠시 대기 후 시작 (브라우저 정리 시간)
    setTimeout(() => {
      // 상태를 먼저 설정
      setIsVoiceReading(true);
      console.log('✅ isVoiceReading 상태를 true로 설정');
      
        // 연속 읽기를 위한 재귀 함수
        const speakNext = (count: number) => {
          console.log(`🔄 speakNext 호출: ${count}/${maxVoiceReadingCount}`);
          
          if (count > maxVoiceReadingCount) {
            // 설정된 횟수 완료 시 자동 중지
            setIsVoiceReading(false);
            setVoiceReadingCount(0);
            console.log(`✅ 음성 읽기 ${maxVoiceReadingCount}번 완료`);
            
            // 완료 알림
            alert(`🎉 말씀을 ${maxVoiceReadingCount}번 모두 읽었습니다!\n\n하나님의 말씀이 마음에 깊이 새겨졌을 것입니다.`);
            return;
          }

          // 본문만 읽기 옵션 (말씀 주소 제외)
          const textToRead = isReadingVerseOnly ? 
            (customVerse.text || todayVerse?.text || DEFAULT_VERSE.text) : 
            fullText;

          const utterance = new SpeechSynthesisUtterance(textToRead);
        utterance.lang = 'ko-KR';
        
        // iOS 감지
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        const isIOSSafari = isIOS && isSafari;
        
        // iOS에서는 더 자연스러운 설정 사용
        if (isIOSSafari) {
          utterance.rate = Math.min(voiceReadingSpeed * 0.9, 1.0); // iOS에서는 조금 느리게
          utterance.pitch = 0.85; // iOS에서는 낮은 피치로 (무서운 음성 방지)
          utterance.volume = 1.0;
          console.log('📱 iOS Safari 감지 - 최적화된 음성 설정 적용');
        } else {
          utterance.rate = voiceReadingSpeed;
          utterance.pitch = 1.0;
          utterance.volume = 1.0;
          console.log('🖥️ 일반 브라우저 - 기본 음성 설정 적용');
        }

        // 한국어 음성 찾기 (iOS 우선순위)
        const voices = window.speechSynthesis.getVoices();
        console.log('🔍 사용 가능한 음성 목록:', voices.map(v => `${v.name} (${v.lang})`));
        
        let koreanVoice = null;
        
        if (isIOSSafari) {
          // iOS에서 더 자연스러운 음성 우선 선택
          koreanVoice = voices.find(voice => 
            voice.lang === 'ko-KR' && 
            (voice.name.includes('Yuna') || voice.name.includes('유나'))
          ) || voices.find(voice => 
            voice.lang.includes('ko-KR')
          ) || voices.find(voice => 
            voice.lang.includes('ko')
          );
          
          if (koreanVoice) {
            console.log('🍎 iOS 최적화 음성 선택:', koreanVoice.name);
          }
        } else {
          // 안드로이드/기타 브라우저
          koreanVoice = voices.find(voice => 
            voice.lang.includes('ko') || 
            voice.name.includes('Korean') || 
            voice.name.includes('한국어')
          );
          
          if (koreanVoice) {
            console.log('🤖 한국어 음성 선택:', koreanVoice.name);
          }
        }
        
        if (koreanVoice) {
          utterance.voice = koreanVoice;
        } else {
          console.warn('⚠️ 한국어 음성을 찾을 수 없습니다. 기본 음성 사용');
        }

        console.log('🎵 utterance 설정:', {
          lang: utterance.lang,
          rate: utterance.rate,
          pitch: utterance.pitch,
          volume: utterance.volume,
          voice: utterance.voice?.name || '기본 음성'
        });

        // 현재 횟수 업데이트
        setVoiceReadingCount(count);
        console.log(`🔄 음성 읽기: ${count}/${maxVoiceReadingCount}`);

        // 음성 읽기 완료 시 즉시 다음 반복
        utterance.onend = () => {
          console.log(`✅ ${count}번째 읽기 완료`);
          // 약간의 지연 없이 바로 다음 반복 시작
          setTimeout(() => {
            speakNext(count + 1);
          }, 50); // 최소한의 지연 (50ms)로 연속성 보장
        };

        // 음성 읽기 시작
        console.log('🔊 utterance.speak() 호출');
        window.speechSynthesis.speak(utterance);
      };

      speakNext(1);
      console.log('🔊 음성 읽기 시작:', fullText);
    }, 100);
  };

  // 음성 읽기 중지 함수
  const stopVoiceReading = () => {
    if (isVoiceReading) {
      window.speechSynthesis.cancel();
      setIsVoiceReading(false);
      setVoiceReadingCount(0);
      console.log('🔇 음성 읽기 강제 중지');
    }
  };

  // 묵상 편집 모달 열기
  const openMeditationEditModal = (meditation: MeditationData) => {
    console.log('✏️ 묵상 편집 모달 열기:', meditation.verseReference);
    setEditingMeditation(meditation);
    setMeditationEditData({
      verseReference: meditation.verseReference || '',
      verseText: meditation.verseText || '',
      godsWill: meditation.godsWill || '',
      growthChange: meditation.growthChange || '',
      prayer: meditation.prayer || ''
    });
    setShowMeditationEditModal(true);
  };

  // 묵상 정보 수정 함수
  const handleUpdateMeditation = async () => {
    if (!editingMeditation) return;

    try {
      setIsUpdatingMeditation(true);
      console.log('✏️ 묵상 정보 수정 시작:', editingMeditation.id, meditationEditData);

      const result = await meditationAPI.updateMeditation(editingMeditation.id!, meditationEditData);
      console.log('✅ 묵상 정보 수정 완료:', result);

      // 묵상 히스토리 새로고침
      await loadMeditationHistory();
      
      // 홈 화면의 오늘의 묵상 데이터 새로고침
      await loadTodayMeditation();

      setShowMeditationEditModal(false);
      alert('묵상 기록이 성공적으로 수정되었습니다.');
      
    } catch (error: any) {
      console.error('❌ 묵상 정보 수정 실패:', error);
      alert(`❌ ${error.message || '묵상 기록 수정에 실패했습니다.'}`);
    } finally {
      setIsUpdatingMeditation(false);
    }
  };

  // 피드 게시물 좋아요 토글
  const toggleLike = async (postId: string) => {
    if (isGuestMode) {
      // 게스트 모드에서는 로컬 상태만 업데이트
      setLikedPosts(prev => {
        const newSet = new Set(prev);
        if (newSet.has(postId)) {
          newSet.delete(postId);
        } else {
          newSet.add(postId);
        }
        return newSet;
      });
      return;
    }

    try {
      console.log('❤️ 좋아요 토글:', postId);
      // TODO: API 호출 구현
      // await feedAPI.toggleLike(postId);
      
      // 임시로 로컬 상태만 업데이트
      setLikedPosts(prev => {
        const newSet = new Set(prev);
        if (newSet.has(postId)) {
          newSet.delete(postId);
        } else {
          newSet.add(postId);
        }
        return newSet;
      });
      
    } catch (error) {
      console.error('❌ 좋아요 토글 실패:', error);
    }
  };

  // 피드 게시물 상세 모달 열기
  const openFeedDetailModal = (post: MeditationData) => {
    setSelectedFeedItem(post);
    setShowFeedDetailModal(true);
  };

  // 카카오톡 공유 함수
  const shareToKakao = (post: MeditationData) => {
    console.log('📤 카카오톡 공유 시도:', post.verseReference, 'ID:', post.id);
    
    // 공유 URL 생성 (피드 탭 + 게시물 ID)
    const shareUrl = `${window.location.origin}/?tab=feed&post=${post.id}`;
    console.log('🔗 공유 URL:', shareUrl);
    
    // 모바일 환경 감지
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // 카카오 SDK 초기화 확인 및 안전한 접근
    if (typeof window !== 'undefined' && (window as any).Kakao) {
      const kakao = (window as any).Kakao;
      
      if (!kakao.isInitialized()) {
        console.error('❌ 카카오 SDK가 초기화되지 않았습니다.');
        fallbackShare(post, shareUrl);
        return;
      }
      
      try {
        // 모바일에서는 sendDefault 대신 다른 방법 사용
        if (isMobile) {
          // 모바일에서는 카카오톡 앱이 설치되어 있는지 확인
          if (kakao.isInApp()) {
            // 카카오톡 인앱에서는 sendDefault 사용
            kakao.Share.sendDefault({
              objectType: 'feed',
              content: {
                title: `${post.user?.name || '익명'}님의 묵상 나눔`,
                description: `${post.verseReference}\n"${post.verseText}"`,
                imageUrl: `${import.meta.env.VITE_FRONTEND_URL || 'https://www.e2bmove.com'}/logo-512.png`,
                link: {
                  mobileWebUrl: shareUrl,
                  webUrl: shareUrl,
                },
              },
              buttons: [
                {
                  title: 'e2b에서 보기',
                  link: {
                    mobileWebUrl: shareUrl,
                    webUrl: shareUrl,
                  },
                },
              ],
            });
          } else {
            // 카카오톡 앱이 설치되어 있지 않은 경우 대체 방법 사용
            fallbackShare(post, shareUrl);
            return;
          }
        } else {
          // 데스크톱에서는 sendDefault 사용
          kakao.Share.sendDefault({
            objectType: 'feed',
            content: {
              title: `${post.user?.name || '익명'}님의 묵상 나눔`,
              description: `${post.verseReference}\n"${post.verseText}"`,
              imageUrl: `${import.meta.env.VITE_FRONTEND_URL || 'https://www.e2bmove.com'}/logo-512.png`,
              link: {
                mobileWebUrl: shareUrl,
                webUrl: shareUrl,
              },
            },
            buttons: [
              {
                title: 'e2b에서 보기',
                link: {
                  mobileWebUrl: shareUrl,
                  webUrl: shareUrl,
                },
              },
            ],
          });
        }
        console.log('✅ 카카오톡 공유 성공');
      } catch (error) {
        console.error('❌ 카카오톡 공유 실패:', error);
        fallbackShare(post, shareUrl);
      }
    } else {
      console.log('ℹ️ 카카오 SDK 없음 - 대체 공유 방법 사용');
      fallbackShare(post, shareUrl);
    }
  };

  // 대체 공유 방법
  const fallbackShare = (post: MeditationData, shareUrl: string) => {
    const shareText = `${post.user?.name || '익명'}님의 묵상 나눔\n\n${post.verseReference}\n"${post.verseText}"\n\ne2b에서 확인하기: ${shareUrl}`;
    
    // 모바일에서 웹 공유 API 지원 확인
    if (navigator.share) {
      navigator.share({
        title: `${post.user?.name || '익명'}님의 묵상 나눔`,
        text: shareText,
        url: shareUrl
      }).then(() => {
        console.log('✅ 웹 공유 API 성공');
      }).catch((error) => {
        console.error('❌ 웹 공유 API 실패:', error);
        copyToClipboard(shareText);
      });
    } else {
      // 웹 공유 API 미지원 시 클립보드 복사
      copyToClipboard(shareText);
    }
  };

  // 클립보드 복사
  const copyToClipboard = (text: string) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        alert('💬 공유 내용이 클립보드에 복사되었습니다!\n카카오톡이나 다른 앱에 붙여넣기 해주세요.');
      }).catch((error) => {
        console.error('❌ 클립보드 복사 실패:', error);
        // 대체 방법: execCommand 사용
        fallbackCopyToClipboard(text);
      });
    } else {
      // 대체 방법: execCommand 사용
      fallbackCopyToClipboard(text);
    }
  };

  // 대체 클립보드 복사 방법
  const fallbackCopyToClipboard = (text: string) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        alert('💬 공유 내용이 클립보드에 복사되었습니다!\n카카오톡이나 다른 앱에 붙여넣기 해주세요.');
      } else {
        alert('❌ 클립보드 복사에 실패했습니다.\n수동으로 복사해주세요:\n\n' + text);
      }
    } catch (err) {
      console.error('❌ execCommand 복사 실패:', err);
      alert('❌ 클립보드 복사에 실패했습니다.\n수동으로 복사해주세요:\n\n' + text);
    } finally {
      document.body.removeChild(textArea);
    }
  };

  // 멤버 묵상 공유 함수
  const shareMemberMeditation = (member: any, meditation: any) => {
    console.log('📤 멤버 묵상 공유 시도:', member.name, meditation.verseReference);
    
    // 공유 URL 생성 (멤버 ID와 묵상 ID 포함)
    const shareUrl = `${window.location.origin}/?tab=crews&member=${member.id}&meditation=${meditation.id}`;
    console.log('🔗 공유 URL:', shareUrl);
    
    // 공유 텍스트 생성
    const shareText = `${member.name}님의 묵상 나눔\n\n📖 ${meditation.verseReference}\n"${meditation.verseText}"\n\n💭 ${meditation.godsWill || '하나님의 뜻을 묵상했습니다.'}\n\n🌱 ${meditation.growthChange || '성장의 다짐을 했습니다.'}\n\n🙏 ${meditation.prayer || '기도의 마음을 담았습니다.'}\n\ne2b에서 확인하기: ${shareUrl}`;
    
    // 모바일에서 웹 공유 API 지원 확인
    if (navigator.share) {
      navigator.share({
        title: `${member.name}님의 묵상 나눔`,
        text: shareText,
        url: shareUrl
      }).then(() => {
        console.log('✅ 웹 공유 API 성공');
      }).catch((error) => {
        console.error('❌ 웹 공유 API 실패:', error);
        copyToClipboard(shareText);
      });
    } else {
      // 웹 공유 API 미지원 시 클립보드 복사
      copyToClipboard(shareText);
    }
  };

  // 공유된 멤버 묵상 로드
  const loadSharedMemberMeditation = async (memberId: string, meditationId: string) => {
    try {
      console.log('📖 공유된 멤버 묵상 로드:', memberId, meditationId);
      
      // 게스트 모드에서는 샘플 데이터 사용
      if (isGuestMode) {
        const sampleMember = {
          id: memberId,
          name: '공유된 멤버',
          avatar: null
        };
        
        const sampleMeditation = {
          id: meditationId,
          date: new Date().toISOString().split('T')[0],
          verseReference: '요한복음 3:16',
          verseText: '하나님이 세상을 이처럼 사랑하사 독생자를 주셨으니 이는 그를 믿는 자마다 멸망하지 않고 영생을 얻게 하려 하심이라',
          godsWill: '하나님께서는 우리를 사랑하시며, 구원의 길을 열어주셨다는 것을 깨달았습니다.',
          growthChange: '더욱 겸손한 마음으로 하나님의 사랑에 응답하며 살아가겠습니다.',
          prayer: '하나님의 무한한 사랑에 감사드리며, 오늘 하루도 주님의 뜻대로 살아가게 도와주세요.',
          prayerShareType: 'public'
        };
        
        setSelectedMemberForDetail(sampleMember);
        setMemberMeditationDetail(sampleMeditation);
        setShowMemberMeditationDetail(true);
        return;
      }
      
      // 인증된 사용자 - 실제 API 호출
      const meditationDetail = await crewAPI.getMemberMeditationToday(memberId);
      console.log('✅ 공유된 멤버 묵상 로드 완료:', meditationDetail);
      
      // 멤버 정보는 API 응답에서 가져오거나 기본값 사용
      const memberInfo = {
        id: memberId,
        name: meditationDetail.user?.name || '공유된 멤버',
        avatar: meditationDetail.user?.avatar || null
      };
      
      setSelectedMemberForDetail(memberInfo);
      setMemberMeditationDetail(meditationDetail);
      setShowMemberMeditationDetail(true);
      
    } catch (error: any) {
      console.error('❌ 공유된 멤버 묵상 로드 실패:', error);
      
      // 에러 발생 시 게스트 모드로 전환하여 샘플 데이터 표시
      const sampleMember = {
        id: memberId,
        name: '공유된 멤버',
        avatar: null
      };
      
      const sampleMeditation = {
        id: meditationId,
        date: new Date().toISOString().split('T')[0],
        verseReference: '시편 23:1',
        verseText: '여호와는 나의 목자시니 내게 부족함이 없으리로다',
        godsWill: '하나님께서 나의 목자가 되어주신다는 것을 믿고 의지하겠습니다.',
        growthChange: '어려운 상황에서도 하나님을 신뢰하는 믿음을 기르겠습니다.',
        prayer: '주님, 저의 목자가 되어주셔서 감사합니다. 오늘도 주님의 인도하심을 따르겠습니다.',
        prayerShareType: 'public'
      };
      
      setSelectedMemberForDetail(sampleMember);
      setMemberMeditationDetail(sampleMeditation);
      setShowMemberMeditationDetail(true);
    }
  };

  // 공지사항 데이터 로드
  const loadAnnouncements = async () => {
    if (isLoadingAnnouncements) return;
    
    setIsLoadingAnnouncements(true);
    try {
      console.log('📢 공지사항 불러오기 시작');
      const data = await getAnnouncements(1, 20);
      console.log('📢 불러온 공지사항:', data);
      
      setAnnouncements(data);
    } catch (error) {
      console.error('공지사항 로드 오류:', error);
      // 오류 시 빈 배열로 설정하여 앱이 중단되지 않도록 함
      setAnnouncements([]);
    } finally {
      setIsLoadingAnnouncements(false);
    }
  };


  // 피드 데이터 로드 (초기 로드)
  const loadFeedData = async () => {
    if (isLoadingFeed) return;
    
    // 게스트 모드에서는 API 호출하지 않음 (샘플 데이터 사용)
    if (isGuestMode) {
      console.log('🎭 게스트 모드: 샘플 피드 데이터 사용');
      // 샘플 데이터도 10개로 제한
      setFeedData(sampleFeedData.slice(0, 10) as any as MeditationData[]);
      setHasMoreFeed(sampleFeedData.length > 10);
      return;
    }
    
    // 인증된 사용자만 실제 API 호출
    if (!isAuthenticated) {
      console.log('🔒 인증되지 않은 사용자: 피드 로드 건너뛰기');
      return;
    }
    
    setIsLoadingFeed(true);
    try {
      console.log('📱 피드 데이터 불러오기 시작 (페이지 1, 30개, scope=my-crews)');
      const response = await meditationAPI.getMeditationFeed(1, 30, 'my-crews'); // 내 크루 범위
      console.log('📱 불러온 피드:', response);
      
      setFeedData(response.meditations || []);
      setFeedPage(1);
      setHasMoreFeed((response.meditations || []).length === 30); // 30개가 로드되면 더 있을 가능성
      
    } catch (error) {
      console.error('피드 데이터 불러오기 실패:', error);
      // 오류 시 빈 배열로 설정
      setFeedData([]);
      setHasMoreFeed(false);
    } finally {
      setIsLoadingFeed(false);
    }
  };

  // 추가 피드 데이터 로드 (더보기)
  const loadMoreFeedData = async () => {
    if (isLoadingMoreFeed || !hasMoreFeed) return;
    
    // 게스트 모드에서는 샘플 데이터 추가 로드
    if (isGuestMode) {
      console.log('🎭 게스트 모드: 추가 샘플 데이터 로드');
      setIsLoadingMoreFeed(true);
      
      // 시뮬레이션 딜레이
      setTimeout(() => {
        const currentLength = feedData.length;
        const nextBatch = sampleFeedData.slice(currentLength, currentLength + 10);
        setFeedData(prev => [...prev, ...nextBatch as any as MeditationData[]]);
        setHasMoreFeed(currentLength + 10 < sampleFeedData.length);
        setIsLoadingMoreFeed(false);
      }, 1000);
      return;
    }
    
    // 인증된 사용자만 실제 API 호출
    if (!isAuthenticated) {
      console.log('🔒 인증되지 않은 사용자: 추가 피드 로드 건너뛰기');
      return;
    }
    
    setIsLoadingMoreFeed(true);
    try {
      const nextPage = feedPage + 1;
      console.log(`📱 추가 피드 데이터 불러오기 시작 (페이지 ${nextPage}, 30개, scope=my-crews)`);
      const response = await meditationAPI.getMeditationFeed(nextPage, 30, 'my-crews');
      console.log('📱 추가로 불러온 피드:', response);
      
      const newMeditations = response.meditations || [];
      setFeedData(prev => [...prev, ...newMeditations]);
      setFeedPage(nextPage);
      setHasMoreFeed(newMeditations.length === 30); // 30개가 로드되면 더 있을 가능성
      
    } catch (error) {
      console.error('추가 피드 데이터 불러오기 실패:', error);
      setHasMoreFeed(false);
    } finally {
      setIsLoadingMoreFeed(false);
    }
  };

  // 공유된 게시물 자동 열기
  useEffect(() => {
    if (sharedPostId && activeTab === 'feed') {
      console.log('🔍 공유된 게시물 찾는 중:', sharedPostId);
      
      // 현재 피드 데이터 (인증된 사용자는 실제 데이터, 게스트는 샘플 데이터)
      const currentFeedData = isAuthenticated && !isGuestMode ? feedData : sampleFeedData as any[];
      
      // 피드에서 해당 게시물 찾기
      const sharedPost = currentFeedData.find(post => post.id === sharedPostId) as MeditationData;
      
      if (sharedPost) {
        console.log('✅ 공유된 게시물 발견:', sharedPost.verseReference);
        
        // 게스트 모드이고 비인증 상태라면 게스트 모드 활성화
        if (!isAuthenticated && !isGuestMode) {
          console.log('🎭 게스트 모드로 게시물 미리보기 활성화');
          setIsGuestMode(true);
        }
        
        // 상세 모달 열기
        setTimeout(() => {
          openFeedDetailModal(sharedPost);
          setSharedPostId(null); // 한 번만 실행되도록
        }, 500); // 피드 렌더링 후 모달 열기
        
      } else {
        console.log('❌ 공유된 게시물을 찾을 수 없음:', sharedPostId);
        
        // 게시물을 찾을 수 없는 경우 알림
        setTimeout(() => {
          alert('공유된 게시물을 찾을 수 없습니다. 삭제되었거나 접근 권한이 없을 수 있습니다.');
          setSharedPostId(null);
        }, 1000);
      }
    }
  }, [sharedPostId, feedData, activeTab, isAuthenticated, isGuestMode]);

  // 중복 함수 제거됨 - 위쪽에 동일한 함수가 있음

  // 미완료 묵상 기록 삭제 함수
  const deleteIncompleteMeditations = async () => {
    try {
      console.log('🗑️ 미완료 묵상 기록 삭제 시작');
      
      // 4단계 모두 완료되지 않은 묵상 기록을 삭제
      const result = await meditationAPI.deleteIncompleteMeditations();
      
      console.log('✅ 미완료 묵상 기록 삭제 완료:', result.deletedCount, '개');
      
      return result;
    } catch (error) {
      console.error('❌ 미완료 묵상 삭제 실패:', error);
      // 오류가 발생해도 묵상 시작을 막지 않음
      return { message: '삭제 실패', deletedCount: 0 };
    }
  };

  // 인증 상태 확인 및 검증 함수
  const verifyAuthentication = async (): Promise<boolean> => {
    try {
      // 1. 게스트 모드인 경우 인증 불필요
      if (isGuestMode) {
        return true;
      }

      // 2. 클라이언트 측 인증 상태 확인
      if (!isAuthenticated) {
        console.log('⚠️ 클라이언트 측 인증 상태: 로그인 필요');
        return false;
      }

      // 3. 클라이언트 측 토큰 만료 확인
      const isTokenValid = checkAuthExpiry();
      if (!isTokenValid) {
        console.log('⚠️ 클라이언트 측 토큰 만료됨');
        clearAuth();
        return false;
      }

      // 4. 서버 측 토큰 유효성 검증 (실제 API 호출)
      try {
        const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
        const token = accessToken || localStorage.getItem('accessToken');
        
        if (!token) {
          console.log('⚠️ 액세스 토큰이 없습니다');
          clearAuth();
          return false;
        }

        // 서버에 프로필 조회 요청으로 토큰 유효성 검증
        const response = await fetch(`${API_BASE_URL}/users/me`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            console.log('⚠️ 서버 측 인증 실패: 토큰이 유효하지 않습니다');
            clearAuth();
            return false;
          }
          // 기타 오류는 일단 통과 (네트워크 오류 등)
          console.warn('⚠️ 서버 응답 오류:', response.status);
        } else {
          console.log('✅ 서버 측 인증 확인 완료');
        }

        return true;
      } catch (serverError: any) {
        // 네트워크 오류 등은 일단 통과 (사용자가 묵상은 시작할 수 있도록)
        console.warn('⚠️ 서버 인증 검증 중 오류 (계속 진행):', serverError.message);
        return true; // 네트워크 오류는 일단 통과
      }
    } catch (error) {
      console.error('인증 검증 중 오류:', error);
      return false;
    }
  };

  // 새로운 묵상 시작 (완전 초기화)
  const startNewMeditation = async () => {
    try {
      console.log('🆕 새로운 묵상 시작');
      
      // 0. 인증 상태 확인 및 검증 (게스트 모드가 아닌 경우)
      if (!isGuestMode) {
        const isAuthValid = await verifyAuthentication();
        
        if (!isAuthValid) {
          console.log('🚫 인증 실패 - 로그인 모달 표시');
          
          // 사용자에게 명확한 안내 메시지 (toast 사용)
          toast.error('로그인이 필요합니다', {
            duration: 4000,
            style: {
              background: '#fef2f2',
              color: '#dc2626',
              border: '1px solid #fecaca',
              borderRadius: '12px',
              padding: '12px 16px',
              fontSize: '14px',
              fontWeight: '500',
            },
            icon: '🔐',
          });
          
          // 추가 안내 메시지
          setTimeout(() => {
            toast('묵상 내용을 저장하려면 로그인이 필요합니다.\n로그인 후 다시 시도해주세요.', {
              duration: 3000,
              style: {
                background: '#fffbeb',
                color: '#92400e',
                border: '1px solid #fde68a',
                borderRadius: '12px',
                padding: '12px 16px',
                fontSize: '13px',
              },
            });
          }, 500);
          
          // 로그인 모달 표시
          setAuthMode('login');
          setShowAuthModal(true);
          
          return; // 묵상 시작 중단
        }
        
        console.log('✅ 인증 확인 완료 - 묵상 시작 진행');
      }
      
      // 1. 미완료 묵상 기록 삭제 (인증된 사용자만)
      if (isAuthenticated && !isGuestMode) {
        await deleteIncompleteMeditations();
      }
      
      // 2. 모든 상태 완전 초기화
      setMeditationStep(1);
      setReadingCount(0);
      setMeditationData({ godsWill: '', growthChange: '', prayer: '' });
      setCustomVerse({ reference: '', text: '' });
      setTempVerse({ reference: '', text: '' });
      setPrayerShareType('public');
      setIsNewMeditationSession(true);
      setIsEditingVerse(false);
      
      // 묵상 입력 모달 관련 상태도 초기화
      setShowMeditationInputModal(false);
      setMeditationInputField('godsWill');
      setMeditationInputTitle('');
      setMeditationInputPlaceholder('');
      setMeditationInputValue('');
      
      // 3. 묵상 화면 표시
      setShowMeditation(true);
      
      console.log('✅ 새로운 묵상 시작 완료 - 모든 상태 초기화됨');
    } catch (error) {
      console.error('새로운 묵상 시작 중 오류:', error);
      // 오류가 발생해도 묵상은 시작할 수 있도록
      setShowMeditation(true);
    }
  };

  // 이어서 묵상하기 (기존 데이터 복원)
  const continueMeditation = async () => {
    try {
      console.log('🔄 이어서 묵상하기 시작');
      
      // 0. 인증 상태 확인 및 검증 (게스트 모드가 아닌 경우)
      if (!isGuestMode) {
        const isAuthValid = await verifyAuthentication();
        
        if (!isAuthValid) {
          console.log('🚫 인증 실패 - 로그인 모달 표시');
          
          toast.error('로그인이 필요합니다', {
            duration: 4000,
            style: {
              background: '#fef2f2',
              color: '#dc2626',
              border: '1px solid #fecaca',
              borderRadius: '12px',
              padding: '12px 16px',
              fontSize: '14px',
              fontWeight: '500',
            },
            icon: '🔐',
          });
          
          setTimeout(() => {
            toast('묵상 내용을 저장하려면 로그인이 필요합니다.\n로그인 후 다시 시도해주세요.', {
              duration: 3000,
              style: {
                background: '#fffbeb',
                color: '#92400e',
                border: '1px solid #fde68a',
                borderRadius: '12px',
                padding: '12px 16px',
                fontSize: '13px',
              },
            });
          }, 500);
          
          setAuthMode('login');
          setShowAuthModal(true);
          return;
        }
        
        console.log('✅ 인증 확인 완료 - 이어서 묵상하기 진행');
      }
      
      // 1. 서버에서 최신 묵상 데이터 불러오기
      console.log('📚 서버에서 오늘의 묵상 데이터 불러오기 시작');
      let meditationData = todayMeditationData;
      
      // 서버에서 최신 데이터 불러오기 (캐시된 데이터가 아닌 최신 데이터)
      try {
        const latestMeditation = await meditationAPI.getTodayMeditation();
        console.log('📚 서버에서 불러온 최신 묵상 데이터:', latestMeditation);
        meditationData = latestMeditation;
        
        // todayMeditationData도 업데이트
        setTodayMeditationData(latestMeditation);
      } catch (error) {
        console.error('서버에서 묵상 데이터 불러오기 실패, 캐시된 데이터 사용:', error);
        // 서버에서 불러오기 실패 시 캐시된 데이터 사용
        if (!meditationData) {
          toast.error('묵상 데이터를 불러올 수 없습니다', {
            duration: 3000,
          });
          return;
        }
      }
      
      // 2. 데이터 확인
      if (!meditationData) {
        console.log('⚠️ 오늘의 묵상 데이터가 없습니다');
        toast.error('이어서 묵상할 데이터가 없습니다', {
          duration: 3000,
        });
        return;
      }
      
      // 3. 기존 데이터 복원
      console.log('📚 기존 묵상 데이터 복원:', meditationData);
      
      // 말씀 정보 복원
      if (meditationData.verseReference && meditationData.verseText) {
        setCustomVerse({
          reference: meditationData.verseReference,
          text: meditationData.verseText
        });
      }
      
      // 읽기 횟수 복원
      setReadingCount(meditationData.readingCount || 0);
      
      // 묵상 내용 복원
      setMeditationData({
        godsWill: meditationData.godsWill || '',
        growthChange: meditationData.growthChange || '',
        prayer: meditationData.prayer || ''
      });
      
      // 공유 타입 복원
      setPrayerShareType((meditationData.prayerShareType as 'private' | 'public' | 'request') || 'private');
      
      // 이어서 묵상하기는 항상 1단계(카운팅 페이지)로 이동
      setMeditationStep(1);
      
      // 묵상 입력 모달 관련 상태 초기화
      setShowMeditationInputModal(false);
      setMeditationInputField('godsWill');
      setMeditationInputTitle('');
      setMeditationInputPlaceholder('');
      setMeditationInputValue('');
      setIsEditingVerse(false);
      
      // 3. 묵상 화면 표시
      // 주의: setIsNewMeditationSession(true)를 설정하여 useEffect의 loadTodayMeditationForScreen이
      // 실행되지 않도록 함 (이미 데이터를 복원했으므로)
      setIsNewMeditationSession(true);
      setShowMeditation(true);
      
      // 데이터 복원이 완료된 후 약간의 지연을 두고 플래그를 false로 변경
      // 이렇게 하면 이후 데이터 저장 시 정상적으로 작동함
      setTimeout(() => {
        setIsNewMeditationSession(false);
        console.log('🔄 이어서 묵상하기 세션 플래그 해제');
      }, 100);
      
      console.log(`✅ 이어서 묵상하기 완료 - ${nextStep}단계로 이동`);
      console.log(`📊 복원된 데이터: readingCount=${meditationData.readingCount ?? 0}, godsWill=${meditationData.godsWill ? '있음' : '없음'}, growthChange=${meditationData.growthChange ? '있음' : '없음'}, prayer=${meditationData.prayer ? '있음' : '없음'}`);
    } catch (error) {
      console.error('이어서 묵상하기 중 오류:', error);
      toast.error('이어서 묵상하기 중 오류가 발생했습니다', {
        duration: 3000,
      });
    }
  };

  // 카운트 변경 시 자동 저장
  const updateReadingCount = async (newCount: number) => {
    setReadingCount(newCount);
    
    // 실시간으로 데이터베이스에 저장
    if (isAuthenticated && !isSavingMeditation) {
      try {
        setIsSavingMeditation(true);
        console.log('📖 카운트 자동 저장:', newCount);
        
        await meditationAPI.saveMeditation({
          verseReference: customVerse.reference || todayVerse?.reference || DEFAULT_VERSE.reference,
          verseText: customVerse.text || todayVerse?.text || DEFAULT_VERSE.text,
          readingCount: newCount
        });
        
        console.log('✅ 카운트 저장 완료:', newCount);
      } catch (error) {
        console.error('카운트 저장 실패:', error);
      } finally {
        setIsSavingMeditation(false);
      }
    }
  };

  // 컴포넌트 마운트 시 묵상 데이터 불러오기
  useEffect(() => {
    if (isAuthenticated && !isGuestMode) {
      loadTodayMeditationForScreen();
    }
  }, [isAuthenticated, isGuestMode]);

  // 컴포넌트 마운트 시 기도 데이터 불러오기
  useEffect(() => {
    if (isAuthenticated) {
      loadPrayerHistory();
      loadPrayerStats();
    }
  }, [isAuthenticated]);

  // 기도 탭 활성화 시 데이터 새로고침
  useEffect(() => {
    if (isAuthenticated && activeTab === 'prayer') {
      loadPrayerHistory();
      loadPrayerStats();
    }
  }, [isAuthenticated, activeTab]);

  // 피드 탭 활성화 시 데이터 로드
  useEffect(() => {
    if (isAuthenticated && !isGuestMode && activeTab === 'feed') {
      loadFeedData();
    }
  }, [isAuthenticated, isGuestMode, activeTab]);

  // 묵상 시작 시 데이터 불러오기
  useEffect(() => {
    if (showMeditation && isAuthenticated && !isGuestMode) {
      loadTodayMeditationForScreen();
    }
  }, [showMeditation, isAuthenticated, isGuestMode]);

  // 공지사항 데이터
  const noticeData = {
    title: "e2b 플랫폼 안내",
    date: "2025년 10월",
    location: "온라인 플랫폼",
    content: `안녕하세요. 
하나님을 사랑하는 이투비처치(e2b church) 입니다.

이투비 처치의 매일 말씀먹는 운동인 e2b(eat bible everyday) 플랫폼입니다.

매일 하나님의 말씀으로 하루를 주님과 온전히 동행하는 이투비의 세계로 당신을 초대합니다.

**이투비 무브먼트 3단계 시스템**

1단계 : 오늘의 말씀 100번 반복 낭독
2단계 : 공동체 묵상 나눔
3단계 : 오늘의 말씀으로 하나님과 기도하며 하루지내기

이투비처치는 평일 오전6시 30분
이투비게더링을 통해 "함께하는 이투비"를 진행하고 있습니다.

유튜브에 [ 이투비처치 ] 검색하셔서 매일 아침 함께 
"이투비하세요"^^

이투비사용 요령은 이투비처치 홈페이지(www.e2b.co.kr)로 들어오셔서 '이투비따라하기'를 클릭하면 자세히 나와 있습니다.

누구나 자신의 크루를 만들어서 지인들과 이투비를 하실 수 있습니다. 

부디 이투비를 통해 하나님과 진정한 사랑을 나누는 주님의 자녀가 되시길 소망합니다.

이투비는 사랑입니다.

이투비처치 문의 : 임승진 부목사 (010-2954-9106)
                         joshua013@naver.com`
  };
  
  // 기도 타이머 localStorage 헬퍼 함수
  const savePrayerTimerToStorage = (startTime: string | null, accumulated: number, isPaused: boolean, target: number) => {
    try {
      if (startTime) {
        localStorage.setItem(PRAYER_STORAGE_KEYS.START_TIME, startTime);
      } else {
        localStorage.removeItem(PRAYER_STORAGE_KEYS.START_TIME);
      }
      localStorage.setItem(PRAYER_STORAGE_KEYS.ACCUMULATED_TIME, accumulated.toString());
      localStorage.setItem(PRAYER_STORAGE_KEYS.IS_PAUSED, isPaused.toString());
      localStorage.setItem(PRAYER_STORAGE_KEYS.TARGET_MINUTES, target.toString());
      console.log('💾 기도 타이머 저장:', { startTime, accumulated, isPaused, target });
    } catch (error) {
      console.error('❌ localStorage 저장 실패:', error);
    }
  };

  const clearPrayerTimerFromStorage = () => {
    try {
      Object.values(PRAYER_STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
      console.log('🗑️ 기도 타이머 localStorage 클리어');
    } catch (error) {
      console.error('❌ localStorage 클리어 실패:', error);
    }
  };

  // 로그아웃 핸들러 (기도 시간 자동 저장)
  const handleLogout = async () => {
    try {
      // 진행 중인 기도가 있으면 자동 저장
      const startTime = localStorage.getItem(PRAYER_STORAGE_KEYS.START_TIME);
      const accumulated = Number(localStorage.getItem(PRAYER_STORAGE_KEYS.ACCUMULATED_TIME) || 0);
      
      if ((startTime || accumulated > 0) && prayerTimer.time > 0) {
        console.log('💾 로그아웃 - 진행 중인 기도 시간 자동 저장:', prayerTimer.time, '초');
        await savePrayerRecord(prayerTimer.time);
        console.log('✅ 기도 시간 자동 저장 완료');
      }
      
      // 기도 타이머 정리
      clearPrayerTimerFromStorage();
      setPrayerTimer({ time: 0, isRunning: false, target: 60 });
      
      // 로그아웃 실행
      clearAuth();
      console.log('👋 로그아웃 완료');
    } catch (error) {
      console.error('❌ 로그아웃 처리 중 오류:', error);
      // 오류가 있어도 로그아웃은 진행
      clearAuth();
    }
  };

  const calculateElapsedTime = () => {
    try {
      const startTime = localStorage.getItem(PRAYER_STORAGE_KEYS.START_TIME);
      const accumulated = Number(localStorage.getItem(PRAYER_STORAGE_KEYS.ACCUMULATED_TIME) || 0);
      const isPaused = localStorage.getItem(PRAYER_STORAGE_KEYS.IS_PAUSED) === 'true';

      if (isPaused || !startTime) {
        return accumulated;
      }

      const elapsed = Math.floor((Date.now() - new Date(startTime).getTime()) / 1000);
      return accumulated + elapsed;
    } catch (error) {
      console.error('❌ 경과 시간 계산 실패:', error);
      return 0;
    }
  };

  // 컴포넌트 마운트 시 기도 타이머 복원
  useEffect(() => {
    try {
      const startTime = localStorage.getItem(PRAYER_STORAGE_KEYS.START_TIME);
      const accumulated = Number(localStorage.getItem(PRAYER_STORAGE_KEYS.ACCUMULATED_TIME) || 0);
      const isPaused = localStorage.getItem(PRAYER_STORAGE_KEYS.IS_PAUSED) === 'true';
      const target = Number(localStorage.getItem(PRAYER_STORAGE_KEYS.TARGET_MINUTES) || 60);

      if (startTime || accumulated > 0) {
        console.log('🔄 기도 타이머 복원:', { startTime, accumulated, isPaused, target });
        const elapsed = calculateElapsedTime();
        setPrayerTimer({
          time: elapsed,
          isRunning: !isPaused && !!startTime,
          target: target
        });
        
        if (!isPaused && startTime) {
          console.log('▶️ 기도 타이머 진행 중 - 백그라운드 실행 복원');
        }
      }
    } catch (error) {
      console.error('❌ 기도 타이머 복원 실패:', error);
    }
  }, []);

  // 타이머 효과 (백그라운드 실행 지원)
  useEffect(() => {
    let interval: number | null = null;
    
    if (prayerTimer.isRunning) {
      interval = window.setInterval(() => {
        const elapsed = calculateElapsedTime();
        setPrayerTimer(prev => ({ ...prev, time: elapsed }));
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [prayerTimer.isRunning]);

  // 기도 히스토리 불러오기
  const loadPrayerHistory = async () => {
    if (!isAuthenticated) return;
    
    try {
      setIsLoadingPrayerHistory(true);
      console.log('📋 기도 히스토리 불러오기 시작');
      
      const history = await prayerAPI.getPrayerHistory(10);
      setPrayerHistory(history);
      
      console.log('✅ 기도 히스토리 불러오기 성공:', history.length, '개');
      console.log('📊 기도 히스토리 데이터:', history);
    } catch (error) {
      console.error('❌ 기도 히스토리 불러오기 실패:', error);
    } finally {
      setIsLoadingPrayerHistory(false);
    }
  };

  // 기도 통계 불러오기
  const loadPrayerStats = async () => {
    if (!isAuthenticated) return;
    
    try {
      console.log('📊 기도 통계 불러오기 시작');
      
      const stats = await prayerAPI.getPrayerStats();
      setPrayerStats(stats);
      
      console.log('✅ 기도 통계 불러오기 성공:', stats);
      console.log('📈 총 기도 시간:', stats.totalDuration, '초, 총 세션:', stats.totalSessions, '개');
    } catch (error) {
      console.error('❌ 기도 통계 불러오기 실패:', error);
    }
  };

  // 기도 완료 시 기록 저장
  const savePrayerRecord = async (duration: number) => {
    if (!isAuthenticated || isGuestMode) {
      // 게스트 모드에서는 로컬 저장
      const newRecord = {
        id: Date.now().toString(),
        userId: 'guest',
        date: new Date().toISOString().split('T')[0],
        duration: duration,
        timestamp: new Date().toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' }),
        location: '',
        content: '',
        createdAt: new Date().toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' }),
        updatedAt: new Date().toISOString()
      };
      setPrayerHistory(prev => [newRecord, ...prev].slice(0, 10));
      // 시스템 prompt로 기도 내용 입력
      await handlePrayerRecordInput(newRecord.id);
      console.log('✅ 기도 기록 저장 및 입력 완료');
      return;
    }

    try {
      console.log('🙏 기도 기록 저장 중...', { duration });
      
      const newRecord = await prayerAPI.savePrayerRecord({
        duration: duration,
        location: '',
        content: ''
      });

      // 히스토리에 새 기록 추가
      setPrayerHistory(prev => [newRecord, ...prev].slice(0, 10));
      
      // 통계 갱신
      await loadPrayerStats();
      
      console.log('✅ 기도 기록 저장 성공:', newRecord);
      console.log('🔄 기도 히스토리 및 통계 자동 갱신 완료');
      
      // 시스템 prompt로 기도 내용 입력
      await handlePrayerRecordInput(newRecord.id);
    } catch (error: any) {
      console.error('❌ 기도 기록 저장 실패:', error);
      alert(error.message || '기도 기록 저장에 실패했습니다.');
    }
  };

  // 기도 기록 업데이트
  const updatePrayerRecord = async (id: string, location: string, content: string) => {
    if (!isAuthenticated || isGuestMode) {
      // 게스트 모드에서는 로컬 업데이트
      setPrayerHistory(prev => 
        prev.map(record => 
          record.id === id 
            ? { ...record, location, content }
            : record
        )
      );
      return;
    }

    try {
      console.log('🙏 기도 기록 업데이트 중...', { id, location, content });
      
      const updatedRecord = await prayerAPI.updatePrayerRecord(id, {
        location,
        content
      });

      // 히스토리 업데이트
      setPrayerHistory(prev => 
        prev.map(record => 
          record.id === id ? updatedRecord : record
        )
      );
      
      console.log('✅ 기도 기록 업데이트 성공:', updatedRecord);
    } catch (error: any) {
      console.error('❌ 기도 기록 업데이트 실패:', error);
      alert(error.message || '기도 기록 업데이트에 실패했습니다.');
    }
  };

  // 시간 포맷팅 함수
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // 기도 내용 입력 - 시스템 prompt 사용 (모달 문제 우회)
  const handlePrayerRecordInput = async (recordId: string) => {
    console.log('🙏 기도 내용 입력 시작 (시스템 prompt):', recordId);
    
    // 기도 내용 입력
    const content = prompt(
      '오늘 어떤 기도를 하셨나요?\n\n' +
      '(감사, 회개, 간구 등을 자유롭게 기록해보세요)\n' +
      '입력하지 않으시려면 취소를 누르세요.'
    );
    
    // 취소 시 종료
    if (content === null) {
      console.log('⏭️ 기도 내용 입력 건너뛰기');
      return;
    }
    
    // 기도 장소 입력 (선택)
    const location = prompt(
      '기도 장소를 입력하세요 (선택사항)\n\n' +
      '예: 집, 교회, 기도실 등\n' +
      '입력하지 않으시려면 취소 또는 빈칸으로 확인을 누르세요.'
    );
    
    // 저장
    try {
      await updatePrayerRecord(
        recordId, 
        location?.trim() || '', 
        content.trim()
      );
      console.log('✅ 기도 내용 저장 완료');
      alert('기도 내용이 저장되었습니다! 🙏');
    } catch (error) {
      console.error('❌ 기도 내용 저장 실패:', error);
      alert('저장에 실패했습니다. 다시 시도해주세요.');
    }
  };

  // 묵상 컴포넌트
  const MeditationScreen = () => {
    // 홈으로 가기 (1단계에서 카운팅 저장 후 홈으로 이동)
    const handleGoHome = async () => {
      // 음성 읽기 중지
      stopVoiceReading();
      
      // 카운팅이 0보다 크면 저장
      if (readingCount > 0 && isAuthenticated && !isGuestMode) {
        try {
          console.log('💾 홈으로 가기 전 카운팅 저장:', readingCount);
          
          const stepData = {
            verseReference: customVerse.reference || todayVerse?.reference || DEFAULT_VERSE.reference,
            verseText: customVerse.text || todayVerse?.text || DEFAULT_VERSE.text,
            readingCount: readingCount
          };
          
          await completeStep(1, stepData);
          console.log('✅ 카운팅 저장 완료');
          
          // 홈 화면의 오늘의 묵상 데이터 새로고침
          await loadTodayMeditation();
        } catch (error) {
          console.error('❌ 카운팅 저장 실패:', error);
          // 저장 실패해도 홈으로 이동
        }
      }
      
      // 묵상 화면 닫기
      setShowMeditation(false);
    };

    const handleNextStep = async () => {
      // 음성 읽기 중지
      stopVoiceReading();
      
      if (meditationStep < 4) {
        // 현재 단계 데이터 저장
        const stepData: any = {
          verseReference: customVerse.reference || todayVerse?.reference || DEFAULT_VERSE.reference,
          verseText: customVerse.text || todayVerse?.text || DEFAULT_VERSE.text
        };
        
        switch (meditationStep) {
          case 1:
            stepData.readingCount = readingCount;
            break;
          case 2:
            stepData.godsWill = meditationData.godsWill;
            break;
          case 3:
            stepData.growthChange = meditationData.growthChange;
            break;
        }
        
        await completeStep(meditationStep, stepData);
        setMeditationStep(meditationStep + 1);
        console.log(`🚀 ${meditationStep}단계 완료 후 ${meditationStep + 1}단계로 이동`);
      }
    };

    const handlePrevStep = () => {
      if (meditationStep > 1) {
        setMeditationStep(meditationStep - 1);
        console.log(`⬅️ ${meditationStep - 1}단계로 이동`);
      }
    };

    const handleFinishMeditation = async () => {
      // 음성 읽기 중지
      stopVoiceReading();
      
      // 4단계 데이터 저장
      const stepData = {
        verseReference: customVerse.reference || todayVerse?.reference || DEFAULT_VERSE.reference,
        verseText: customVerse.text || todayVerse?.text || DEFAULT_VERSE.text,
        prayer: meditationData.prayer,
        prayerShareType: prayerShareType
      };
      
      await completeStep(4, stepData);
      console.log('✅ 묵상 완료!');
      
      // 홈 화면의 오늘의 묵상 데이터 새로고침
      await loadTodayMeditation();
      
      // 피드 데이터 새로고침
      await loadFeedData();
      
      // 크루 데이터 새로고침 (묵상 완료 상태 반영)
      console.log('🔄 묵상 완료 후 크루 데이터 새로고침 시작');
      try {
        await loadCrewData(true); // 강제 새로고침
        console.log('✅ 크루 데이터 새로고침 완료');
      } catch (error) {
        console.error('❌ 크루 데이터 새로고침 실패:', error);
      }
      
      // 크루 멤버 나눔 상태 모달 표시
      try {
        if (isGuestMode) {
          console.log('🎭 게스트 모드: 묵상 완료 후 크루 멤버 상태 모달 준비');
          await loadCrewMemberStatuses();
          setShowCrewMemberStatusModal(true);
        } else {
          // 인증된 사용자 - 크루 가입 여부 확인
          console.log('🔄 인증된 사용자 - 크루 정보 로드 시작');
          const currentCrew = await loadUserCrew();
          
          if (currentCrew) {
            // 가입된 크루가 있으면 멤버 상태 모달 표시
            console.log('✅ 가입된 크루 발견:', currentCrew.name);
            setCurrentModalCrew(currentCrew);
            await loadCrewMemberStatuses(currentCrew);
            setShowCrewMemberStatusModal(true);
          } else {
            // 가입된 크루가 없어도 크루 멤버 상태 모달 표시 (가입 안내)
            console.log('❌ 가입된 크루가 없음 - 크루 가입 안내 모달 표시');
            setCurrentModalCrew(null);
            setCrewMemberStatuses([]); // 빈 배열로 설정
            setShowCrewMemberStatusModal(true);
          }
        }
      } catch (error) {
        console.log('크루 기능을 사용할 수 없습니다:', error);
        // 크루 기능 없이 계속 진행
      }
      
      // 묵상 완료 후 초기화 - 신규 묵상을 위해
      resetMeditationForNewSession();
      
      // 피드 데이터 새로고침 (묵상이 피드에 반영되도록)
      if (isAuthenticated && !isGuestMode) {
        console.log('🔄 묵상 완료 후 피드 데이터 새로고침');
        loadFeedData();
      }
      
      // 묵상 화면 닫기
      setShowMeditation(false);
    };


    const updateMeditationData = (field: string, value: string) => {
      setMeditationData(prev => ({ ...prev, [field]: value }));
    };

  // 중복 제거됨 - 전역 상태 사용

  // 중복 제거됨 - 전역 함수 사용

  // 중복 제거됨 - 전역 상태 사용

  // 5-1단계 테스트: 전역 이벤트 리스너 완전 비활성화
  // useEffect(() => {
  //   if (!showMeditation) return;
  //   const handleClickCapture = (e: Event) => {
  //     if (isEditingText) {
  //       e.stopPropagation();
  //     }
  //   };
  //   const handleTouchCapture = (e: Event) => {
  //     if (isEditingText) {
  //       e.stopPropagation();
  //     }
  //   };
  //   const handlePointerCapture = (e: Event) => {
  //     if (isEditingText) {
  //       e.stopPropagation();
  //     }
  //   };
  //   const root = document;
  //   root.addEventListener('click', handleClickCapture, true);
  //   root.addEventListener('touchstart', handleTouchCapture, true);
  //   root.addEventListener('touchend', handleTouchCapture, true);
  //   root.addEventListener('touchmove', handleTouchCapture, true);
  //   root.addEventListener('pointerdown', handlePointerCapture, true);
  //   root.addEventListener('pointerup', handlePointerCapture, true);
  //   return () => {
  //     root.removeEventListener('click', handleClickCapture, true);
  //     root.removeEventListener('touchstart', handleTouchCapture, true);
  //     root.removeEventListener('touchend', handleTouchCapture, true);
  //     root.removeEventListener('touchmove', handleTouchCapture, true);
  //     root.removeEventListener('pointerdown', handlePointerCapture, true);
  //     root.removeEventListener('pointerup', handlePointerCapture, true);
  //   };
  // }, [isEditingText, showMeditation]);

    if (!showMeditation) return null;

    return (
      <div className="fixed inset-0 z-50 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50">
        {/* 헤더 - 메인페이지와 동일한 스타일 */}
        <div className="bg-white/70 backdrop-blur-md shadow-sm border-b border-orange-100/50 sticky top-0 z-10">
          <div className="max-w-md mx-auto px-4 py-4 sm:py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 sm:gap-4">
                {/* e2b 로고 - 메인과 동일 */}
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-slate-700 to-slate-900 rounded-2xl flex items-center justify-center shadow-lg p-1.5">
                  <img 
                    src="/images/logo.png" 
                    alt="e2b 로고" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="block">
                  <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-slate-800">
                    오늘의 말씀
                  </h1>
                  <p className="text-xs sm:text-sm text-slate-600">eat bible everyday</p>
                </div>
              </div>
              <button
                onClick={() => setShowMeditation(false)}
                className="w-10 h-10 bg-slate-100 hover:bg-slate-200 rounded-xl flex items-center justify-center transition-colors"
              >
                <X size={18} className="text-slate-600" />
              </button>
            </div>
          </div>
        </div>

        {/* 메인 콘텐츠 */}
        <div className="flex-1 overflow-auto">
          <div className="max-w-md mx-auto p-4 pb-40 w-full min-h-full flex flex-col justify-center">
            {/* 진행 단계 표시 - 클릭 가능한 진행 바 */}
            <div className="mb-4">
              <div className="flex items-center gap-1 mb-2">
                {[1, 2, 3, 4].map((step) => (
                  <button
                    key={step}
                    onClick={() => setMeditationStep(step)}
                    className={`flex-1 h-3 rounded-full transition-all duration-500 cursor-pointer hover:scale-105 ${
                      step <= meditationStep
                        ? 'bg-gradient-to-r from-rose-400 to-pink-500 hover:from-rose-500 hover:to-pink-600'
                        : 'bg-slate-200 hover:bg-slate-300'
                    }`}
                    title={`${step}단계로 이동: ${
                      step === 1 ? "말씀 선포하기" :
                      step === 2 ? "하나님의 뜻 묵상하기" :
                      step === 3 ? "변화 성장 묵상하기" : "기도하기"
                    }`}
                  />
                ))}
              </div>
              <div className="text-center">
                <p className="text-xs text-slate-600">
                  {meditationStep === 1 && "말씀 선포하기"}
                  {meditationStep === 2 && "하나님의 뜻 묵상하기"}
                  {meditationStep === 3 && "변화 성장 묵상하기"}
                  {meditationStep === 4 && "기도하기"}
                </p>
              </div>
            </div>

            {/* 상단 네비게이션 버튼 */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={meditationStep === 1 ? handleGoHome : handlePrevStep}
                className={`flex-1 py-3 px-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
                  meditationStep === 1
                    ? 'bg-blue-500 hover:bg-blue-600 text-white'
                    : 'bg-slate-200 hover:bg-slate-300 text-slate-700'
                }`}
              >
                {meditationStep === 1 ? (
                  <>
                    <Home size={16} />
                    홈으로
                  </>
                ) : (
                  <>
                    <ArrowLeft size={16} />
                    이전
                  </>
                )}
              </button>
              
              {meditationStep < 4 ? (
                <button
                  onClick={handleNextStep}
                  className="flex-1 py-3 px-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all bg-gradient-to-r from-rose-400 to-pink-500 hover:from-rose-500 hover:to-pink-600 text-white"
                >
                  다음
                  <ArrowRight size={16} />
                </button>
              ) : (
                <button
                  onClick={handleFinishMeditation}
                  className="flex-1 py-3 px-4 bg-gradient-to-r from-green-400 to-green-500 hover:from-green-500 hover:to-green-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-all"
                >
                  <CheckCircle size={16} />
                  완료
                </button>
              )}
            </div>


            {/* 말씀 카드 (1단계에서만 표시) */}
            {meditationStep === 1 && (
              <div className="bg-white rounded-3xl shadow-xl p-6 mb-4 border border-orange-100">
                <div className="text-center">
                  {!isEditingVerse ? (
                    <>
                      <div className="relative">
                        <p className="text-base text-slate-600 font-semibold mb-3">
                          {customVerse.reference || todayVerse?.reference || DEFAULT_VERSE.reference}
                        </p>
                        
                        {/* 왼쪽 정렬 버튼들 (음성 읽기, 설정) */}
                        <div className="absolute -top-1 -left-1 flex gap-1">
                          {/* 음성 읽기 버튼 */}
                          <button
                            onClick={handleVoiceReading}
                            className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors shadow-md ${
                              isVoiceReading
                                ? 'bg-red-500 hover:bg-red-600 text-white'
                                : 'bg-green-400 hover:bg-green-500 text-white'
                            }`}
                            title={isVoiceReading ? '듣기 중지' : '듣기'}
                          >
                            {isVoiceReading ? <Pause size={14} /> : <Play size={14} />}
                          </button>
                          
                          {/* 설정 버튼 */}
                          <button
                            onClick={() => setShowVoiceSettings(!showVoiceSettings)}
                            className="w-8 h-8 bg-slate-400 hover:bg-slate-500 text-white rounded-full flex items-center justify-center transition-colors shadow-md"
                            title="음성 설정"
                            disabled={isVoiceReading}
                          >
                            <Settings size={14} />
                          </button>
                        </div>
                        
                        {/* 오른쪽 정렬 버튼들 (새로고침, 편집) */}
                        <div className="absolute -top-1 -right-1 flex gap-1">
                          {/* 새 말씀 가져오기 버튼 */}
                          <button
                            onClick={getNewVerse}
                            className="w-8 h-8 bg-blue-400 hover:bg-blue-500 text-white rounded-full flex items-center justify-center transition-colors shadow-md"
                            title="새로운 말씀 가져오기"
                          >
                            <RotateCcw size={14} />
                          </button>
                          
                          {/* 말씀 편집 버튼 */}
                          <button
                            onClick={openVerseEditModal}
                            className="w-8 h-8 bg-slate-400 hover:bg-slate-500 text-white rounded-full flex items-center justify-center transition-colors shadow-md"
                            title="말씀 기록하기"
                          >
                            <Edit3 size={14} />
                          </button>
                        </div>
                      </div>
                      <p className="text-lg text-slate-800 leading-relaxed font-medium mb-4">
                        {customVerse.text || todayVerse?.text || DEFAULT_VERSE.text}
                      </p>
                      
                      {/* 진행 상황 표시 */}
                      {isVoiceReading && (
                        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                          <div className="text-center">
                            <p className="text-sm font-semibold text-blue-800">
                              {voiceReadingCount}/{maxVoiceReadingCount}번 읽기 중
                            </p>
                            <div className="w-full bg-blue-200 rounded-full h-2 mt-2">
                              <div 
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${(voiceReadingCount / maxVoiceReadingCount) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* 드롭다운 설정 */}
                      {showVoiceSettings && !isVoiceReading && (
                        <div className="mb-4 space-y-3">
                          {/* 스피드 조절 */}
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-slate-700">읽기 속도</span>
                              <span className="text-sm text-slate-500">{voiceReadingSpeed.toFixed(1)}x</span>
                            </div>
                            <input
                              type="range"
                              min="0.5"
                              max="2.0"
                              step="0.1"
                              value={voiceReadingSpeed}
                              onChange={(e) => setVoiceReadingSpeed(parseFloat(e.target.value))}
                              className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                            />
                            <div className="flex justify-between text-xs text-slate-400 mt-1">
                              <span>느림</span>
                              <span>보통</span>
                              <span>빠름</span>
                            </div>
                          </div>
                          
                          {/* 본문만 읽기 옵션 */}
                          <div className="p-3 bg-blue-50 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-slate-700">읽기 모드</span>
                              <button
                                onClick={() => setIsReadingVerseOnly(!isReadingVerseOnly)}
                                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                  isReadingVerseOnly 
                                    ? 'bg-blue-500 text-white' 
                                    : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                                }`}
                              >
                                {isReadingVerseOnly ? '본문만' : '전체'}
                              </button>
                            </div>
                            <p className="text-xs text-slate-500">
                              {isReadingVerseOnly ? '말씀 주소 없이 본문만 반복 읽기' : '말씀 주소와 본문을 함께 읽기'}
                            </p>
                          </div>

                          {/* 반복 횟수 설정 */}
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-slate-700">반복 횟수</span>
                              <span className="text-sm text-slate-500">{maxVoiceReadingCount}번</span>
                            </div>
                            <input
                              type="range"
                              min="10"
                              max="200"
                              step="10"
                              value={maxVoiceReadingCount}
                              onChange={(e) => setMaxVoiceReadingCount(parseInt(e.target.value))}
                              className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                            />
                            <div className="flex justify-between text-xs text-slate-400 mt-1">
                              <span>10번</span>
                              <span>100번</span>
                              <span>200번</span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* 말씀 정보 표시 */}
                      <div className="text-xs text-slate-400 mt-2">
                        {isCustomVerse() ? (
                          <span>✏️ 사용자 정의 말씀</span>
                        ) : (
                          <span>📖 오늘의 말씀 ({todayVerse?.id || 0}/100)</span>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="space-y-4">
                      <input
                        type="text"
                        defaultValue={tempVerse.reference}
                        onBlur={(e) => {
                          console.log('📖 말씀출처 완료:', e.target.value);
                          setTempVerse(prev => ({ ...prev, reference: e.target.value }));
                        }}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent text-center font-semibold"
                        placeholder="말씀 출처 (예: 요한복음 3장 16절)"
                      />
                      <textarea
                        key="verse-text-textarea"
                        defaultValue={tempVerse.text}
                        onBlur={(e) => {
                          console.log('📜 말씀내용 완료:', e.target.value.length, '자');
                          setTempVerse(prev => ({ ...prev, text: e.target.value }));
                        }}
                        autoFocus={false}
                        spellCheck={false}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        className="w-full px-3 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none text-center leading-relaxed"
                        rows={4}
                        placeholder="말씀 내용을 입력하세요"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setIsEditingVerse(false);
                            // 취소 시에는 임시 값을 버리고 기존 값 유지
                          }}
                          className="flex-1 px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-medium transition-colors"
                        >
                          취소
                        </button>
                        <button
                          onClick={() => {
                            setCustomVerse(tempVerse);
                            setIsEditingVerse(false);
                          }}
                          className="flex-1 px-4 py-2 bg-gradient-to-r from-rose-400 to-pink-500 hover:from-rose-500 hover:to-pink-600 text-white rounded-lg font-medium transition-colors"
                        >
                          저장
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 단계별 컨텐츠 */}
            {meditationStep === 1 && (
              <div className="bg-white rounded-3xl shadow-xl p-6 text-center">
                <h3 className="text-xl font-bold text-slate-800 mb-6">
                  오늘 말씀 100번 선포하기
                </h3>
                {/* 카운터 - 수평 레이아웃 */}
                <div className="mb-6">
                  {/* 카운터 숫자와 버튼을 한 줄로 배치 */}
                  <div className="flex items-center justify-center gap-6 mb-6">
                    <button
                      onClick={() => updateReadingCount(Math.max(0, readingCount - 1))}
                      className="w-16 h-16 bg-slate-200 hover:bg-slate-300 rounded-full flex items-center justify-center transition-colors shadow-lg"
                    >
                      <Minus size={24} />
                    </button>
                    
                    <div className="text-6xl font-bold text-slate-800 min-w-[120px] relative">
                      {readingCount}
                      {isSavingMeditation && (
                        <div className="absolute -top-2 -right-2">
                          <div className="w-4 h-4 bg-green-500 rounded-full animate-pulse"></div>
                        </div>
                      )}
                    </div>
                    
                    <button
                      onClick={() => updateReadingCount(readingCount + 1)}
                      className="w-16 h-16 bg-rose-400 hover:bg-rose-500 text-white rounded-full flex items-center justify-center transition-colors shadow-lg"
                    >
                      <Plus size={24} />
                    </button>
                  </div>
                  
                  <button
                    onClick={() => updateReadingCount(0)}
                    className="px-6 py-3 bg-gradient-to-r from-slate-400 to-slate-500 hover:from-slate-500 hover:to-slate-600 text-white rounded-xl font-medium transition-colors shadow-md flex items-center gap-2 mx-auto"
                  >
                    <RotateCcw size={18} />
                    초기화
                  </button>
                </div>
              </div>
            )}

            {meditationStep === 2 && (
              <div className="space-y-4">
                {/* 말씀 표시 (작은 글씨, 상단) */}
                <div className="bg-white/50 rounded-2xl p-4 border border-orange-100/50">
                  <p className="text-xs text-slate-500 text-center mb-1">
                    {customVerse.reference || todayVerse?.reference || DEFAULT_VERSE.reference}
                  </p>
                  <p className="text-sm text-slate-600 text-center leading-relaxed">
                    {customVerse.text || todayVerse?.text || DEFAULT_VERSE.text}
                  </p>
                </div>

                {/* 메인 작성 영역 (편집 가능) */}
                <div className="bg-white rounded-3xl shadow-xl p-8" onClick={(e) => e.stopPropagation()}>
                  <div className="relative">
                  <h3 className="text-lg font-bold text-slate-800 mb-6 text-center">
                    하나님이 나에게 원하시는 것은?
                  </h3>
                    <button
                      onClick={() => openMeditationInputModal('godsWill', '하나님이 나에게 원하시는 것은?', '이 말씀을 통해 하나님이 나에게 원하시는 것을 기록해보세요...')}
                      className="absolute -top-1 -right-1 w-8 h-8 bg-rose-400 hover:bg-rose-500 text-white rounded-full flex items-center justify-center transition-colors shadow-md"
                      title="내용 편집하기"
                    >
                      <Edit3 size={14} />
                    </button>
                  </div>
                  <div 
                    className="bg-rose-50 border border-rose-100 rounded-2xl p-4 text-sm text-slate-700 whitespace-pre-wrap min-h-[56px] cursor-pointer hover:bg-rose-100 transition-colors"
                    onClick={() => openMeditationInputModal('godsWill', '하나님이 나에게 원하시는 것은?', '이 말씀을 통해 하나님이 나에게 원하시는 것을 기록해보세요...')}
                  >
                    {meditationData.godsWill || '클릭해서 내용을 작성해보세요...'}
                  </div>
                </div>
              </div>
            )}

            {meditationStep === 3 && (
              <div className="space-y-4">
                {/* 말씀 표시 (작은 글씨, 상단) */}
                <div className="bg-white/50 rounded-2xl p-4 border border-orange-100/50">
                  <p className="text-xs text-slate-500 text-center mb-1">
                    {customVerse.reference || todayVerse?.reference || DEFAULT_VERSE.reference}
                  </p>
                  <p className="text-sm text-slate-600 text-center leading-relaxed">
                    {customVerse.text || todayVerse?.text || DEFAULT_VERSE.text}
                  </p>
                </div>

                {/* 메인 작성 영역 (편집 가능) */}
                <div className="bg-white rounded-3xl shadow-xl p-8" onClick={(e) => e.stopPropagation()}>
                  <div className="relative">
                  <h3 className="text-lg font-bold text-slate-800 mb-6 text-center">
                    내가 변화성장해야 하는 것은?
                  </h3>
                    <button
                      onClick={() => openMeditationInputModal('growthChange', '변화/성장해야 할 것은?', '변화/성장해야 할 것을 기록해보세요...')}
                      className="absolute -top-1 -right-1 w-8 h-8 bg-rose-400 hover:bg-rose-500 text-white rounded-full flex items-center justify-center transition-colors shadow-md"
                      title="내용 편집하기"
                    >
                      <Edit3 size={14} />
                    </button>
                  </div>
                  <div 
                    className="bg-rose-50 border border-rose-100 rounded-2xl p-4 text-sm text-slate-700 whitespace-pre-wrap min-h-[56px] cursor-pointer hover:bg-rose-100 transition-colors"
                    onClick={() => openMeditationInputModal('growthChange', '변화/성장해야 할 것은?', '변화/성장해야 할 것을 기록해보세요...')}
                  >
                    {meditationData.growthChange || '클릭해서 내용을 작성해보세요...'}
                  </div>
                </div>
              </div>
            )}

            {meditationStep === 4 && (
              <div className="space-y-4">
                {/* 말씀 표시 (작은 글씨, 상단) */}
                <div className="bg-white/50 rounded-2xl p-4 border border-orange-100/50">
                  <p className="text-xs text-slate-500 text-center mb-1">
                    {customVerse.reference || todayVerse?.reference || DEFAULT_VERSE.reference}
                  </p>
                  <p className="text-sm text-slate-600 text-center leading-relaxed">
                    {customVerse.text || todayVerse?.text || DEFAULT_VERSE.text}
                  </p>
                </div>

                {/* 기도제목 공유 설정 */}
                <div className="bg-white rounded-3xl shadow-xl p-6 border border-orange-100">
                  <h4 className="text-lg font-semibold text-slate-700 mb-4 text-center">기도제목 공유 설정</h4>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPrayerShareType('public')}
                      className={`flex-1 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                        prayerShareType === 'public'
                          ? 'bg-blue-500 text-white shadow-md'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      공개
                    </button>
                    <button
                      onClick={() => setPrayerShareType('private')}
                      className={`flex-1 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                        prayerShareType === 'private'
                          ? 'bg-slate-500 text-white shadow-md'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      비공개
                    </button>
                    <button
                      onClick={() => setPrayerShareType('request')}
                      className={`flex-1 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                        prayerShareType === 'request'
                          ? 'bg-rose-500 text-white shadow-md'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      중보요청
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    {prayerShareType === 'private' && '기도제목이 나에게만 기록됩니다'}
                    {prayerShareType === 'public' && '동일 크루 멤버에게만 공유됩니다'}
                    {prayerShareType === 'request' && '모든 교회 멤버에게 공유됩니다'}
                  </p>
                </div>

                {/* 작성된 기도제목 표시 (편집 가능) */}
                <div className="bg-white rounded-3xl shadow-xl p-6 border border-orange-100">
                  <div className="relative">
                  <h3 className="text-lg font-bold text-slate-800 mb-4 text-center">
                    오늘 말씀을 통해 기도합시다
                  </h3>
                    <button
                      onClick={() => openMeditationInputModal('prayer', '기도할 내용은?', '클릭해서 하나님께 기도를 올려드리세요')}
                      className="absolute -top-1 -right-1 w-8 h-8 bg-rose-400 hover:bg-rose-500 text-white rounded-full flex items-center justify-center transition-colors shadow-md"
                      title="기도제목 편집하기"
                    >
                      <Edit3 size={14} />
                    </button>
                  </div>
                  <div 
                    className="bg-slate-50 rounded-2xl p-4 min-h-[120px] flex items-center justify-center cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => openMeditationInputModal('prayer', '기도할 내용은?', '클릭해서 하나님께 기도를 올려드리세요')}
                  >
                    {meditationData.prayer ? (
                      <p className="text-slate-700 leading-relaxed whitespace-pre-wrap text-center">
                        {meditationData.prayer}
                      </p>
                    ) : (
                      <p className="text-slate-400 text-center">
                        클릭해서 하나님께 기도를 올려드리세요
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 하단 나눔 버튼 숨김 처리 */}
        {/* {meditationStep > 1 && (
          <div className={`fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-orange-100/50 shadow-lg z-[60] ${showSimpleInput ? 'pointer-events-none' : ''}` }>
            <div className="max-w-md mx-auto px-4 py-4 safe-area-inset-bottom">
                <button
                  type="button"
                  onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); openShareForCurrentStep(); }}
                  onClick={() => openShareForCurrentStep()}
                className="w-full py-4 px-6 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all bg-rose-500 hover:bg-rose-600 text-white min-h-[56px]"
                  aria-label={meditationStep === 4 ? "작성" : "나눔"}
                >
{meditationStep === 4 ? "작성" : "나눔"}
                  </button>
                </div>
            </div>
        )} */}
      </div>
    );
  };

  // 크루 데이터
  const crews = [
    { id: 'men1', name: '남성 1크루', members: 12, color: 'from-slate-600 to-slate-800' },
    { id: 'men2', name: '남성 2크루', members: 15, color: 'from-slate-700 to-slate-900' },
    { id: 'women1', name: '여성 1크루', members: 18, color: 'from-rose-400 to-pink-600' },
    { id: 'women2', name: '여성 2크루', members: 14, color: 'from-pink-400 to-rose-600' },
    { id: 'women3', name: '여성 3크루', members: 16, color: 'from-pink-500 to-rose-700' },
    { id: 'women4', name: '여성 4크루', members: 13, color: 'from-rose-500 to-pink-700' },
    { id: 'youth1', name: '청년 1크루', members: 20, color: 'from-slate-500 to-rose-500' }
  ];


  // 피드 데이터 처리 - 게스트 모드일 경우 샘플 데이터 사용
  const displayFeedData = isGuestMode ? SAMPLE_FEED_DATA : (feedData.length > 0 ? feedData : []);

  // 순위 데이터
  const rankings = [
    { rank: 1, name: '이소망', crew: '청년 1크루', score: 95, avatar: '🌟' },
    { rank: 2, name: '김성도', crew: '남성 1크루', score: 87, avatar: '⭐' },
    { rank: 3, name: '박믿음', crew: '여성 2크루', score: 83, avatar: '✨' },
    { rank: 4, name: '최은혜', crew: '여성 1크루', score: 78, avatar: '💫' },
    { rank: 5, name: '정평안', crew: '남성 2크루', score: 72, avatar: '🌙' }
  ];

  const handleLike = (postId) => {
    setDevotionLikes(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };


  // 홈 화면
  const HomeScreen = () => (
    <div className="space-y-8 pb-32">
      {/* 오늘의 말씀 - 메인 콘텐츠 */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl shadow-xl overflow-hidden border-2 border-orange-200/80">
        {/* 노트 헤더 */}
        <div className="bg-white/60 backdrop-blur-sm px-6 py-4 border-b border-orange-200/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-slate-700 to-slate-900 rounded-2xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-lg" style={{ fontFamily: 'cursive' }}>e</span>
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-800">오늘의 말씀</h3>
                <p className="text-slate-600 text-xs font-medium">eat bible everyday</p>
              </div>
            </div>
            <button
              onClick={openMeditationHistory}
              className="p-2 hover:bg-white/50 rounded-xl transition-colors"
              title="묵상 기록 보기"
            >
              <BookOpen className="text-slate-600" size={24} />
            </button>
          </div>
        </div>
        
        {/* 노트 내용 */}
        <div className="p-7 space-y-5">
          {isLoadingTodayMeditation ? (
            /* 로딩 상태 */
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl border border-orange-200/50 shadow-sm">
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-500"></div>
                <span className="ml-3 text-slate-600">오늘의 묵상을 확인하는 중...</span>
              </div>
            </div>
          ) : todayMeditationData ? (
            /* 오늘 묵상한 데이터가 있는 경우 */
          <div className="bg-white/80 backdrop-blur-sm p-4 rounded-2xl border border-orange-200/50 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 bg-green-500 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xs">✓</span>
              </div>
                <p className="text-sm text-slate-600 font-semibold">{todayMeditationData.verseReference}</p>
                <span className="text-xs text-green-600 font-medium px-2 py-1 bg-green-100 rounded-full">완료</span>
            </div>
              <p className="text-slate-800 leading-relaxed font-semibold text-base text-center mb-4">
                {todayMeditationData.verseText}
              </p>
              {/* 묵상 완료 시간 표시 */}
              <div className="text-center">
                <p className="text-xs text-slate-500">
                  {new Date(todayMeditationData.updatedAt).toLocaleString('ko-KR', {
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })} 완료
                </p>
          </div>
            </div>
          ) : (
            /* 오늘 묵상한 데이터가 없는 경우 */
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl border border-orange-200/50 shadow-sm">
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-gradient-to-br from-rose-400 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <BookOpen className="text-white" size={32} />
                </div>
                <h4 className="text-xl font-bold text-slate-800 mb-4 leading-tight">오늘 하루도 이투비로 승리하세요</h4>
                <div className="space-y-2">
                  <p className="text-base text-slate-700 font-medium">말씀 먹는 운동 이투비와 함께</p>
                  <p className="text-sm text-slate-600 leading-relaxed">주님 안에 머무르는 소중한 시간을 가져보세요</p>
                </div>
              </div>
            </div>
          )}
          
          <div className="mt-6">
            {todayMeditationData ? (
              // 완료된 묵상이 있는 경우: 두 개의 버튼 표시
              <div className="flex gap-3">
                <button 
                  onClick={continueMeditation}
                  className="flex-1 bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 hover:from-blue-500 hover:via-blue-600 hover:to-blue-700 text-white font-bold py-5 px-6 rounded-2xl shadow-xl transform hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-3 border border-blue-200/20"
                >
                  <Edit3 size={22} />
                  <span className="text-base">이어서</span>
                </button>
                <button 
                  onClick={startNewMeditation}
                  className="flex-1 bg-gradient-to-r from-pink-400 via-rose-400 to-pink-500 hover:from-pink-500 hover:via-rose-500 hover:to-pink-600 text-white font-bold py-5 px-6 rounded-2xl shadow-xl transform hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-3 border border-pink-200/20"
                >
                  <Edit3 size={22} />
                  <span className="text-base">새묵상</span>
                </button>
              </div>
            ) : (
              // 완료된 묵상이 없는 경우: 기존 버튼 하나만 표시
              <button 
                onClick={startNewMeditation}
                className="w-full bg-gradient-to-r from-pink-400 via-rose-400 to-pink-500 hover:from-pink-500 hover:via-rose-500 hover:to-pink-600 text-white font-bold py-5 px-6 rounded-2xl shadow-xl transform hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-3 border border-pink-200/20"
              >
                <Edit3 size={22} />
                <span className="text-lg">e2b 시작</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 간단 입력 모달 - 5-2단계: 배경 클릭 이벤트 제거, 5-3단계: stopPropagation 제거 */}
      {showSimpleInput && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-[100] p-4 pt-8">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-slate-800">{simpleTitle}</h3>
              <button onClick={() => simpleCanClose && setShowSimpleInput(false)} className="text-slate-500 hover:text-slate-700">
                <X size={24} />
              </button>
            </div>
            {/* 큰 사이즈 네이티브 입력 필드 - Controlled Input */}
            <div className="flex-1 overflow-hidden">
            <textarea
                ref={simpleInputRef}
                placeholder={simplePlaceholder || "여기에 텍스트를 입력해주세요..."}
                className="w-full h-full min-h-[300px] p-6 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-rose-300 resize-none text-lg leading-relaxed bg-slate-50/50 focus:bg-white transition-colors"
                value={simpleValue}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setSimpleValue(newValue);
                  console.log('📱 [입력창] 텍스트 변경:', newValue.length, '자');
                }}
                onFocus={() => console.log('📱 [입력창] 포커스 획득')}
                onBlur={() => console.log('📱 [입력창] 포커스 해제')}
                style={{ 
                  minHeight: '300px',
                  maxHeight: 'calc(85vh - 200px)',
                  fontSize: '18px',
                  lineHeight: '1.6'
                }}
              />
            </div>
            {/* 큰 사이즈 버튼 */}
            <div className="mt-6 flex gap-3">
              <button 
                onClick={() => {
                  setShowSimpleInput(false);
                  setSimpleValue(''); // Reset value
                }} 
                className="flex-1 py-4 px-6 rounded-2xl bg-slate-100 text-slate-700 font-bold text-lg hover:bg-slate-200 transition-colors"
              >
                취소
              </button>
              <button 
                onClick={handleSimpleSave} 
                className="flex-1 py-4 px-6 rounded-2xl bg-rose-500 text-white font-bold text-lg hover:bg-rose-600 transition-colors shadow-lg hover:shadow-xl"
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}


      {/* 크루 가입 모달 */}
      {showJoinCrewModal && selectedCrewForJoin && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[90] p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-slate-800">크루 가입</h3>
              <button 
                onClick={() => setShowJoinCrewModal(false)} 
                className="text-slate-500 hover:text-slate-700 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-rose-400 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users size={32} className="text-white" />
              </div>
              <h4 className="text-lg font-bold text-slate-800 mb-2">{selectedCrewForJoin.name}</h4>
              <p className="text-slate-600 text-sm">{selectedCrewForJoin.members || 0}명이 참여 중</p>
            </div>
            
            <div className="bg-slate-50 rounded-xl p-4 mb-6">
              <p className="text-slate-700 text-sm leading-relaxed">
                이 크루에 가입하시겠습니까? 가입하시면 크루 멤버들과 함께 묵상을 나누고 
                신앙 생활을 함께할 수 있습니다.
              </p>
            </div>
            
            <div className="flex gap-3">
              <button 
                onClick={() => setShowJoinCrewModal(false)}
                className="flex-1 py-3 px-4 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl font-semibold transition-colors"
              >
                취소
              </button>
              <button 
                onClick={() => handleJoinCrew(selectedCrewForJoin)}
                disabled={isJoiningCrew}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-xl font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isJoiningCrew ? '신청 중...' : '📝 가입 신청'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 기도 타이머 바로가기 - 실제 데이터 반영 */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-100 border border-blue-200/50 p-5 rounded-3xl cursor-pointer transform transition-all hover:scale-[1.02] hover:shadow-xl shadow-lg relative overflow-hidden"
           onClick={() => setActiveTab('prayer')}>
        {/* 배경 장식 */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-2 right-2 w-20 h-20 border border-blue-300 rounded-full"></div>
          <div className="absolute bottom-4 left-4 w-12 h-12 border border-blue-300 rounded-full"></div>
        </div>
        
        <div className="relative z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Clock size={22} className="text-white" />
              </div>
              <div>
                <span className="font-bold text-lg text-slate-800">오늘의 기도시간</span>
                <p className="text-slate-600 text-sm font-medium">
                  {prayerStats ? (
                    <>
                      오늘: {formatTime(prayerStats.totalDuration)} | 이번주: {formatTime(prayerStats.thisWeekTotal)}
                    </>
                  ) : (
                    '기도 기록을 불러오는 중...'
                  )}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg transform hover:scale-110 transition-transform">
                <span className="text-white font-bold text-sm">→</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 공지사항 - 타이틀만 표시, 클릭으로 모달 */}
      <div 
        className="relative bg-gradient-to-br from-slate-100 to-slate-200 border border-slate-300/50 p-5 rounded-3xl overflow-hidden shadow-lg cursor-pointer transform transition-all hover:scale-[1.02] hover:shadow-xl"
        onClick={() => setShowNoticeModal(true)}
      >
        {/* 배경 패턴 */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-4 right-4 w-24 h-24 border-2 border-slate-400 rounded-full"></div>
          <div className="absolute bottom-6 left-6 w-16 h-16 border border-slate-400 rounded-full"></div>
        </div>
        
        <div className="relative z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-slate-600 to-slate-700 rounded-2xl flex items-center justify-center shadow-lg">
                <Bell size={22} className="text-white" />
              </div>
              <div>
                <span className="font-bold text-lg text-slate-800">e2b 공지</span>
                <p className="text-slate-600 text-sm font-medium">세부 내용은 클릭해서 확인해 주세요</p>
              </div>
            </div>
            <div className="text-right">
              <div className="w-10 h-10 bg-gradient-to-br from-slate-500 to-slate-600 rounded-2xl flex items-center justify-center shadow-lg transform hover:scale-110 transition-transform">
                <span className="text-white font-bold text-sm">→</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // 크루 선택 화면 - 새로운 디자인
  const CrewScreen = () => (
    <div className="space-y-6 pb-24">
      {/* 새 크루 만들기 버튼 */}
      <div className="mb-6">
        <button
          onClick={() => setShowCreateCrewModal(true)}
          className="w-full py-4 px-6 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-2xl font-bold text-lg transition-all transform hover:scale-[1.02] shadow-lg flex items-center justify-center gap-3"
        >
          <Plus size={24} />
          새 크루 만들기
        </button>
      </div>
      

      {/* 로딩 상태 - 스켈레톤 UI */}
      {isLoadingCrews && availableCrews.length === 0 ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="rounded-xl p-3 bg-white border border-slate-100 shadow-sm">
              <div className="flex items-center gap-3">
                {/* 크루 아이콘 스켈레톤 */}
                <div className="w-12 h-12 rounded-lg bg-slate-200 animate-pulse"></div>
                
                {/* 크루 정보 스켈레톤 */}
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="h-5 w-32 bg-slate-200 rounded animate-pulse"></div>
                    <div className="h-4 w-12 bg-slate-200 rounded animate-pulse"></div>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="h-3 w-20 bg-slate-200 rounded animate-pulse"></div>
                      <div className="h-3 w-8 bg-slate-200 rounded animate-pulse"></div>
                    </div>
                    <div className="h-2 w-full bg-slate-100 rounded-full">
                      <div className="h-2 w-1/3 bg-slate-200 rounded-full animate-pulse"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <div className="text-center py-4">
            <p className="text-slate-500 text-sm">크루 목록을 불러오는 중...</p>
          </div>
        </div>
      ) : (
        /* 크루 목록 - 내가 가입한 크루만 표시 */
        <div className="space-y-2">
          {(() => {
            const myCrewsList = availableCrews.filter(crew => {
              // 내가 가입한 크루만 필터링
              const isMyCrewMember = myCrews.some(myCrew => myCrew.id === crew.id);
              return isMyCrewMember;
            });
            
            // 가입한 크루가 없을 때 안내 메시지
            if (myCrewsList.length === 0) {
              return (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users size={32} className="text-slate-400" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-2">가입한 크루가 없습니다</h3>
                  <p className="text-slate-600 mb-4">새 크루를 만들거나 기존 크루에 가입해보세요</p>
                  <button
                    onClick={() => setShowCreateCrewModal(true)}
                    className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl font-medium hover:from-blue-600 hover:to-indigo-600 transition-all"
                  >
                    새 크루 만들기
                  </button>
                </div>
              );
            }
            
            return myCrewsList.map((crew, index) => {
              // 실제 묵상 완료 데이터 기반 참여빈도 계산
              const participationRate = calculateParticipationRate(crew.id);
              const memberCount = crew.memberCount || 0;
              
              // 현재 사용자가 이 크루의 멤버인지 확인 (다중 크루 지원)
              const isMyCrewMember = myCrews.some(myCrew => myCrew.id === crew.id);
              
              // 현재 사용자가 이 크루의 방장인지 확인
              const isCrewLeader = user?.id === crew.leaderId;
              
              console.log('👥 크루 멤버십 확인:', { 
                crewId: crew.id, 
                crewName: crew.name, 
                myCrewsCount: myCrews.length,
                myCrewsIds: myCrews.map(c => c.id),
                isMyCrewMember,
                isCrewLeader,
                myCrewType: typeof myCrew,
                myCrewObject: myCrew
              });
              
              return (
            <div
              key={crew.id}
              className={`rounded-xl p-3 shadow-sm border transition-all cursor-pointer relative ${
                isMyCrewMember 
                  ? 'bg-gradient-to-br from-red-50 to-rose-50 border-red-300 hover:shadow-lg ring-2 ring-red-100' 
                  : 'bg-white border-slate-100 hover:shadow-md'
              }`}
              onClick={() => openCrewDetail(crew)}
            >
              <div className="flex items-center gap-3">
                {/* 크루 이미지/아이콘 */}
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-white text-base font-bold shadow-sm relative ${
                  isMyCrewMember 
                    ? 'bg-gradient-to-br from-red-500 to-rose-600' 
                    : 'bg-gradient-to-br from-slate-600 to-slate-800'
                }`}>
                  {crew.name.charAt(0)}
                  {isMyCrewMember && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                      <CheckCircle size={12} className="text-white" />
                </div>
                  )}
              </div>
                
                {/* 크루 정보 */}
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-lg text-slate-800">{crew.name}</h3>
                      <span className="text-slate-500 text-sm">({memberCount}명)</span>
                    </div>
                    {/* 방장 표기 */}
                    {isCrewLeader && (
                      <span className="px-2 py-0.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold rounded-full shadow-sm flex items-center gap-1">
                        👑 방장
                      </span>
                    )}
                  </div>
                  
                  {/* 참여율 진행바 */}
                  <div className="mb-1">
                    <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                      <span>구성원 참여율</span>
                      <span className={`font-semibold ${
                        isMyCrewMember ? 'text-red-600' : 'text-green-600'
                      }`}>{participationRate}%</span>
          </div>
                    <div className="w-full bg-slate-100 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${
                          isMyCrewMember 
                            ? 'bg-gradient-to-r from-red-400 to-red-500' 
                            : 'bg-gradient-to-r from-green-400 to-green-500'
                        }`}
                        style={{ width: `${participationRate}%` }}
                      />
            </div>
          </div>
                </div>
      </div>
      
            </div>
          );
            });
          })()}
        </div>
      )}

    </div>
  );

  // 피드 화면
  const FeedScreen = () => (
    <div className="space-y-6">
      {/* 로딩 상태 */}
      {isLoadingFeed && feedData.length === 0 && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500"></div>
          <span className="ml-3 text-slate-600 text-lg">피드를 불러오는 중...</span>
        </div>
      )}

      {/* 피드 데이터 표시 */}
      {displayFeedData.map(post => (
        <div 
          key={post.id} 
          className="bg-gradient-to-br from-white to-orange-50/30 rounded-3xl shadow-xl p-6 border border-orange-100/50 cursor-pointer transition-all hover:shadow-2xl hover:scale-[1.02] active:scale-[0.98]"
          onClick={() => openFeedDetail(post)}
        >
          <div className="flex items-center gap-3 mb-4">
            {/* 사용자 프로필 사진 또는 이니셜 */}
            {getAvatarUrl(post.user?.avatar) ? (
              <img 
                src={getAvatarUrl(post.user?.avatar)} 
                alt={post.user.name || 'User'} 
                className="w-12 h-12 rounded-2xl object-cover shadow-lg"
                onError={(e) => {
                  console.error('Avatar image failed to load:', e.currentTarget.src);
                  // 이미지 로드 실패 시 기본 아이콘으로 대체
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <div className="w-12 h-12 bg-gradient-to-br from-slate-600 to-slate-800 rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-lg">
                {post.user?.name?.[0] || 'U'}
              </div>
            )}
            <div>
              <p className="font-bold text-slate-800">{post.user?.name || 'Unknown'}</p>
              <p className="text-sm text-slate-600">
                {post.createdAt ? (() => {
                  const postTime = new Date(post.createdAt);
                  const now = new Date();
                  const diffMs = now.getTime() - postTime.getTime();
                  const diffMinutes = Math.floor(diffMs / (1000 * 60));
                  const diffHours = Math.floor(diffMinutes / 60);
                  const diffDays = Math.floor(diffHours / 24);

                  if (diffMinutes < 1) return '방금 전';
                  if (diffMinutes < 60) return `${diffMinutes}분 전`;
                  if (diffHours < 24) return `${diffHours}시간 전`;
                  if (diffDays === 1) return '어제';
                  if (diffDays < 7) return `${diffDays}일 전`;
                  // 일주일 이상: KST로 고정 포맷
                  return formatDateTimeKST(postTime, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                })() : (post.time || '방금 전')}
              </p>
            </div>
          </div>
          
          {/* 말씀 표시 */}
          {(post.verse || post.verseText || post.verseReference) && (
            <div className="bg-gradient-to-r from-rose-50 to-orange-50 p-4 rounded-2xl mb-4 border-l-4 border-rose-300">
              <p className="text-sm font-semibold text-rose-600 mb-1">{post.verseReference || '오늘의 말씀'}</p>
              <p className="text-slate-700 font-medium italic">{post.verse || post.verseText}</p>
            </div>
          )}
          
          {/* 간단한 묵상 요약 */}
          <div className="bg-white/80 backdrop-blur-sm p-4 rounded-2xl mb-4 border border-orange-200/50">
            {(post.godsWill || post.growthChange || (post.prayer && post.prayerShareType !== 'private')) ? (
              <div>
                <p className="text-slate-600 text-sm line-clamp-2 mb-3">
                  {post.godsWill ? `💭 ${post.godsWill.substring(0, 60)}${post.godsWill.length > 60 ? '...' : ''}` : 
                   post.growthChange ? `🌱 ${post.growthChange.substring(0, 60)}${post.growthChange.length > 60 ? '...' : ''}` :
                   (post.prayer && post.prayerShareType !== 'private') ? `🙏 ${post.prayer.substring(0, 60)}${post.prayer.length > 60 ? '...' : ''}` : ''}
                </p>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    openFeedDetailModal(post);
                  }}
                  className="text-rose-500 hover:text-rose-600 text-sm font-medium flex items-center gap-1"
                >
                  자세히 보기 <ChevronRight size={16} />
                </button>
              </div>
            ) : (
              <p className="text-slate-500 italic text-sm">묵상을 진행 중입니다...</p>
            )}
          </div>
          
          {/* 액션 버튼들 */}
          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center gap-4">
            <button 
              className={`flex items-center gap-2 transition-all transform hover:scale-110 ${devotionLikes[post.id] ? 'text-rose-500' : 'text-slate-500 hover:text-rose-500'}`}
              onClick={(e) => {
                e.stopPropagation();
                  toggleLike(post.id!);
              }}
            >
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${devotionLikes[post.id] ? 'bg-rose-100' : 'bg-slate-100'}`}>
                <Heart size={16} fill={devotionLikes[post.id] ? 'currentColor' : 'none'} />
              </div>
              <span className="font-semibold">{(post.likes || 0) + (devotionLikes[post.id] ? 1 : 0)}</span>
            </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  openFeedDetailModal(post);
                }}
                className="flex items-center gap-2 text-slate-500 hover:text-blue-500 transition-colors"
              >
                <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center">
                  <MessageCircle size={16} />
                </div>
                <span className="text-sm font-medium">상세보기</span>
              </button>
            </div>
            <button 
              data-share-button
              onClick={(e) => {
                e.stopPropagation();
                shareToKakao(post);
              }}
              className="flex items-center gap-2 text-slate-500 hover:text-yellow-500 transition-colors"
            >
              <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center">
                <Share2 size={16} />
              </div>
              <span className="text-sm font-medium">공유</span>
            </button>
          </div>
        </div>
      ))}

      {/* 더보기 버튼 */}
      {hasMoreFeed && displayFeedData.length > 0 && (
        <div className="flex justify-center py-6">
          <button
            onClick={loadMoreFeedData}
            disabled={isLoadingMoreFeed}
            className={`px-8 py-4 rounded-2xl font-semibold text-lg transition-all transform hover:scale-105 shadow-lg ${
              isLoadingMoreFeed
                ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-rose-400 to-pink-500 hover:from-rose-500 hover:to-pink-600 text-white shadow-xl'
            }`}
          >
            {isLoadingMoreFeed ? (
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-500"></div>
                <span>불러오는 중...</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <ChevronRight size={20} className="rotate-90" />
                <span>더보기</span>
              </div>
            )}
          </button>
        </div>
      )}

      {/* 피드 데이터가 없을 때 */}
      {!isLoadingFeed && displayFeedData.length === 0 && (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-gradient-to-br from-slate-200 to-slate-300 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageCircle size={32} className="text-slate-500" />
          </div>
          <p className="text-slate-600 text-lg font-medium mb-2">아직 공유된 묵상이 없습니다</p>
          <p className="text-slate-500 text-sm">첫 번째 묵상을 나눠보세요!</p>
        </div>
      )}
    </div>
  );

  // 기도 타이머 화면
  const PrayerScreen = () => (
    <div className="space-y-4 sm:space-y-8">
      {/* 타이머 UI를 가장 상단으로 */}
      <div className="bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 text-white rounded-2xl sm:rounded-3xl p-4 sm:p-8 text-center relative overflow-hidden shadow-2xl">
        {/* 배경 장식 */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-4 right-4 sm:top-8 sm:right-8 w-16 h-16 sm:w-32 sm:h-32 border border-white rounded-full"></div>
          <div className="absolute bottom-4 left-4 sm:bottom-8 sm:left-8 w-12 h-12 sm:w-24 sm:h-24 border border-white rounded-full"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 sm:w-40 sm:h-40 border border-white rounded-full"></div>
        </div>
        
        <div className="relative z-10">
          <div className="mb-4 sm:mb-8">
            <div className="text-4xl sm:text-6xl md:text-7xl font-mono font-bold mb-2 sm:mb-3 tracking-wider">{formatTime(prayerTimer.time)}</div>
            <div className="flex items-center justify-center gap-2 text-white/80">
              <Clock size={16} className="sm:w-5 sm:h-5" />
              <span className="text-sm sm:text-base">목표: {prayerTimer.target}분</span>
              <button
                onClick={() => setShowTargetSetting(!showTargetSetting)}
                className="ml-2 px-2 py-1 text-xs bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-white/90 font-medium"
              >
                변경
              </button>
              <button
                onClick={() => setShowPrayerGoalModal(true)}
                className="ml-2 px-2 py-1 text-xs bg-purple-500/20 hover:bg-purple-500/30 rounded-lg transition-colors text-white/90 font-medium flex items-center gap-1"
              >
                <Target size={12} />
                목표량
              </button>
            </div>
            
            {/* 목표 시간 설정 */}
            {showTargetSetting && (
              <div className="mt-4 p-4 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20">
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={() => setPrayerTimer(prev => ({ ...prev, target: Math.max(5, prev.target - 5) }))}
                    className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors"
                  >
                    <Minus size={16} />
                  </button>
                  <div className="text-center">
                    <div className="text-2xl font-bold">{prayerTimer.target}분</div>
                    <div className="text-xs text-white/60">목표 시간</div>
                  </div>
                  <button
                    onClick={() => setPrayerTimer(prev => ({ ...prev, target: Math.min(180, prev.target + 5) }))}
                    className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors"
                  >
                    <Plus size={16} />
                  </button>
                </div>
                <div className="mt-3 flex justify-center gap-2">
                  {[15, 30, 60, 90].map(time => (
                    <button
                      key={time}
                      onClick={() => {
                        setPrayerTimer(prev => ({ ...prev, target: time }));
                        setShowTargetSetting(false);
                      }}
                      className={`px-3 py-1 rounded-lg text-xs transition-colors ${
                        prayerTimer.target === time 
                          ? 'bg-rose-400 text-white' 
                          : 'bg-white/10 hover:bg-white/20 text-white/80'
                      }`}
                    >
                      {time}분
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="w-full bg-white/10 rounded-full h-4 sm:h-6 mb-4 sm:mb-8 backdrop-blur-sm border border-white/20">
            <div 
              className="bg-gradient-to-r from-rose-400 to-pink-500 rounded-full h-4 sm:h-6 transition-all duration-500 flex items-center justify-end pr-1 sm:pr-2"
              style={{ width: `${Math.min((prayerTimer.time / (prayerTimer.target * 60)) * 100, 100)}%` }}
            >
              {prayerTimer.time > 0 && (
                <div className="w-3 h-3 sm:w-4 sm:h-4 bg-white rounded-full shadow-lg"></div>
              )}
            </div>
          </div>
          
          <div className="flex justify-center gap-3 sm:gap-6">
            <button 
              className="bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 px-4 py-2 sm:px-8 sm:py-4 rounded-xl sm:rounded-2xl font-bold transition-all flex items-center gap-2 sm:gap-3 transform hover:scale-105 shadow-lg text-sm sm:text-base"
              onClick={() => {
                if (prayerTimer.isRunning) {
                  // 일시정지: 현재 경과 시간을 누적 시간으로 저장
                  console.log('⏸️ 기도 일시정지');
                  const elapsed = calculateElapsedTime();
                  savePrayerTimerToStorage(null, elapsed, true, prayerTimer.target);
                  setPrayerTimer(prev => ({ ...prev, isRunning: false, time: elapsed }));
                } else {
                  // 시작/재시작: 시작 시간 기록
                  console.log('▶️ 기도 시작/재시작');
                  const now = new Date().toISOString();
                  savePrayerTimerToStorage(now, prayerTimer.time, false, prayerTimer.target);
                  setPrayerTimer(prev => ({ ...prev, isRunning: true }));
                }
              }}
            >
              {prayerTimer.isRunning ? <Pause size={18} className="sm:w-6 sm:h-6" /> : <Play size={18} className="sm:w-6 sm:h-6" />}
              {prayerTimer.isRunning ? '일시정지' : '시작'}
            </button>
            <button 
              className="bg-rose-400 hover:bg-rose-500 text-white px-4 py-2 sm:px-8 sm:py-4 rounded-xl sm:rounded-2xl font-bold transition-all flex items-center gap-2 sm:gap-3 transform hover:scale-105 shadow-lg text-sm sm:text-base"
              onClick={() => {
                if (prayerTimer.time > 0) {
                  console.log('💾 기도 시간 저장 및 완료:', prayerTimer.time, '초');
                  savePrayerRecord(prayerTimer.time);
                  clearPrayerTimerFromStorage();
                  setPrayerTimer({ time: 0, isRunning: false, target: prayerTimer.target });
                  console.log('✅ 기도 기록 저장 완료 및 타이머 리셋');
                } else {
                  alert('⚠️ 저장할 기도 시간이 없습니다.');
                }
              }}
            >
              <Save size={18} className="sm:w-6 sm:h-6" />
              저장
            </button>
          </div>
          
          {prayerTimer.time >= prayerTimer.target * 60 && (
            <div className="mt-4 sm:mt-8 bg-gradient-to-r from-rose-400 to-pink-500 text-white py-3 sm:py-4 px-4 sm:px-8 rounded-xl sm:rounded-2xl font-bold text-lg sm:text-xl shadow-xl">
              🎉 목표 달성! 하나님과의 시간 완료!
            </div>
          )}
        </div>
      </div>

      {/* 기도 통계 */}
      {prayerStats && (
        <div className="bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50 rounded-2xl sm:rounded-3xl shadow-lg border border-rose-100/50 p-4 sm:p-6">
          <h3 className="text-lg sm:text-xl font-bold text-slate-800 mb-3 sm:mb-4 flex items-center gap-2">
            <Trophy size={20} className="text-rose-500" />
            기도 통계
          </h3>
          
          {/* 오늘 & 이번 주 통계 (1줄) */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {/* 오늘기도시간 */}
            <div className="bg-gradient-to-r from-rose-400 to-pink-500 text-white rounded-xl p-3 sm:p-4 text-center">
              <div className="text-xl sm:text-2xl font-bold">{formatTime(prayerStats.totalDuration)}</div>
              <div className="text-xs sm:text-sm opacity-90 mt-1">오늘 기도시간</div>
            </div>
            
            {/* 이번 주 기도시간 */}
            <div className="bg-gradient-to-r from-pink-500 to-rose-600 text-white rounded-xl p-3 sm:p-4 text-center">
              <div className="text-xl sm:text-2xl font-bold">{formatTime(prayerStats.thisWeekTotal)}</div>
              <div className="text-xs sm:text-sm opacity-90 mt-1">이번 주</div>
            </div>
          </div>
        </div>
      )}

      {/* 기도 히스토리 */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl sm:rounded-3xl shadow-lg border border-orange-100/50 p-4 sm:p-6">
        <h3 className="text-lg sm:text-xl font-bold text-slate-800 mb-3 sm:mb-4 flex items-center gap-2">
          <Clock size={20} className="text-rose-500" />
          기도 기록 {isLoadingPrayerHistory && <span className="text-xs text-slate-400">(불러오는 중...)</span>}
        </h3>
        
        {prayerHistory.length > 0 ? (
          <div className="space-y-2 sm:space-y-3 max-h-[400px] sm:max-h-[500px] overflow-y-auto pr-2">
            {(() => {
              // 오늘 날짜 (KST 기준)
              const today = getTodayString();
              
              // 오늘 기도와 과거 기도 분리
              const todayPrayers = prayerHistory.filter(record => record.date === today);
              const pastPrayers = prayerHistory.filter(record => record.date !== today);
              
              // 과거 기도를 날짜별로 그룹화하여 일별 합산
              const groupedPastPrayers = pastPrayers.reduce((groups, record) => {
                const date = record.date;
                if (!groups[date]) {
                  groups[date] = {
                    date,
                    totalDuration: 0,
                    records: []
                  };
                }
                groups[date].totalDuration += record.duration;
                groups[date].records.push(record);
                return groups;
              }, {} as Record<string, { date: string; totalDuration: number; records: any[] }>);
              
              const sortedPastPrayers = Object.values(groupedPastPrayers)
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
              
              return (
                <>
                  {/* 오늘 기도 (개별 기록) */}
                  {todayPrayers.map((record) => (
                    <button
                      key={record.id}
                      onClick={() => handlePrayerRecordInput(record.id)}
                      className="w-full p-3 sm:p-4 bg-gradient-to-r from-rose-50 to-pink-50 hover:from-rose-100 hover:to-pink-100 rounded-lg sm:rounded-xl border border-rose-200 hover:border-rose-300 transition-all cursor-pointer"
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-rose-400 to-pink-500 rounded-lg sm:rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                            <span className="text-white text-sm sm:text-base font-bold">🙏</span>
                          </div>
                          <div className="text-left">
                            <p className="font-bold text-slate-800 text-base sm:text-lg">{formatTime(record.duration)}</p>
                            <p className="text-xs sm:text-sm text-slate-600">오늘</p>
                          </div>
                        </div>
                        <div className="text-xs sm:text-sm text-slate-500">
                          {new Date(record.timestamp).toLocaleTimeString('ko-KR', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </div>
                      </div>
                      
                      {/* 기도 장소 */}
                      {record.location && (
                        <div className="flex items-center gap-2 mb-2 text-sm text-slate-600">
                          <MapPin size={14} className="text-rose-400 flex-shrink-0" />
                          <span className="truncate">{record.location}</span>
                        </div>
                      )}
                      
                      {/* 기도 내용 */}
                      {record.content && (
                        <div className="bg-rose-50/50 border border-rose-100 rounded-lg p-2 sm:p-3 text-xs sm:text-sm text-slate-700">
                          <div className="flex items-start gap-2">
                            <FileText size={14} className="text-rose-400 flex-shrink-0 mt-0.5" />
                            <p className="line-clamp-2">{record.content}</p>
                          </div>
                        </div>
                      )}
                    </button>
                  ))}
                  
                  {/* 과거 기도 (일별 합산) */}
                  {sortedPastPrayers.map((group) => (
                    <div
                      key={group.date}
                      className="w-full p-3 sm:p-4 bg-white/50 hover:bg-white/70 rounded-lg sm:rounded-xl border border-slate-100 hover:border-slate-200 transition-all"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-slate-400 to-slate-500 rounded-lg sm:rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                            <span className="text-white text-sm sm:text-base font-bold">📅</span>
                          </div>
                          <div className="text-left">
                            <p className="font-bold text-slate-800 text-base sm:text-lg">{formatTime(group.totalDuration)}</p>
                            <p className="text-xs sm:text-sm text-slate-600">
                              {new Date(group.date).toLocaleDateString('ko-KR', { 
                                month: 'long', 
                                day: 'numeric' 
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="text-xs sm:text-sm text-slate-500">
                          {group.records.length}회
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              );
            })()}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock size={24} className="text-slate-400" />
            </div>
            <p className="text-slate-500 mb-2">아직 기도 기록이 없습니다</p>
            <p className="text-sm text-slate-400">기도 타이머를 사용하여 기도 시간을 기록해보세요</p>
          </div>
        )}
      </div>
    </div>
  );

  // 순위 화면
  const RankingScreen = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="w-16 h-16 bg-gradient-to-br from-slate-700 to-slate-900 rounded-3xl flex items-center justify-center shadow-lg">
            <Trophy className="text-white" size={28} />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-slate-800">체크업 순위</h2>
            <p className="text-slate-600">eat bible everyday</p>
          </div>
        </div>
        <p className="text-slate-600">이번 주 기도·묵상 참여 순위</p>
      </div>
      
      <div className="bg-gradient-to-br from-white to-orange-50/30 rounded-3xl shadow-xl overflow-hidden border border-orange-100/50">
        {rankings.map((user, index) => (
          <div key={user.rank} className={`flex items-center p-6 ${index !== rankings.length - 1 ? 'border-b border-orange-100/50' : ''} ${user.rank <= 3 ? 'bg-gradient-to-r from-yellow-50 to-orange-50' : ''}`}>
            <div className="flex items-center gap-4 flex-1">
              <div className={`text-3xl ${user.rank <= 3 ? 'scale-125' : ''}`}>
                {user.avatar}
              </div>
              <div className={`w-12 h-12 ${user.rank === 1 ? 'bg-gradient-to-br from-yellow-400 to-orange-500' : user.rank === 2 ? 'bg-gradient-to-br from-gray-300 to-gray-500' : user.rank === 3 ? 'bg-gradient-to-br from-orange-300 to-orange-600' : 'bg-gradient-to-br from-slate-400 to-slate-600'} rounded-2xl flex items-center justify-center shadow-lg`}>
                <span className="text-white font-bold text-lg">{user.rank}</span>
              </div>
              <div className="flex-1">
                <p className="font-bold text-slate-800 text-lg">{user.name}</p>
                <p className="text-slate-600">{user.crew}</p>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-rose-400 rounded-xl flex items-center justify-center">
                    <span className="text-white font-bold text-xs">2</span>
                  </div>
                  <div>
                    <p className="text-xl font-bold text-slate-800">{user.score}</p>
                    <p className="text-sm text-slate-600">점</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );


  // 개발 모드에서 로그인 우회 (환경변수 확인)
  const isDevelopment = import.meta.env.DEV || import.meta.env.VITE_BYPASS_LOGIN === 'true';
  
  // 로그인하지 않은 경우 로그인 화면만 표시 (개발 모드나 게스트 모드가 아닐 때만)
  if (!isAuthenticated && !isDevelopment && !isGuestMode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 flex items-center justify-center">
        {/* 모바일 스케일링 제거 - 1배로 조정 */}
        <div className="scale-100 origin-center w-full">
          <div className="max-w-md w-full mx-auto px-4">
            {/* 로그인 카드 섹션 */}
            <div className="text-center mb-12 animate-fadeIn">
            <div className="bg-gradient-to-br from-white via-orange-50/30 to-amber-50/50 backdrop-blur-md rounded-3xl p-8 shadow-2xl border border-white/40 transform hover:scale-[1.02] transition-all duration-500 hover:shadow-3xl relative overflow-hidden">
              {/* 카드 배경 장식 효과 */}
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-orange-200/20 to-rose-200/20 rounded-full blur-2xl animate-pulse-slow"></div>
              <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-gradient-to-tr from-amber-200/20 to-yellow-200/20 rounded-full blur-2xl animate-pulse-slow delay-1000"></div>
              
              <div className="relative z-10">
                <h2 className="text-2xl font-bold text-slate-800 mb-6 animate-slideDown">환영합니다!</h2>
                
                {/* 로고 섹션 - 어두운 배경 유지 */}
                <div className="flex justify-center mb-6 animate-bounceIn delay-300">
                  <div className="w-24 h-24 md:w-28 md:h-28 bg-gradient-to-br from-slate-700 to-slate-900 rounded-3xl flex items-center justify-center shadow-2xl p-2 transform hover:rotate-3 hover:scale-110 transition-all duration-300 border-2 border-slate-600/50">
                    <img 
                      src="/images/logo.png" 
                      alt="e2b 로고" 
                      className="w-full h-full object-contain filter drop-shadow-lg"
                    />
                  </div>
                </div>
                
                <p className="text-slate-600 mb-6 animate-slideUp delay-500">매일 말씀과 함께하는 신앙 생활을 시작하세요</p>
                {/* 로그인 버튼 */}
                <button
                  onClick={() => {
                    setAuthMode('login');
                    setShowAuthModal(true);
                  }}
                  className="w-full flex items-center justify-center gap-3 bg-gradient-to-br from-slate-600 to-slate-800 text-white px-6 py-4 rounded-2xl hover:from-slate-700 hover:to-slate-900 transition-all duration-300 shadow-xl hover:shadow-2xl font-semibold text-lg mb-4 transform hover:scale-[1.02] hover:-translate-y-1 animate-slideUp delay-900"
                >
                  <LogIn size={20} className="animate-pulse" />
                  로그인하기
                </button>
                
                {/* 서비스 둘러보기 버튼 */}
                <button
                  onClick={() => setIsGuestMode(true)}
                  className="w-full flex items-center justify-center gap-3 bg-gradient-to-br from-amber-100 to-orange-100 text-slate-700 px-6 py-3 rounded-2xl hover:from-amber-200 hover:to-orange-200 transition-all duration-300 shadow-lg hover:shadow-xl font-semibold border border-amber-200/50 transform hover:scale-[1.02] hover:-translate-y-0.5 animate-slideUp delay-1000"
                >
                  <BookOpen size={18} />
                  서비스 둘러보기
                </button>
                
                <p className="text-xs text-slate-500 mt-3 text-center animate-fadeIn delay-1200">
                  로그인 없이 서비스를 미리 체험해보세요
                </p>
              </div>
            </div>
          </div>
        </div> {/* max-w-md 컨테이너 끝 */}
        </div> {/* 로그인 페이지 스케일링 컨테이너 끝 */}
        
        {/* 인증 모달 */}
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          initialMode={authMode}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50">
      {/* 모바일 스케일링 제거 - 1배로 조정 */}
      <div className="scale-100 origin-top-left w-full">
        {/* 게스트 모드 알림 배너 */}
        {isGuestMode && (
          <div className="bg-amber-100 border-b border-amber-200 px-4 py-2">
            <div className="max-w-md mx-auto flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BookOpen size={16} className="text-amber-700" />
                <span className="text-sm text-amber-800 font-medium">
                  서비스 둘러보기 모드
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setIsGuestMode(false);
                    setShowAuthModal(true);
                    setAuthMode('login');
                  }}
                  className="text-xs bg-amber-200 hover:bg-amber-300 text-amber-800 px-2 py-1 rounded transition-colors"
                >
                  로그인
                </button>
                <button
                  onClick={() => setIsGuestMode(false)}
                  className="text-amber-700 hover:text-amber-800"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          </div>
        )}

      {/* 헤더 */}
      <header className="bg-white/70 backdrop-blur-md shadow-sm border-b border-orange-100/50 sticky top-0 z-50">
        <div className="max-w-md mx-auto px-4 py-4 sm:py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4">
              {/* e2b 로고 - 더 크게 */}
              <div 
                className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-slate-700 to-slate-900 rounded-2xl flex items-center justify-center shadow-lg p-1.5 cursor-pointer"
                onClick={() => {
                  // 5번 연속 클릭하면 디버그 모달 열기
                  const now = Date.now();
                  const clicks = JSON.parse(localStorage.getItem('debugClicks') || '[]');
                  const recentClicks = clicks.filter((time: number) => now - time < 3000);
                  recentClicks.push(now);
                  localStorage.setItem('debugClicks', JSON.stringify(recentClicks));
                  
                  if (recentClicks.length >= 5) {
                    setShowDebugModal(true);
                    localStorage.removeItem('debugClicks');
                    addDebugInfo('디버그 모달 수동 열기');
                  }
                }}
              >
                <img 
                  src="/images/logo.png" 
                  alt="e2b 로고" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="block">
                  <h1 className="text-base sm:text-lg md:text-xl font-bold text-slate-800">
                    e2b
                  </h1>
                  <p className="text-xs text-slate-600">eat bible everyday</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={openNotificationModal}
                className="relative w-8 h-8 sm:w-10 sm:h-10 bg-slate-100 rounded-xl sm:rounded-2xl flex items-center justify-center hover:bg-slate-200 transition-colors"
                aria-label="알림"
              >
                <Bell className="text-slate-600" size={16} />
                {hasNewNotifications && notificationCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[20px] h-[18px] px-1.5 bg-rose-500 text-white text-[9px] sm:text-[10px] font-semibold leading-[18px] rounded-full text-center whitespace-nowrap">
                    신규 {notificationCount > 99 ? '99+' : notificationCount}
                  </span>
                )}
              </button>
              
              <div className="relative group">
                <Avatar 
                  src={(isGuestMode ? guestUser : user)?.avatar}
                  alt={(isGuestMode ? guestUser : user)?.name || '사용자'}
                  size="md"
                  className="cursor-pointer shadow-md"
                />
                
                {/* 드롭다운 메뉴 */}
                <div className="absolute right-0 top-10 sm:top-12 bg-white rounded-xl shadow-lg border border-slate-200 py-2 min-w-[140px] sm:min-w-[160px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="px-3 sm:px-4 py-2 border-b border-slate-100">
                    <p className="font-semibold text-slate-800 text-sm">{(isGuestMode ? guestUser : user)?.name}</p>
                    {isGuestMode && (
                      <p className="text-xs text-amber-600">게스트 모드</p>
                    )}
                  </div>
                  {!isGuestMode && (
                    <button
                      onClick={() => setShowUserSettings(true)}
                      className="w-full px-3 sm:px-4 py-2 text-left hover:bg-slate-50 transition-colors flex items-center gap-2 text-slate-700 text-sm"
                    >
                      <Settings size={14} />
                      프로필 설정
                    </button>
                  )}
                  {!isGuestMode && (user?.role === 'ADMIN' || user?.role === 'PASTOR') && (
                    <button
                      onClick={() => setActiveTab('monitor')}
                      className="w-full px-3 sm:px-4 py-2 text-left hover:bg-slate-50 transition-colors flex items-center gap-2 text-slate-700 text-sm"
                    >
                      <Target size={14} />
                      시스템 모니터링
                    </button>
                  )}
                  <button
                    onClick={() => isGuestMode ? setIsGuestMode(false) : handleLogout()}
                    className="w-full px-3 sm:px-4 py-2 text-left hover:bg-slate-50 transition-colors flex items-center gap-2 text-slate-700 text-sm"
                  >
                    <LogOut size={14} />
                    {isGuestMode ? '게스트 모드 종료' : '로그아웃'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* 메인 컨텐츠 */}
      <main className="max-w-md mx-auto px-4 sm:px-5 py-5 sm:py-7 pb-24 sm:pb-28">
        {activeTab === 'home' && <HomeScreen />}
        {activeTab === 'crews' && <CrewSystem isAuthenticated={isAuthenticated} isGuestMode={isGuestMode} user={user} onOpenCrewEditModal={openCrewEditModal} />}
        {activeTab === 'feed' && <FeedScreen />}
        {activeTab === 'prayer' && <PrayerScreen />}
        {activeTab === 'ranking' && <RankingScreen />}
        {activeTab === 'monitor' && <MonitorScreen />}
      </main>


      {/* 인증 모달 */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode={authMode}
      />

      {/* 크루 생성 모달 */}
      {showCreateCrewModal && (
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[50] flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCreateCrewModal(false);
            }
          }}
        >
          <div 
            className="bg-white rounded-2xl p-4 w-full max-w-md shadow-2xl relative transform transition-all scale-100 opacity-100 max-h-[50vh] overflow-y-auto my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-base font-bold text-slate-800">새 크루 만들기</h3>
              <button 
                onClick={() => setShowCreateCrewModal(false)} 
                className="text-slate-500 hover:text-slate-700 transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            
            <div className="space-y-2.5">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">크루 이름</label>
                <input
                  type="text"
                  value={crewName}
                  onChange={(e) => setCrewName(e.target.value)}
                  placeholder="크루 이름을 입력하세요"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent text-sm"
                  maxLength={30}
                />
                <p className="text-[10px] text-slate-400 mt-0.5">{crewName.length}/30자</p>
              </div>
              
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">크루 설명</label>
                <textarea
                  value={crewDescription}
                  onChange={(e) => setCrewDescription(e.target.value)}
                  placeholder="크루에 대한 간단한 설명을 입력하세요"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none text-sm"
                  rows={2}
                  maxLength={200}
                />
                <p className="text-[10px] text-slate-400 mt-0.5">{crewDescription.length}/200자</p>
              </div>
            </div>
            
            <div className="flex gap-2 mt-4">
              <button 
                onClick={() => setShowCreateCrewModal(false)}
                className="flex-1 py-2 px-3 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-semibold transition-colors text-sm"
              >
                취소
              </button>
              <button 
                onClick={handleCreateCrew}
                disabled={isCreatingCrew || !crewName.trim() || !crewDescription.trim()}
                className="flex-1 py-2 px-3 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {isCreatingCrew ? '생성 중...' : '크루 만들기'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 말씀 편집 모달 */}
      {showVerseEditModal && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowVerseEditModal(false);
            }
          }}
        >
          <div className="min-h-full flex items-center justify-center p-4 py-8">
            <div 
              className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl relative transform transition-all scale-100 opacity-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-slate-800">말씀 기록</h3>
                <button 
                  onClick={() => setShowVerseEditModal(false)} 
                  className="text-slate-500 hover:text-slate-700 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">성경 구절</label>
                  <input
                    type="text"
                    value={editingVerseReference}
                    onChange={(e) => setEditingVerseReference(e.target.value)}
                    placeholder="예: 요한복음 3장 16절"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent text-lg"
                    maxLength={50}
                  />
                  <p className="text-xs text-slate-400 mt-1">{editingVerseReference.length}/50자</p>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">새기는 말씀</label>
                  <textarea
                    value={editingVerseText}
                    onChange={(e) => setEditingVerseText(e.target.value)}
                    placeholder="전체 구절을 입력해주세요"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none text-lg leading-relaxed"
                    rows={6}
                    maxLength={500}
                  />
                  <p className="text-xs text-slate-400 mt-1">{editingVerseText.length}/500자</p>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button 
                  onClick={() => setShowVerseEditModal(false)}
                  className="flex-1 py-3 px-4 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl font-semibold transition-colors"
                >
                  취소
                </button>
                <button 
                  onClick={handleVerseSave}
                  disabled={!editingVerseReference.trim() || !editingVerseText.trim()}
                  className="flex-1 py-3 px-4 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-xl font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  저장
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 묵상 입력 모달 */}
      {showMeditationInputModal && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowMeditationInputModal(false);
            }
          }}
        >
          <div className="min-h-full flex items-center justify-center p-4 py-8">
            <div 
              className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl relative transform transition-all scale-100 opacity-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-slate-800">{meditationInputTitle}</h3>
                <button 
                  onClick={() => setShowMeditationInputModal(false)} 
                  className="text-slate-500 hover:text-slate-700 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">묵상 내용</label>
                  <textarea
                    value={meditationInputValue}
                    onChange={(e) => setMeditationInputValue(e.target.value)}
                    placeholder={meditationInputPlaceholder}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none text-lg leading-relaxed"
                    rows={8}
                    maxLength={1000}
                  />
                  <p className="text-xs text-slate-400 mt-1">{meditationInputValue.length}/1000자</p>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button 
                  onClick={() => setShowMeditationInputModal(false)}
                  className="flex-1 py-3 px-4 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl font-semibold transition-colors"
                >
                  취소
                </button>
                <button 
                  onClick={handleMeditationInputSave}
                  className="flex-1 py-3 px-4 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-xl font-semibold transition-colors"
                >
                  저장
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 크루 상세 정보 모달 - createPortal로 body에 직접 렌더링 */}
      {showCrewDetailModal && selectedCrewDetail && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCrewDetailModal(false);
            }
          }}
        >
          <div className="min-h-screen flex items-center justify-center p-4 py-8">
            <div 
              className="bg-white rounded-2xl w-full max-w-lg shadow-2xl relative transform transition-all scale-100 opacity-100 my-auto max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* 모달 헤더 */}
              <div className="relative bg-gradient-to-r from-slate-600 to-slate-700 text-white p-6 rounded-t-2xl">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center text-white text-2xl font-bold">
                    {selectedCrewDetail.name.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold">{selectedCrewDetail.name}</h2>
                    <p className="text-white/80 text-sm mt-1">{selectedCrewDetail.description}</p>
                  </div>
                </div>
                <div className="absolute top-4 right-4 flex items-center gap-2">
                  {/* 크루장만 보이는 관리 버튼들 */}
                  {(() => {
                    const isLeader = isCurrentUserCrewLeader(selectedCrewDetail);
                    console.log('🔍 크루 상세 모달 - 크루장 권한 확인:', {
                      isLeader,
                      crewName: selectedCrewDetail.name,
                      userId: user?.id,
                      crewLeaderId: selectedCrewDetail.leaderId,
                      userInfo: user
                    });
                    return isLeader;
                  })() && (
                    <>
                      <button
                        onClick={() => openLeadershipTransferModal(selectedCrewDetail)}
                        className="text-white hover:text-white transition-colors w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/20 bg-white/10 border border-white/20"
                        title="크루장 권한 이관"
                      >
                        <Users size={20} />
                      </button>
                      <button
                        onClick={() => handleDeleteCrew(selectedCrewDetail)}
                        className="text-white hover:text-white transition-colors w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/20 bg-white/10 border border-white/20"
                        title="크루 삭제"
                      >
                        <Trash2 size={20} />
                      </button>
                      <button
                        onClick={() => openCrewEditModal(selectedCrewDetail)}
                        className="text-white hover:text-white transition-colors w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/20 bg-white/10 border border-white/20"
                        title="크루 정보 수정"
                      >
                        <Edit3 size={20} />
                      </button>
                    </>
                  )}
                  
                  {/* 모든 멤버에게 보이는 크루 공유 버튼 */}
                  {(() => {
                    const isMember = selectedCrewDetail.members?.some((m: any) => m.id === user?.id);
                    return isMember && (
                      <button
                        onClick={() => handleShareCrew(selectedCrewDetail)}
                        className="text-white hover:text-white transition-colors w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/20 bg-white/10 border border-white/20"
                        title="크루 공유"
                      >
                        <Share2 size={20} />
                      </button>
                    );
                  })()}
                  <button 
                    onClick={() => setShowCrewDetailModal(false)} 
                    className="text-white/80 hover:text-white transition-colors w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* 크루 통계 */}
              <div className="p-6 border-b border-slate-100">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-slate-800">{selectedCrewDetail.memberCount || 0}</div>
                    <div className="text-xs text-slate-500">총 멤버</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-rose-600">
                      {crewDetailMembers.length > 0 ? Math.floor((crewDetailMembers.filter(m => m.hasSharedToday).length / crewDetailMembers.length) * 100) : 0}%
                    </div>
                    <div className="text-xs text-slate-500">오늘 참여율</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-800">{selectedCrewDetail.maxMembers || 20}</div>
                    <div className="text-xs text-slate-500">최대 인원</div>
                  </div>
                </div>
              </div>

              {/* 크루장 정보 */}
              <div className="p-6 border-b border-slate-100">
                <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <User size={18} className="text-slate-600" />
                  크루장
                </h3>
                <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-slate-600 to-slate-800 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {selectedCrewDetail.leader?.name?.charAt(0) || 'L'}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-slate-800">{selectedCrewDetail.leader?.name || '크루장'}</div>
                    <div className="text-sm text-slate-500">크루를 이끌어가고 있습니다</div>
                  </div>
                  <div className="w-3 h-3 bg-rose-500 rounded-full"></div>
                </div>
              </div>

              {/* 가입 신청 목록 (방장만 보임) */}
              {(() => {
                const isLeader = user?.id === selectedCrewDetail.leaderId;
                return isLeader && (
                  <div className="p-6 border-b border-slate-100">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <UserPlus size={18} className="text-rose-600" />
                        가입 신청
                        {joinRequests.length > 0 && (
                          <span className="px-2 py-0.5 bg-rose-500 text-white text-xs rounded-full font-bold animate-pulse">
                            {joinRequests.length}
                          </span>
                        )}
                      </h3>
                      {!isLoadingRequests && joinRequests.length === 0 && (
                        <span className="text-xs text-slate-400">신청 없음</span>
                      )}
                    </div>
                    
                    {isLoadingRequests ? (
                      <div className="text-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-rose-500 mx-auto"></div>
                      </div>
                    ) : joinRequests.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {joinRequests.map((request: any) => (
                          <div 
                            key={request.id}
                            className="border border-slate-200 rounded-lg p-3 bg-slate-50"
                          >
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-8 h-8 bg-gradient-to-br from-rose-400 to-pink-400 rounded-full flex items-center justify-center text-white font-bold text-xs">
                                {request.users.name.charAt(0)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-slate-800 text-sm truncate">{request.users.name}</h4>
                                <p className="text-xs text-slate-500 truncate">{request.users.church || '교회 정보 없음'}</p>
                              </div>
                              <span className="text-xs text-slate-400 whitespace-nowrap">
                                {new Date(request.createdAt).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })}
                              </span>
                            </div>
                            
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleJoinRequestAction(request.id, 'APPROVED', request.users.name)}
                                className="flex-1 py-1.5 px-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-1 text-xs"
                              >
                                <UserCheck size={14} />
                                승인
                              </button>
                              <button
                                onClick={() => handleJoinRequestAction(request.id, 'REJECTED', request.users.name)}
                                className="flex-1 py-1.5 px-3 bg-gradient-to-r from-slate-400 to-slate-500 hover:from-slate-500 hover:to-slate-600 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-1 text-xs"
                              >
                                <UserX size={14} />
                                거부
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-3 text-slate-400 text-sm bg-slate-50 rounded-lg">
                        대기 중인 가입 신청이 없습니다.
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* 멤버 목록 */}
              <div className="p-6">
                <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <Users size={18} className="text-slate-600" />
                  멤버 목록
                </h3>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {crewDetailMembers.length > 0 ? crewDetailMembers.map((member, index) => (
                    <div 
                      key={member.id} 
                      className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                      onClick={async () => {
                        console.log('👤 멤버 클릭:', member.name, member.id);
                        if (member.hasSharedToday) {
                          try {
                            // 멤버의 묵상 나눔 상세 로드
                            console.log('🔄 멤버 묵상 나눔 상세 로드 중:', member.name);
                            const meditationDetail = await crewAPI.getMemberMeditationToday(member.id);
                            console.log('✅ 멤버 묵상 나눔 상세 로드 완료:', meditationDetail);
                            
                            setSelectedMemberForDetail(member);
                            setMemberMeditationDetail(meditationDetail);
                            setShowMemberMeditationDetail(true);
                          } catch (error: any) {
                            console.error('❌ 멤버 묵상 나눔 상세 로드 실패:', error);
                            
                            // 에러 메시지 표시 (백엔드에서 오는 메시지 사용)
                            const errorMessage = error.message || '묵상 나눔 상세를 불러올 수 없습니다.';
                            alert(errorMessage);
                          }
                        } else {
                          alert(`${member.name}님은 아직 오늘의 묵상을 완료하지 않았습니다.`);
                        }
                      }}
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-slate-400 to-slate-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                        {member.name.charAt(0)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-800">{member.name}</span>
                          {member.role === 'leader' && (
                            <span className="px-2 py-0.5 bg-rose-100 text-rose-600 text-xs rounded-full font-medium">
                              크루장
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-slate-500">
                          {member.hasSharedToday ? '오늘 묵상 완료' : '아직 묵상하지 않음'}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${member.hasSharedToday ? 'bg-green-500' : 'bg-slate-300'}`}></div>
                        {member.hasSharedToday && member.completedAt && (
                          <span className="text-xs text-green-600 font-medium">
                            {new Date(member.completedAt).toLocaleTimeString('ko-KR', { 
                              hour: '2-digit', 
                              minute: '2-digit',
                              hour12: false 
                            })}
                          </span>
                        )}
                        {/* 리더 전용 강제 나가기 버튼 */}
                        {selectedCrewDetail?.leader?.id === user?.id && member.role !== 'leader' && (
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!confirm(`정말 ${member.name}님을 크루에서 나가게 하시겠습니까?`)) return;
                              try {
                                await crewAPI.kickMember(selectedCrewDetail.id, member.id);
                                alert(`${member.name}님을 크루에서 나가게 했습니다.`);
                                // 멤버 목록 새로고침
                                const refreshed = await crewAPI.getCrewDetail(selectedCrewDetail.id);
                                setSelectedCrewDetail(refreshed);
                                setCrewDetailMembers(refreshed?.members || []);
                              } catch (error: any) {
                                alert(error.message || '멤버 나가기에 실패했습니다.');
                              }
                            }}
                            className="ml-2 px-2 py-1 text-xs rounded-md bg-red-50 hover:bg-red-100 text-red-600 border border-red-200"
                          >
                            강제 나가기
                          </button>
                        )}
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-4 text-slate-500">
                      멤버가 없습니다.
                    </div>
                  )}
                </div>
              </div>

              {/* 하단 버튼 */}
              <div className="p-6 border-t border-slate-100">
                <div className="flex gap-3">
                  <button 
                    onClick={() => setShowCrewDetailModal(false)}
                    className="flex-1 py-3 px-4 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl font-semibold transition-colors"
                  >
                    닫기
                  </button>
                  {(() => {
                    const isMember = isCurrentUserMemberOfCrew(selectedCrewDetail.id);
                    console.log('🔍 크루 상세 모달 - 멤버십 상태:', { 
                      selectedCrewId: selectedCrewDetail.id, 
                      myCrewId: myCrew?.id, 
                      isMember 
                    });
                    
                    return isMember ? (
                      <button 
                        onClick={() => handleLeaveCrew(selectedCrewDetail)}
                        className="flex-1 py-3 px-4 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl font-semibold transition-colors"
                      >
                        🚪 나가기
                      </button>
                    ) : (
                      <button 
                        onClick={() => handleJoinCrew(selectedCrewDetail)}
                        disabled={isJoiningCrew}
                        className="flex-1 py-3 px-4 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-xl font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isJoiningCrew ? '신청 중...' : '📝 가입 신청'}
                      </button>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 공지사항 모달 */}
      {showNoticeModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden">
            {/* 모달 헤더 */}
            <div className="bg-gradient-to-r from-slate-600 to-slate-700 text-white p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-2xl flex items-center justify-center">
                    <Bell size={20} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold">e2b 공지</h2>
                    <p className="text-white/80 text-sm">eat bible everyday</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowNoticeModal(false)}
                  className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center hover:bg-white/30 transition-colors"
                >
                  <X size={18} className="text-white" />
                </button>
              </div>
            </div>

            {/* 모달 내용 */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-4">
                <div>
                  <h3 className="text-2xl font-bold text-slate-800 mb-3">{noticeData.title}</h3>
                  <div className="flex items-center gap-2 text-slate-600 text-base mb-4">
                    <span>📅 {noticeData.date}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600 text-base mb-4">
                    <span>📍 {noticeData.location}</span>
                  </div>
                </div>
                
                <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200">
                  <pre className="text-slate-700 text-base leading-relaxed whitespace-pre-wrap font-medium">
                    {noticeData.content}
                  </pre>
                </div>
              </div>
            </div>

            {/* 모달 하단 */}
            <div className="p-6 border-t border-slate-200 bg-slate-50">
              <button
                onClick={() => setShowNoticeModal(false)}
                className="w-full bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white font-bold py-3 px-6 rounded-2xl transition-all"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 알림 모달 (가입요청/중보요청) */}
      {showNotificationModal && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto"
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            width: '100vw',
            height: '100vh'
          }}
          onClick={() => {
            setShowNotificationModal(false);
            
            // 공지사항 확인 여부 저장
            if (announcements.length > 0) {
              localStorage.setItem('hasSeenAnnouncement', 'true');
              console.log('✅ 공지사항 확인 완료 - 배경 클릭');
            }
            
            // 배경 클릭 시에도 알림 재로드
            setTimeout(() => loadUserNotifications(), 500);
          }}
        >
          <div className="min-h-screen flex items-start justify-center p-4 pt-8 sm:pt-16">
            <div 
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full my-auto"
              onClick={(e) => e.stopPropagation()}
              style={{ maxHeight: 'calc(100vh - 4rem)' }}
            >
              {/* 헤더 (고정) */}
              <div className="bg-gradient-to-r from-slate-600 to-slate-700 text-white p-4 sm:p-6 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-xl sm:rounded-2xl flex items-center justify-center">
                      <Bell size={18} className="text-white sm:hidden" />
                      <Bell size={20} className="text-white hidden sm:block" />
                    </div>
                    <div>
                      <h2 className="text-base sm:text-lg font-bold">알림</h2>
                      <p className="text-white/80 text-xs sm:text-sm">가입 요청 / 중보기도 요청</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowNotificationModal(false);
                      
                      // 공지사항 확인 여부 저장
                      if (announcements.length > 0) {
                        localStorage.setItem('hasSeenAnnouncement', 'true');
                        console.log('✅ 공지사항 확인 완료 - X 버튼 클릭');
                      }
                      
                      // X 버튼 클릭 시에도 알림 재로드
                      setTimeout(() => loadUserNotifications(), 500);
                    }}
                    className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center hover:bg-white/30 transition-colors flex-shrink-0"
                  >
                    <X size={18} className="text-white" />
                  </button>
                </div>
              </div>

              {/* 내용 (스크롤 가능) */}
              <div 
                className="p-4 sm:p-6 space-y-4 sm:space-y-6 overflow-y-auto" 
                style={{ maxHeight: 'calc(100vh - 16rem)' }}
              >
              {/* 공지사항 섹션 */}
              <div>
                <h3 className="text-sm font-bold text-slate-800 mb-2">📢 공지사항</h3>
                {isLoadingAnnouncements ? (
                  <div className="text-sm text-slate-500 text-center py-4">로딩 중...</div>
                ) : announcements.length === 0 ? (
                  <div className="text-sm text-slate-500">공지사항이 없습니다.</div>
                ) : (
                  <div className="space-y-2">
                    {announcements.map((announcement: any) => (
                      <div key={announcement.id} className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                        <div className="text-sm font-semibold text-blue-800 mb-2">{announcement.title}</div>
                        <div className="text-xs text-blue-700 whitespace-pre-wrap leading-relaxed max-h-60 overflow-y-auto">
                          {announcement.content}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-sm font-bold text-slate-800 mb-2">크루 가입 요청</h3>
                {pendingJoinsByCrew.length === 0 ? (
                  <div className="text-sm text-slate-500">대기 중인 가입 요청이 없습니다.</div>
                ) : (
                  <div className="space-y-2">
                    {pendingJoinsByCrew.map((it: any) => (
                      <div key={it.crewId} className="p-3 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
                        <div className="font-medium text-slate-700">{it.crewName}</div>
                        <div className="text-xs bg-rose-100 text-rose-700 rounded-full px-2 py-1">{it.count}건</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-sm font-bold text-slate-800 mb-2">중보 기도 요청</h3>
                {prayerRequests.length === 0 ? (
                  <div className="text-sm text-slate-500">중보 기도 요청이 없습니다.</div>
                ) : (
                  <div className="space-y-2">
                    {prayerRequests.map((m: any) => (
                      <div key={m.id} className="p-3 bg-purple-50 rounded-xl border border-purple-200">
                        <div className="text-sm font-semibold text-purple-800">{m.user?.name || '사용자'}</div>
                        <div className="text-sm text-purple-700 mt-1 whitespace-pre-wrap">{m.prayer}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

              {/* 하단 (고정) */}
              <div className="p-4 sm:p-6 border-t border-slate-200 bg-slate-50 rounded-b-3xl">
                <button
                  onClick={() => {
                    setShowNotificationModal(false);
                    
                    // 공지사항 확인 여부 저장
                    if (announcements.length > 0) {
                      localStorage.setItem('hasSeenAnnouncement', 'true');
                      console.log('✅ 공지사항 확인 완료 - 다음부터 자동 팝업 안 뜸');
                    }
                    
                    // 모달 닫을 때 알림 재로드 (승인/거부 후 카운트 갱신)
                    setTimeout(() => loadUserNotifications(), 500);
                  }}
                  className="w-full bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white font-bold py-3 px-6 rounded-xl sm:rounded-2xl transition-all text-sm sm:text-base"
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 크루 편집 모달 (크루장 전용) */}
      {showCrewEditModal && editingCrew && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCrewEditModal(false);
            }
          }}
        >
          <div className="min-h-screen flex items-center justify-center p-4 py-8">
            <div 
              className="bg-white rounded-2xl w-full max-w-md shadow-2xl relative transform transition-all scale-100 opacity-100 my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* 모달 헤더 */}
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-slate-800">크루 정보 수정</h3>
                  <button
                    onClick={() => setShowCrewEditModal(false)}
                    className="text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>
                <p className="text-sm text-slate-600 mt-2">크루장만 크루 정보를 수정할 수 있습니다.</p>
              </div>

              {/* 모달 내용 */}
              <div className="p-6 space-y-4">
                {/* 크루명 */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    크루명 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={crewEditData.name}
                    onChange={(e) => setCrewEditData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="크루명을 입력해주세요"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent text-lg"
                    maxLength={30}
                  />
                  <p className="text-xs text-slate-500 mt-1">{crewEditData.name.length}/30자</p>
                </div>

                {/* 크루 설명 */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    크루 설명
                  </label>
                  <textarea
                    value={crewEditData.description}
                    onChange={(e) => setCrewEditData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="크루에 대한 간단한 설명을 입력해주세요"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none"
                    rows={4}
                    maxLength={200}
                  />
                  <p className="text-xs text-slate-500 mt-1">{crewEditData.description.length}/200자</p>
                </div>

                {/* 크루 이미지 URL */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    크루 이미지 URL (선택)
                  </label>
                  <input
                    type="url"
                    value={crewEditData.image}
                    onChange={(e) => setCrewEditData(prev => ({ ...prev, image: e.target.value }))}
                    placeholder="https://example.com/image.jpg"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  />
                  <p className="text-xs text-slate-500 mt-1">크루를 대표할 이미지 URL을 입력해주세요</p>
                </div>
              </div>

              {/* 모달 하단 버튼 */}
              <div className="p-6 border-t border-slate-100">
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCrewEditModal(false)}
                    className="flex-1 py-3 px-4 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl font-semibold transition-colors"
                  >
                    취소
                  </button>
                  <button
                    onClick={handleUpdateCrew}
                    disabled={isUpdatingCrew || !crewEditData.name.trim()}
                    className="flex-1 py-3 px-4 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-xl font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUpdatingCrew ? '수정 중...' : '수정하기'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 묵상 수정 모달 */}
      {showMeditationEditModal && editingMeditation && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowMeditationEditModal(false);
            }
          }}
        >
          <div className="min-h-screen flex items-center justify-center p-4 py-8">
            <div 
              className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl relative transform transition-all scale-100 opacity-100 my-auto max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* 모달 헤더 */}
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-slate-800">묵상 기록 수정</h3>
                  <button
                    onClick={() => setShowMeditationEditModal(false)}
                    className="text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>
                <p className="text-sm text-slate-600 mt-2">
                  {new Date(editingMeditation.date || '').toLocaleDateString('ko-KR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })} 묵상 기록
                </p>
              </div>

              {/* 모달 내용 */}
              <div className="p-6 space-y-6">
                {/* 말씀 정보 */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                    <BookOpen size={18} />
                    말씀 정보
                  </h4>
                  
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      성경 구절
                    </label>
                    <input
                      type="text"
                      value={meditationEditData.verseReference}
                      onChange={(e) => setMeditationEditData(prev => ({ ...prev, verseReference: e.target.value }))}
                      placeholder="예: 요한복음 3장 16절"
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent text-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      말씀 내용
                    </label>
                    <textarea
                      value={meditationEditData.verseText}
                      onChange={(e) => setMeditationEditData(prev => ({ ...prev, verseText: e.target.value }))}
                      placeholder="말씀 내용을 입력해주세요"
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none"
                      rows={3}
                    />
                  </div>
                </div>

                {/* 묵상 내용 */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                    <Heart size={18} />
                    묵상 내용
                  </h4>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      하나님이 나에게 원하시는 것
                    </label>
                    <textarea
                      value={meditationEditData.godsWill}
                      onChange={(e) => setMeditationEditData(prev => ({ ...prev, godsWill: e.target.value }))}
                      placeholder="하나님이 나에게 원하시는 것을 적어보세요"
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none"
                      rows={4}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      내가 변화하고 성장해야 할 부분
                    </label>
                    <textarea
                      value={meditationEditData.growthChange}
                      onChange={(e) => setMeditationEditData(prev => ({ ...prev, growthChange: e.target.value }))}
                      placeholder="변화하고 성장해야 할 부분을 적어보세요"
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none"
                      rows={4}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      기도
                    </label>
                    <textarea
                      value={meditationEditData.prayer}
                      onChange={(e) => setMeditationEditData(prev => ({ ...prev, prayer: e.target.value }))}
                      placeholder="하나님께 올려드릴 기도를 적어보세요"
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none"
                      rows={4}
                    />
                  </div>
                </div>
              </div>

              {/* 모달 하단 버튼 */}
              <div className="p-6 border-t border-slate-100">
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowMeditationEditModal(false)}
                    className="flex-1 py-3 px-4 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl font-semibold transition-colors"
                  >
                    취소
                  </button>
                  <button
                    onClick={handleUpdateMeditation}
                    disabled={isUpdatingMeditation}
                    className="flex-1 py-3 px-4 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-xl font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUpdatingMeditation ? '수정 중...' : '수정하기'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 크루 수정 모달 */}
      {showCrewEditModal && editingCrew && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCrewEditModal(false);
            }
          }}
        >
          <div className="min-h-screen flex items-center justify-center p-4 py-8">
            <div 
              className="bg-white rounded-2xl w-full max-w-md shadow-2xl relative transform transition-all scale-100 opacity-100 my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* 모달 헤더 */}
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-slate-800">크루 정보 수정</h3>
                  <button
                    onClick={() => setShowCrewEditModal(false)}
                    className="text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>
                <p className="text-sm text-slate-600 mt-2">크루장만 크루 정보를 수정할 수 있습니다.</p>
              </div>

              {/* 모달 내용 */}
              <div className="p-6 space-y-4">
                {/* 크루명 */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    크루명 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={crewEditData.name}
                    onChange={(e) => setCrewEditData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="크루명을 입력해주세요"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent text-lg"
                    maxLength={30}
                  />
                  <p className="text-xs text-slate-500 mt-1">{crewEditData.name.length}/30자</p>
                </div>

                {/* 크루 설명 */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    크루 설명
                  </label>
                  <textarea
                    value={crewEditData.description}
                    onChange={(e) => setCrewEditData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="크루에 대한 간단한 설명을 입력해주세요"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none"
                    rows={4}
                    maxLength={200}
                  />
                  <p className="text-xs text-slate-500 mt-1">{crewEditData.description.length}/200자</p>
                </div>

                {/* 크루 이미지 URL */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    크루 이미지 URL (선택)
                  </label>
                  <input
                    type="url"
                    value={crewEditData.image}
                    onChange={(e) => setCrewEditData(prev => ({ ...prev, image: e.target.value }))}
                    placeholder="https://example.com/image.jpg"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  />
                  <p className="text-xs text-slate-500 mt-1">크루를 대표할 이미지 URL을 입력해주세요</p>
                </div>
              </div>

              {/* 모달 하단 버튼 */}
              <div className="p-6 border-t border-slate-100">
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCrewEditModal(false)}
                    className="flex-1 py-3 px-4 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl font-semibold transition-colors"
                  >
                    취소
                  </button>
                  <button
                    onClick={handleUpdateCrew}
                    disabled={isUpdatingCrew || !crewEditData.name.trim()}
                    className="flex-1 py-3 px-4 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-xl font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUpdatingCrew ? '수정 중...' : '수정하기'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 묵상 화면 */}
      <MeditationScreen />

      {/* 사용자 설정 페이지 */}
      {showUserSettings && (
        <UserSettingsPage 
          onClose={() => setShowUserSettings(false)} 
          userCrew={myCrew}
          onCrewUpdate={(crew) => setMyCrew(crew)}
        />
      )}

      {/* 피드 상세보기 모달 */}
      {showFeedDetailModal && selectedFeedItem && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowFeedDetailModal(false);
            }
          }}
        >
          <div className="min-h-screen flex items-center justify-center p-4 py-8">
            <div 
              className="bg-white rounded-2xl w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl shadow-2xl relative transform transition-all scale-100 opacity-100 my-auto max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
            {/* 모달 헤더 */}
              <div className="sticky top-0 bg-white rounded-t-2xl border-b border-slate-200 p-4 sm:p-6 z-10">
              <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* 사용자 프로필 사진 또는 이니셜 */}
                  {selectedFeedItem.user?.avatar ? (
                    <img 
                        src={getAvatarUrl(selectedFeedItem.user.avatar)} 
                      alt={selectedFeedItem.user.name || 'User'} 
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl object-cover shadow-lg flex-shrink-0"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-slate-600 to-slate-800 rounded-xl flex items-center justify-center text-white font-bold text-sm sm:text-lg shadow-lg flex-shrink-0 ${selectedFeedItem.user?.avatar ? 'hidden' : ''}`}>
                      {selectedFeedItem.user?.name?.[0] || 'U'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-lg sm:text-xl font-bold text-slate-800 truncate">{selectedFeedItem.user?.name || 'Unknown'}</h3>
                      <p className="text-xs sm:text-sm text-slate-600 truncate">
                      {selectedFeedItem.createdAt ? formatDateTimeKST(new Date(selectedFeedItem.createdAt), {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : (selectedFeedItem.time || '방금 전')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowFeedDetailModal(false)}
                    className="w-8 h-8 sm:w-10 sm:h-10 bg-slate-100 hover:bg-slate-200 rounded-xl flex items-center justify-center transition-colors flex-shrink-0 ml-2"
                >
                    <X size={16} className="sm:w-5 sm:h-5 text-slate-600" />
                </button>
              </div>
            </div>

            {/* 모달 내용 */}
              <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* 말씀 섹션 */}
              {(selectedFeedItem.verse || selectedFeedItem.verseText || selectedFeedItem.verseReference) && (
                  <div className="bg-gradient-to-r from-rose-50 to-orange-50 p-4 sm:p-6 rounded-2xl sm:rounded-3xl border-l-4 border-rose-300">
                    <div className="flex items-start gap-2 mb-3">
                      <BookOpen size={18} className="text-rose-600 flex-shrink-0 mt-0.5" />
                      <h4 className="text-base sm:text-lg font-bold text-rose-700 leading-tight">{selectedFeedItem.verseReference || '오늘의 말씀'}</h4>
                  </div>
                    <p className="text-slate-700 text-sm sm:text-lg font-medium italic leading-relaxed">{selectedFeedItem.verse || selectedFeedItem.verseText}</p>
                  {selectedFeedItem.readingCount && (
                      <div className="mt-3 text-xs sm:text-sm text-rose-600 font-semibold">
                      📖 {selectedFeedItem.readingCount}번 읽기 완료
                    </div>
                  )}
                </div>
              )}

              {/* 묵상 단계별 내용 */}
                <div className="space-y-3 sm:space-y-4">
                {selectedFeedItem.godsWill && (
                    <div className="bg-white border-2 border-slate-200 rounded-2xl sm:rounded-3xl p-4 sm:p-6">
                      <div className="flex items-start gap-3 mb-3 sm:mb-4">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-blue-600 font-bold text-xs sm:text-sm">1</span>
                      </div>
                        <h4 className="text-sm sm:text-lg font-bold text-slate-800 leading-tight">하나님이 나에게 원하시는 것은?</h4>
                    </div>
                      <p className="text-slate-700 text-sm sm:text-base leading-relaxed whitespace-pre-wrap pl-9 sm:pl-11">{selectedFeedItem.godsWill}</p>
                  </div>
                )}

                {selectedFeedItem.growthChange && (
                    <div className="bg-white border-2 border-slate-200 rounded-2xl sm:rounded-3xl p-4 sm:p-6">
                      <div className="flex items-start gap-3 mb-3 sm:mb-4">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-100 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-green-600 font-bold text-xs sm:text-sm">2</span>
                      </div>
                        <h4 className="text-sm sm:text-lg font-bold text-slate-800 leading-tight">내가 변화성장해야 하는 것은?</h4>
                    </div>
                      <p className="text-slate-700 text-sm sm:text-base leading-relaxed whitespace-pre-wrap pl-9 sm:pl-11">{selectedFeedItem.growthChange}</p>
                  </div>
                )}

                {selectedFeedItem.prayer && selectedFeedItem.prayerShareType !== 'private' && (
                    <div className="bg-white border-2 border-slate-200 rounded-2xl sm:rounded-3xl p-4 sm:p-6">
                      <div className="flex items-start gap-3 mb-3 sm:mb-4">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-purple-100 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-purple-600 font-bold text-xs sm:text-sm">3</span>
                      </div>
                        <h4 className="text-sm sm:text-lg font-bold text-slate-800 leading-tight">
                        {selectedFeedItem.prayerShareType === 'request' ? '중보기도 요청 🙏' : '이 말씀으로 기도해보자'}
                      </h4>
                    </div>
                      <p className="text-slate-700 text-sm sm:text-base leading-relaxed whitespace-pre-wrap pl-9 sm:pl-11">{selectedFeedItem.prayer}</p>
                    {selectedFeedItem.prayerShareType === 'request' && (
                        <div className="mt-3 sm:mt-4 p-3 bg-purple-50 rounded-xl sm:rounded-2xl ml-9 sm:ml-11">
                          <p className="text-xs sm:text-sm text-purple-700 font-medium">💜 함께 중보기도 해주세요</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 게스트 모드 로그인 유도 또는 좋아요 */}
              <div className="pt-3 sm:pt-4 border-t border-slate-200">
                {isGuestMode ? (
                  /* 게스트 모드: 로그인 유도 */
                  <div className="text-center space-y-3">
                    <div className="bg-gradient-to-r from-rose-50 to-orange-50 p-4 rounded-xl border border-rose-200">
                      <p className="text-sm text-slate-700 mb-3">
                        💝 이 묵상이 마음에 드시나요?<br/>
                        <span className="font-semibold text-rose-600">로그인하고 더 많은 묵상을 나눠보세요!</span>
                      </p>
                <button 
                        onClick={() => {
                          setShowFeedDetailModal(false);
                          setShowAuthModal(true);
                        }}
                        className="w-full bg-gradient-to-r from-rose-400 to-pink-500 hover:from-rose-500 hover:to-pink-600 text-white font-semibold py-3 px-6 rounded-xl transition-all transform hover:scale-[1.02] shadow-lg"
                      >
                        🚀 로그인하고 참여하기
                      </button>
                    </div>
                  </div>
                ) : (
                  /* 로그인 사용자: 좋아요 버튼 */
                  <div className="flex items-center justify-center">
                    <button 
                      className={`flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-2 sm:py-3 rounded-xl sm:rounded-2xl transition-all transform hover:scale-105 ${
                    devotionLikes[selectedFeedItem.id] ? 'text-rose-500 bg-rose-50' : 'text-slate-500 hover:text-rose-500 hover:bg-rose-50'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLike(selectedFeedItem.id);
                  }}
                >
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center ${
                    devotionLikes[selectedFeedItem.id] ? 'bg-rose-100' : 'bg-slate-100'
                  }`}>
                        <Heart size={16} className="sm:w-5 sm:h-5" fill={devotionLikes[selectedFeedItem.id] ? 'currentColor' : 'none'} />
                  </div>
                      <span className="font-semibold text-base sm:text-lg">
                    {(selectedFeedItem.likes || 0) + (devotionLikes[selectedFeedItem.id] ? 1 : 0)}
                  </span>
                </button>
              </div>
                )}
            </div>
          </div>
        </div>
        </div>
        </div>,
        document.body
      )}

      {/* 모바일 디버깅 모달 */}
      {showDebugModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[80] p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden">
            <div className="bg-red-500 text-white p-4 rounded-t-3xl">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold">🐛 모바일 디버그 정보</h3>
                <button
                  onClick={() => setShowDebugModal(false)}
                  className="w-8 h-8 bg-red-600 hover:bg-red-700 rounded-xl flex items-center justify-center transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
            
            <div className="p-4 max-h-[60vh] overflow-y-auto">
              <div className="space-y-4">
                {/* 기본 정보 */}
                <div className="bg-blue-50 p-3 rounded-2xl">
                  <h4 className="font-bold text-blue-800 mb-2">📱 기기 정보</h4>
                  <div className="text-sm text-blue-700 space-y-1">
                    <p>User Agent: {navigator.userAgent}</p>
                    <p>화면 크기: {window.innerWidth} x {window.innerHeight}</p>
                    <p>뷰포트: {window.visualViewport?.width || 'N/A'} x {window.visualViewport?.height || 'N/A'}</p>
                    <p>키보드 상태: {keyboardVisible ? '열림' : '닫힘'}</p>
                  </div>
                </div>

                {/* 입력 이벤트 정보 */}
                <div className="bg-green-50 p-3 rounded-2xl">
                  <h4 className="font-bold text-green-800 mb-2">⌨️ 입력 이벤트</h4>
                  <div className="text-xs text-green-700 space-y-1 max-h-32 overflow-y-auto">
                    {Object.entries(inputDebugInfo).map(([event, info]: [string, any]) => (
                      <div key={event} className="border-b border-green-200 pb-1">
                        <strong>{event}:</strong> {info.tagName} "{info.value?.slice(0, 20)}..." 
                        cursor: {info.selectionStart}-{info.selectionEnd}
                      </div>
                    ))}
                  </div>
                </div>

                {/* 테스트 입력 필드 */}
                <div className="bg-yellow-50 p-3 rounded-2xl">
                  <h4 className="font-bold text-yellow-800 mb-2">🧪 테스트 입력</h4>
                  <textarea
                    placeholder="여기에 텍스트를 입력해보세요..."
                    className="w-full p-2 border border-yellow-300 rounded-lg text-sm"
                    rows={3}
                    onFocus={(e) => addDebugInfo(`TEST FOCUS: ${e.target.tagName}`)}
                    onBlur={(e) => addDebugInfo(`TEST BLUR: value="${(e.target as HTMLTextAreaElement).value}"`)}
                    onInput={(e) => addDebugInfo(`TEST INPUT: "${(e.target as HTMLTextAreaElement).value}"`)}
                    onChange={(e) => addDebugInfo(`TEST CHANGE: "${(e.target as HTMLTextAreaElement).value}"`)}
                    onKeyDown={(e) => addDebugInfo(`TEST KEYDOWN: ${e.key}`)}
                    onCompositionStart={() => addDebugInfo('TEST COMPOSITION START')}
                    onCompositionEnd={(e) => addDebugInfo(`TEST COMPOSITION END: "${(e.target as HTMLTextAreaElement).value}"`)}
                  />
                </div>

                {/* 디버그 로그 */}
                <div className="bg-gray-50 p-3 rounded-2xl">
                  <h4 className="font-bold text-gray-800 mb-2">📝 이벤트 로그</h4>
                  <div className="text-xs text-gray-700 space-y-1 max-h-40 overflow-y-auto font-mono">
                    {debugInfo.map((log, index) => (
                      <div key={index} className="border-b border-gray-200 pb-1">
                        {log}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t border-gray-200 flex gap-2">
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(JSON.stringify({
                    userAgent: navigator.userAgent,
                    screenSize: `${window.innerWidth}x${window.innerHeight}`,
                    viewport: `${window.visualViewport?.width || 'N/A'}x${window.visualViewport?.height || 'N/A'}`,
                    keyboardVisible,
                    inputEvents: inputDebugInfo,
                    logs: debugInfo
                  }, null, 2));
                  alert('디버그 정보가 클립보드에 복사되었습니다!');
                }}
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-2xl font-semibold transition-colors"
              >
                📋 복사
              </button>
              <button
                onClick={() => {
                  setDebugInfo([]);
                  setInputDebugInfo({});
                }}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded-2xl font-semibold transition-colors"
              >
                🗑️ 초기화
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 묵상 히스토리 모달 */}
      {showMeditationHistory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden">
            {/* 헤더 */}
            <div className="bg-gradient-to-r from-slate-600 to-slate-800 text-white p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold">묵상 기록 ({meditationTotal})</h2>
                <button
                  onClick={() => {
                    setShowMeditationHistory(false);
                    setSelectedHistoryItem(null);
                    setShowAllHistory(false); // 전체 보기 상태 초기화
                  }}
                  className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* 내용 */}
            <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
              {!selectedHistoryItem ? (
                // 리스트 화면
                <div className="p-6">
                  {isLoadingHistory ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600 mx-auto"></div>
                      <p className="text-slate-600 mt-2">불러오는 중...</p>
                    </div>
                  ) : meditationHistory.length === 0 ? (
                    <div className="text-center py-8">
                      <BookOpen size={48} className="text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-600">아직 묵상 기록이 없습니다.</p>
                      <p className="text-slate-400 text-sm mt-2">묵상을 시작해보세요!</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {meditationHistory.map((item, index) => (
                        <div
                          key={item.id}
                          onClick={() => setSelectedHistoryItem(item)}
                          className="bg-slate-50 rounded-2xl p-4 cursor-pointer hover:bg-slate-100 transition-colors border border-slate-200"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs bg-slate-600 text-white px-2 py-1 rounded-full">
                                  {new Date(item.date || '').toLocaleDateString('ko-KR', {
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </span>
                                {item.step4Complete && (
                                  <CheckCircle size={16} className="text-green-500" />
                                )}
                              </div>
                              <p className="text-sm font-medium text-slate-800 mb-1">
                                {item.verseReference}
                              </p>
                              <p className="text-xs text-slate-600 line-clamp-2">
                                {item.verseText}
                              </p>
                              {item.godsWill && (
                                <p className="text-xs text-slate-500 mt-2 line-clamp-1">
                                  💭 {item.godsWill}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openMeditationEditModal(item);
                                }}
                                className="p-2 hover:bg-blue-100 rounded-lg transition-colors group"
                                title="수정"
                              >
                                <Edit3 size={14} className="text-slate-400 group-hover:text-blue-500" />
                              </button>
                              <button
                                onClick={(e) => deleteMeditationRecord(item.id, e)}
                                className="p-2 hover:bg-red-100 rounded-lg transition-colors group"
                                title="삭제"
                              >
                                <Trash2 size={14} className="text-slate-400 group-hover:text-red-500" />
                              </button>
                              <ArrowRight size={16} className="text-slate-400" />
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {/* 전체 보기 버튼 */}
                      {!showAllHistory && meditationTotal > 20 && (
                        <div className="text-center mt-6">
                          <button
                            onClick={handleShowAllHistory}
                            className="px-6 py-3 bg-slate-600 text-white rounded-xl hover:bg-slate-700 transition-colors font-semibold"
                          >
                            전체 보기 ({meditationTotal}개)
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                // 상세 화면
                <div className="p-6">
                  <button
                    onClick={() => setSelectedHistoryItem(null)}
                    className="flex items-center gap-2 text-slate-600 hover:text-slate-800 mb-4"
                  >
                    <ArrowLeft size={16} />
                    <span className="text-sm">목록으로</span>
                  </button>

                  <div className="space-y-6">
                    {/* 날짜 및 말씀 */}
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-4 border border-orange-200/50">
                      <div className="text-center">
                        <p className="text-xs text-slate-500 mb-2">
                          {new Date(selectedHistoryItem.date || '').toLocaleDateString('ko-KR', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                        <p className="text-sm font-semibold text-slate-700 mb-2">
                          {selectedHistoryItem.verseReference}
                        </p>
                        <p className="text-sm text-slate-600 leading-relaxed">
                          {selectedHistoryItem.verseText}
                        </p>
                      </div>
                    </div>

                    {/* 읽기 횟수 */}
                    {selectedHistoryItem.readingCount > 0 && (
                      <div className="bg-slate-50 rounded-2xl p-4">
                        <h4 className="text-sm font-semibold text-slate-700 mb-2">📖 말씀 선포</h4>
                        <p className="text-sm text-slate-600">
                          {selectedHistoryItem.readingCount}번 선포했습니다
                        </p>
                      </div>
                    )}

                    {/* 하나님의 뜻 */}
                    {selectedHistoryItem.godsWill && (
                      <div className="bg-slate-50 rounded-2xl p-4">
                        <h4 className="text-sm font-semibold text-slate-700 mb-2">🙏 하나님의 뜻</h4>
                        <p className="text-sm text-slate-600 leading-relaxed">
                          {selectedHistoryItem.godsWill}
                        </p>
                      </div>
                    )}

                    {/* 변화성장 */}
                    {selectedHistoryItem.growthChange && (
                      <div className="bg-slate-50 rounded-2xl p-4">
                        <h4 className="text-sm font-semibold text-slate-700 mb-2">🌱 변화성장</h4>
                        <p className="text-sm text-slate-600 leading-relaxed">
                          {selectedHistoryItem.growthChange}
                        </p>
                      </div>
                    )}

                    {/* 기도 */}
                    {selectedHistoryItem.prayer && (
                      <div className="bg-slate-50 rounded-2xl p-4">
                        <h4 className="text-sm font-semibold text-slate-700 mb-2">🕊️ 기도</h4>
                        <p className="text-sm text-slate-600 leading-relaxed">
                          {selectedHistoryItem.prayer}
                        </p>
                      </div>
                    )}

                    {/* 완료 상태 */}
                    <div className="bg-slate-50 rounded-2xl p-4">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3">완료 현황</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { step: 1, label: '말씀 선포', completed: selectedHistoryItem.step1Complete },
                          { step: 2, label: '하나님의 뜻', completed: selectedHistoryItem.step2Complete },
                          { step: 3, label: '변화성장', completed: selectedHistoryItem.step3Complete },
                          { step: 4, label: '기도', completed: selectedHistoryItem.step4Complete }
                        ].map(item => (
                          <div
                            key={item.step}
                            className={`text-xs px-3 py-2 rounded-lg text-center ${
                              item.completed
                                ? 'bg-green-100 text-green-700'
                                : 'bg-slate-200 text-slate-500'
                            }`}
                          >
                            {item.completed ? '✓' : '○'} {item.label}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 묵상 디버깅 모달 */}
      {showMeditationDebug && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-slate-800">🐛 묵상 디버깅</h3>
              <button
                onClick={() => setShowMeditationDebug(false)}
                className="text-slate-500 hover:text-slate-700"
              >
                ✕
              </button>
            </div>

            {/* 디바이스 정보 */}
            <div className="mb-4 p-3 bg-slate-50 rounded-lg">
              <h4 className="font-semibold text-sm text-slate-700 mb-2">📱 디바이스 정보</h4>
              <div className="text-xs text-slate-600 space-y-1">
                <div>User Agent: {navigator.userAgent.slice(0, 50)}...</div>
                <div>화면 크기: {window.screen.width}x{window.screen.height}</div>
                <div>뷰포트: {window.innerWidth}x{window.innerHeight}</div>
                <div>키보드: {keyboardVisible ? '열림' : '닫힘'}</div>
                <div>한글 입력: {isComposing ? '진행중' : '대기'}</div>
              </div>
            </div>

            {/* 묵상 이벤트 로그 */}
            <div className="mb-4">
              <h4 className="font-semibold text-sm text-slate-700 mb-2">📝 묵상 이벤트 로그</h4>
              <div className="bg-slate-900 text-green-400 p-3 rounded-lg text-xs max-h-40 overflow-y-auto font-mono">
                {meditationDebugInfo.length === 0 ? (
                  <div className="text-slate-500">아직 이벤트가 없습니다.</div>
                ) : (
                  meditationDebugInfo.map((log, index) => (
                    <div key={index} className="mb-1">{log}</div>
                  ))
                )}
              </div>
            </div>

            {/* 테스트 입력 필드 */}
            <div className="mb-4">
              <h4 className="font-semibold text-sm text-slate-700 mb-2">🧪 테스트 입력</h4>
              <textarea
                placeholder="여기서 입력을 테스트해보세요..."
                className="w-full p-3 border border-slate-300 rounded-lg text-sm resize-none"
                rows={3}
                onFocus={() => addMeditationDebugInfo('TEST FOCUS: TEXTAREA')}
                onBlur={(e) => addMeditationDebugInfo(`TEST BLUR: value="${e.target.value}"`)}
                onInput={(e) => addMeditationDebugInfo(`TEST INPUT: "${(e.target as HTMLTextAreaElement).value}"`)}
                onCompositionStart={() => addMeditationDebugInfo('TEST COMPOSITION START')}
                onCompositionEnd={(e) => addMeditationDebugInfo(`TEST COMPOSITION END: "${e.data}"`)}
                onTouchStart={() => addMeditationDebugInfo('TEST TOUCH START')}
                onTouchEnd={() => addMeditationDebugInfo('TEST TOUCH END')}
              />
            </div>

            {/* 액션 버튼들 */}
            <div className="flex gap-2">
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(JSON.stringify({
                    userAgent: navigator.userAgent,
                    screenSize: `${window.screen.width}x${window.screen.height}`,
                    viewport: `${window.innerWidth}x${window.innerHeight}`,
                    keyboardVisible,
                    isComposing,
                    logs: meditationDebugInfo
                  }, null, 2));
                  alert('디버그 정보가 클립보드에 복사되었습니다!');
                }}
                className="flex-1 py-2 px-4 bg-blue-500 text-white rounded-lg text-sm font-semibold hover:bg-blue-600 transition-all"
              >
                📋 복사
              </button>
              <button
                onClick={() => {
                  setMeditationDebugInfo([]);
                  addMeditationDebugInfo('디버그 로그 초기화됨');
                }}
                className="flex-1 py-2 px-4 bg-slate-500 text-white rounded-lg text-sm font-semibold hover:bg-slate-600 transition-all"
              >
                🗑️ 초기화
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 크루 모달 */}
      {showCrewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-slate-800">
                {userCrewStatus === 'none' ? '크루 가입하기' : '크루 나눔'}
              </h3>
              <button
                onClick={() => setShowCrewModal(false)}
                className="text-slate-500 hover:text-slate-700"
              >
                <X size={24} />
              </button>
            </div>

            {userCrewStatus === 'none' ? (
              // 크루 미가입 시 - 크루 목록 표시
              <div className="space-y-4">
                <p className="text-slate-600 mb-4">가입할 크루를 선택해주세요</p>
                {/* 디버깅 정보 */}
                {isGuestMode && (
                  <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded mb-2">
                    🎭 게스트 모드 | 크루 개수: {availableCrews.length}
                  </div>
                )}
                {availableCrews.map(crew => (
                  <div key={crew.id} className="bg-gradient-to-br from-white to-rose-50/30 rounded-2xl p-4 border border-rose-100">
                    <div className="flex items-center gap-3">
                      {crew.image ? (
                        <img 
                          src={crew.image} 
                          alt={crew.name}
                          className="w-12 h-12 rounded-xl object-cover shadow-lg"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gradient-to-br from-slate-600 to-slate-800 rounded-xl flex items-center justify-center text-white font-bold">
                          {crew.name[0]}
                        </div>
                      )}
                      <div className="flex-1">
                        <h4 className="font-bold text-slate-800">{crew.name}</h4>
                        <p className="text-sm text-slate-600">{crew.description || '크루 설명이 없습니다.'}</p>
                        <p className="text-xs text-slate-500">
                          리더: {crew.leader.name} | 멤버: {crew.memberCount}명
                        </p>
                      </div>
                      <button
                        onClick={() => handleJoinCrew(crew)}
                        className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-all font-semibold"
                      >
                        가입 신청
                      </button>
                    </div>
                  </div>
                ))}
                {availableCrews.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-slate-500">가입 가능한 크루가 없습니다.</p>
                  </div>
                )}
              </div>
            ) : (
              // 크루 가입 시 - 멤버 리스트 표시
              <div className="space-y-3">
                <div className="text-center mb-4">
                  <h4 className="font-bold text-slate-800">{myCrew?.name}</h4>
                  <p className="text-sm text-slate-600">오늘의 크루 나눔 현황</p>
                </div>
                {crewMembers.map(member => (
                  <div 
                    key={member.id}
                    className={`p-4 rounded-2xl border cursor-pointer transition-all ${
                      member.hasSharedToday 
                        ? 'bg-white hover:bg-rose-50 border-rose-200' 
                        : 'bg-gray-50 border-gray-200 opacity-50 cursor-not-allowed'
                    }`}
                    onClick={() => member.hasSharedToday && openMemberMeditation(member)}
                  >
                    <div className="flex items-center gap-3">
                      {member.avatar ? (
                        <img 
                          src={member.avatar} 
                          alt={member.name}
                          className="w-10 h-10 rounded-xl object-cover shadow-lg"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gradient-to-br from-slate-600 to-slate-800 rounded-xl flex items-center justify-center text-white font-bold text-sm">
                          {member.name[0] || 'U'}
                        </div>
                      )}
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-800">{member.name}</h4>
                        <p className="text-sm text-slate-600">{member.church || '교회 정보 없음'}</p>
                      </div>
                      <div className="text-right">
                        {member.hasSharedToday ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                            ✅ 나눔 완료
                          </span>
                        ) : (
                          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full">
                            ⏳ 나눔 대기
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {crewMembers.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-slate-500">크루 멤버가 없습니다.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 멤버 나눔 상세 모달 */}
      {showMemberMeditationModal && selectedMemberMeditation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                {selectedMemberMeditation.user.avatar ? (
                  <img 
                    src={selectedMemberMeditation.user.avatar}
                    alt={selectedMemberMeditation.user.name}
                    className="w-10 h-10 rounded-xl object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 bg-gradient-to-br from-slate-600 to-slate-800 rounded-xl flex items-center justify-center text-white font-bold">
                    {selectedMemberMeditation.user.name[0]}
                  </div>
                )}
                <div>
                  <h3 className="font-bold text-slate-800">{selectedMemberMeditation.user.name}</h3>
                  <p className="text-sm text-slate-600">
                    {new Date(selectedMemberMeditation.createdAt).toLocaleDateString('ko-KR')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowMemberMeditationModal(false)}
                className="text-slate-500 hover:text-slate-700"
              >
                <X size={24} />
              </button>
            </div>

            {/* 말씀 */}
            <div className="mb-6 p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl">
              <div className="flex items-center gap-2 mb-3">
                <BookOpen size={18} className="text-amber-600" />
                <span className="font-semibold text-amber-800">{selectedMemberMeditation.verseReference}</span>
              </div>
              <p className="text-slate-700 leading-relaxed">{selectedMemberMeditation.verseText}</p>
              <p className="text-sm text-amber-700 mt-2">📖 {selectedMemberMeditation.readingCount}번 읽음</p>
            </div>

            {/* 4단계 내용 */}
            <div className="space-y-4">
              {selectedMemberMeditation.godsWill && (
                <div className="p-4 bg-blue-50 rounded-2xl">
                  <h4 className="font-semibold text-blue-800 mb-2">하나님의 뜻</h4>
                  <p className="text-slate-700 text-sm">{selectedMemberMeditation.godsWill}</p>
                </div>
              )}

              {selectedMemberMeditation.growthChange && (
                <div className="p-4 bg-green-50 rounded-2xl">
                  <h4 className="font-semibold text-green-800 mb-2">변화와 성장</h4>
                  <p className="text-slate-700 text-sm">{selectedMemberMeditation.growthChange}</p>
                </div>
              )}

              {selectedMemberMeditation.prayer && (
                <div className="p-4 bg-purple-50 rounded-2xl">
                  <h4 className="font-semibold text-purple-800 mb-2">기도</h4>
                  <p className="text-slate-700 text-sm">{selectedMemberMeditation.prayer}</p>
                  <p className="text-xs text-purple-600 mt-2">
                    {selectedMemberMeditation.prayerShareType === 'public' && '크루 공유'}
                    {selectedMemberMeditation.prayerShareType === 'request' && '중보기도 요청'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 크루 멤버 나눔 상태 모달 */}
      {showCrewMemberStatusModal && createPortal(
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCrewMemberStatusModal(false);
            }
          }}
        >
          <div className="min-h-screen flex items-center justify-center p-4 py-8">
            <div
              className="bg-white rounded-2xl w-full max-w-lg shadow-2xl relative transform transition-all scale-100 opacity-100 my-auto max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* 헤더 */}
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-slate-800">
                    {currentModalCrew ? '🎯 오늘의 나눔 현황' : '👥 크루 가입하기'}
                  </h3>
                  <button
                    onClick={() => setShowCrewMemberStatusModal(false)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <X size={20} className="text-slate-400" />
                  </button>
                </div>
                <p className="text-sm text-slate-600 mt-2">
                  {currentModalCrew 
                    ? `${currentModalCrew.name} 크루 멤버들의 오늘 묵상 나눔 상태를 확인해보세요`
                    : '묵상을 완료하셨네요! 이제 크루에 가입해서 함께 나눔해보세요'
                  }
                </p>
              </div>

              {/* 멤버 목록 */}
              <div className="p-6">
                {crewMemberStatuses.length === 0 ? (
                  <div className="text-center py-8">
                    {currentModalCrew ? (
                      <>
                        <Users size={48} className="text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500">크루 멤버 정보를 불러오는 중...</p>
                      </>
                    ) : (
                      <>
                        <Users size={48} className="text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-600 font-medium mb-2">아직 크루에 가입하지 않으셨네요!</p>
                        <p className="text-slate-500 text-sm mb-4">크루에 가입하여 함께 묵상을 나누어보세요.</p>
                        <button
                          onClick={() => {
                            setShowCrewMemberStatusModal(false);
                            setActiveTab('crews');
                          }}
                          className="px-6 py-3 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-xl font-semibold transition-all duration-200"
                        >
                          크루 둘러보기
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {crewMemberStatuses.map((member, index) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200 hover:bg-slate-100 transition-colors cursor-pointer"
                        onClick={() => member.hasSharedToday && loadMemberMeditationDetail(member)}
                        title={member.hasSharedToday ? '클릭하여 묵상 내용 보기' : '아직 묵상을 완료하지 않았습니다'}
                      >
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <div className="w-10 h-10 bg-gradient-to-br from-slate-400 to-slate-600 rounded-full flex items-center justify-center">
                              {member.avatar ? (
                                <img src={member.avatar} alt={member.name} className="w-10 h-10 rounded-full object-cover" />
                              ) : (
                                <User className="text-white" size={16} />
                              )}
                            </div>
                            {member.role === 'leader' && (
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-500 rounded-full flex items-center justify-center">
                                <span className="text-[8px] text-white">👑</span>
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-800">{member.name}</p>
                              {/* 100번 달성 메달 (100회 이상만 표시) */}
                              {member.totalMeditationCount && member.totalMeditationCount >= 100 && (
                                <div className="flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-full text-[10px] font-bold shadow-sm">
                                  <Trophy size={10} />
                                  <span>100회</span>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <p className="text-xs text-slate-500">
                                {member.role === 'leader' ? '크루장' : '멤버'}
                              </p>
                              {/* 총 묵상 횟수 (기본으로 표시) */}
                              {member.totalMeditationCount !== undefined && (
                                <span className="text-xs text-slate-600 font-medium">
                                  • {member.totalMeditationCount}회
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          {member.hasSharedToday ? (
                            <>
                              {member.completedAt && (
                                <span className="text-sm font-medium text-green-600">
                                  {new Date(member.completedAt).toLocaleTimeString('ko-KR', { 
                                    hour: '2-digit', 
                                    minute: '2-digit',
                                    hour12: false 
                                  })}
                                </span>
                              )}
                              <div className="flex items-center gap-2 text-green-600">
                                <CheckCircle size={16} />
                                <span className="text-sm font-medium">완료</span>
                              </div>
                            </>
                          ) : (
                            <div className="flex items-center gap-2 text-slate-400">
                              <div className="w-4 h-4 border-2 border-slate-300 rounded-full"></div>
                              <span className="text-sm">대기중</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* 통계 */}
                {crewMemberStatuses.length > 0 && (
                  <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-700 font-medium">오늘의 참여율</p>
                        <p className="text-xs text-blue-600">
                          {crewMemberStatuses.filter(m => m.hasSharedToday).length}명 / {crewMemberStatuses.length}명 완료
                        </p>
                      </div>
                      <div className="text-2xl font-bold text-blue-600">
                        {Math.round((crewMemberStatuses.filter(m => m.hasSharedToday).length / crewMemberStatuses.length) * 100)}%
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* 하단 버튼 */}
              <div className="p-6 border-t border-slate-100">
                <button
                  onClick={() => setShowCrewMemberStatusModal(false)}
                  className="w-full bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white font-medium py-3 px-4 rounded-xl transition-all duration-200"
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 멤버 묵상 상세 모달 */}
      {showMemberMeditationDetail && selectedMemberForDetail && memberMeditationDetail && createPortal(
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowMemberMeditationDetail(false);
            }
          }}
        >
          <div className="min-h-screen flex items-center justify-center p-4 py-8">
            <div
              className="bg-white rounded-2xl w-full max-w-lg shadow-2xl relative transform transition-all scale-100 opacity-100 my-auto max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* 헤더 */}
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-slate-400 to-slate-600 rounded-full flex items-center justify-center">
                      {selectedMemberForDetail.avatar ? (
                        <img src={selectedMemberForDetail.avatar} alt={selectedMemberForDetail.name} className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <User className="text-white" size={20} />
                      )}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-800">
                        {selectedMemberForDetail.name}님의 묵상
                      </h3>
                      <p className="text-sm text-slate-600">
                        {new Date(memberMeditationDetail.date).toLocaleDateString('ko-KR')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* 공유 버튼 */}
                    <button
                      onClick={() => shareMemberMeditation(selectedMemberForDetail, memberMeditationDetail)}
                      className="p-2 hover:bg-blue-100 rounded-lg transition-colors group"
                      title="공유하기"
                    >
                      <Share2 size={20} className="text-slate-400 group-hover:text-blue-500" />
                    </button>
                    <button
                      onClick={() => setShowMemberMeditationDetail(false)}
                      className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                      <X size={20} className="text-slate-400" />
                    </button>
                  </div>
                </div>
              </div>

              {/* 묵상 내용 */}
              <div className="p-6 space-y-6">
                {/* 말씀 */}
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
                  <h4 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
                    <BookOpen size={16} />
                    오늘의 말씀
                  </h4>
                  <p className="text-sm font-semibold text-amber-700 mb-1">
                    {memberMeditationDetail.verseReference}
                  </p>
                  <p className="text-sm text-amber-600 leading-relaxed">
                    {memberMeditationDetail.verseText}
                  </p>
                </div>

                {/* 하나님이 원하시는 것 */}
                {memberMeditationDetail.godsWill && (
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                    <h4 className="font-bold text-blue-800 mb-2">💭 하나님이 원하시는 것은?</h4>
                    <p className="text-sm text-blue-700 leading-relaxed">
                      {memberMeditationDetail.godsWill}
                    </p>
                  </div>
                )}

                {/* 변화/성장할 부분 */}
                {memberMeditationDetail.growthChange && (
                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <h4 className="font-bold text-green-800 mb-2">🌱 변화/성장할 부분은?</h4>
                    <p className="text-sm text-green-700 leading-relaxed">
                      {memberMeditationDetail.growthChange}
                    </p>
                  </div>
                )}

                {/* 기도 내용 */}
                {memberMeditationDetail.prayer && (
                  <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold text-purple-800 flex items-center gap-2">
                        🙏 기도 내용
                      </h4>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        memberMeditationDetail.prayerShareType === 'public' 
                          ? 'bg-green-100 text-green-700' 
                          : memberMeditationDetail.prayerShareType === 'request'
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-slate-100 text-slate-700'
                      }`}>
                        {memberMeditationDetail.prayerShareType === 'public' && '공개'}
                        {memberMeditationDetail.prayerShareType === 'private' && '비공개'}
                        {memberMeditationDetail.prayerShareType === 'request' && '중보기도 요청'}
                      </span>
                    </div>
                    <p className="text-sm text-purple-700 leading-relaxed">
                      {memberMeditationDetail.prayer}
                    </p>
                  </div>
                )}
              </div>

              {/* 하단 버튼 */}
              <div className="p-6 border-t border-slate-100">
                {/* 로그인하지 않은 사용자에게 로그인 유도 */}
                {!isAuthenticated && (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                        <User className="text-white" size={16} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-blue-800">더 많은 기능을 이용해보세요!</h4>
                        <p className="text-sm text-blue-600">로그인하면 나만의 묵상을 기록하고 크루에 참여할 수 있습니다.</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setShowMemberMeditationDetail(false);
                        setShowAuthModal(true);
                        setAuthMode('login');
                      }}
                      className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-all duration-200 text-sm"
                    >
                      로그인하기
                    </button>
                  </div>
                )}
                
                <button
                  onClick={() => setShowMemberMeditationDetail(false)}
                  className="w-full bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white font-medium py-3 px-4 rounded-xl transition-all duration-200"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      </div> {/* 모바일 스케일링 컨테이너 끝 */}

      {/* 하단 네비게이션 - 묵상 페이지에서만 숨김 */}
      {!showMeditation && (
        <nav className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-md border-t border-orange-100/50 shadow-2xl z-50">
          <div className="max-w-md mx-auto px-2 sm:px-4">
            <div className="flex items-center justify-around py-2 sm:py-3">
              {[
                { id: 'home', icon: Home, label: '홈' },
                { id: 'crews', icon: Users, label: '크루' },
                { id: 'feed', icon: MessageCircle, label: '피드' },
                { id: 'prayer', icon: Clock, label: '기도' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    // 피드 탭 클릭 시 데이터 새로고침
                    if (tab.id === 'feed') {
                      loadFeedData();
                    }
                    // 크루 탭 클릭 시 데이터 새로고침
                    if (tab.id === 'crews') {
                      console.log('🔄 크루 탭 클릭 - 데이터 새로고침');
                      loadCrewData();
                    }
                  }}
                  className={`flex flex-col items-center justify-center py-1 sm:py-2 px-2 sm:px-3 rounded-lg sm:rounded-xl transition-all ${
                    activeTab === tab.id
                      ? 'text-rose-600 bg-rose-50'
                      : 'text-slate-600 hover:text-rose-500 hover:bg-rose-25'
                  }`}
                >
                  <tab.icon size={18} className="sm:w-5 sm:h-5 mb-0.5 sm:mb-1" />
                  <span className="text-xs font-medium">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </nav>
      )}

      {/* 크루 권한 이관 모달 */}
      {showLeadershipTransferModal && selectedCrewForTransfer && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowLeadershipTransferModal(false);
            }
          }}
        >
          <div className="min-h-screen flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-auto">
              {/* 헤더 */}
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">
                      {shouldLeaveAfterTransfer ? '권한 이관 후 나가기' : '크루장 권한 이관'}
                    </h2>
                    <p className="text-sm text-slate-600 mt-1">
                      "{selectedCrewForTransfer.name}" 크루의 크루장 권한을 다른 멤버에게 이관합니다.
                      {shouldLeaveAfterTransfer && (
                        <span className="block mt-1 text-rose-600 font-medium">
                          ⚠️ 권한 이관 후 자동으로 크루에서 나갑니다.
                        </span>
                      )}
                    </p>
                  </div>
                  <button 
                    onClick={() => {
                      setShowLeadershipTransferModal(false);
                      setShouldLeaveAfterTransfer(false);
                    }}
                    className="text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              {/* 멤버 목록 */}
              <div className="p-6">
                <div className="space-y-3">
                  <label className="block text-sm font-semibold text-slate-700 mb-3">
                    새로운 크루장을 선택해주세요
                  </label>
                  {crewMembers.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Users size={48} className="mx-auto mb-3 text-slate-300" />
                      <p>이관할 수 있는 멤버가 없습니다.</p>
                      <p className="text-sm">크루에 다른 멤버가 있어야 권한을 이관할 수 있습니다.</p>
                    </div>
                  ) : (
                    crewMembersForTransfer.map((member) => (
                      <label
                        key={member.id}
                        className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          selectedNewLeader === member.id
                            ? 'border-rose-500 bg-rose-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="newLeader"
                          value={member.id}
                          checked={selectedNewLeader === member.id}
                          onChange={(e) => setSelectedNewLeader(e.target.value)}
                          className="sr-only"
                        />
                        <div className="flex items-center space-x-3 flex-1">
                          <div className="w-10 h-10 bg-gradient-to-r from-rose-400 to-pink-500 rounded-full flex items-center justify-center text-white font-bold">
                            {member.name.charAt(0)}
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-slate-800">{member.name}</div>
                            {member.church && (
                              <div className="text-sm text-slate-500">{member.church}</div>
                            )}
                          </div>
                          {selectedNewLeader === member.id && (
                            <div className="w-6 h-6 bg-rose-500 rounded-full flex items-center justify-center">
                              <CheckCircle size={16} className="text-white" />
                            </div>
                          )}
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* 하단 버튼 */}
              <div className="p-6 border-t border-slate-100">
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowLeadershipTransferModal(false);
                      setShouldLeaveAfterTransfer(false);
                    }}
                    className="flex-1 py-3 px-4 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl font-semibold transition-colors"
                  >
                    취소
                  </button>
                  <button
                    onClick={handleTransferLeadership}
                    disabled={!selectedNewLeader || isTransferringLeadership}
                    className="flex-1 py-3 px-4 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 disabled:from-slate-300 disabled:to-slate-400 text-white rounded-xl font-semibold transition-all disabled:cursor-not-allowed"
                  >
                    {isTransferringLeadership 
                      ? (shouldLeaveAfterTransfer ? '이관 및 나가기 중...' : '이관 중...') 
                      : (shouldLeaveAfterTransfer ? '이관 후 나가기' : '권한 이관')
                    }
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 가입 신청 관리 모달 */}
      {showJoinRequestsModal && selectedCrewDetail && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10000] flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowJoinRequestsModal(false);
            }
          }}
        >
          <div 
            className="bg-white rounded-2xl w-full max-w-md shadow-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 모달 헤더 */}
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                  <UserPlus size={24} className="text-rose-500" />
                  가입 신청 관리
                </h3>
                <button 
                  onClick={() => setShowJoinRequestsModal(false)} 
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
              <p className="text-sm text-slate-500 mt-2">"{selectedCrewDetail.name}" 크루의 가입 신청 목록</p>
            </div>

            {/* 모달 본문 */}
            <div className="p-6">
              {isLoadingRequests ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-500 mx-auto"></div>
                  <p className="text-slate-600 mt-4">로딩 중...</p>
                </div>
              ) : joinRequests.length === 0 ? (
                <div className="text-center py-8">
                  <UserPlus size={48} className="text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">대기 중인 가입 신청이 없습니다.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {joinRequests.map((request: any) => (
                    <div 
                      key={request.id}
                      className="border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-rose-400 to-pink-400 rounded-full flex items-center justify-center text-white font-bold">
                          {request.users.name.charAt(0)}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-slate-800">{request.users.name}</h4>
                          <p className="text-sm text-slate-500">{request.users.church || '교회 정보 없음'}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            신청일: {new Date(request.createdAt).toLocaleDateString('ko-KR')}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleJoinRequestAction(request.id, 'APPROVED', request.users.name)}
                          className="flex-1 py-2 px-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                        >
                          <UserCheck size={18} />
                          승인
                        </button>
                        <button
                          onClick={() => handleJoinRequestAction(request.id, 'REJECTED', request.users.name)}
                          className="flex-1 py-2 px-4 bg-gradient-to-r from-slate-400 to-slate-500 hover:from-slate-500 hover:to-slate-600 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                        >
                          <UserX size={18} />
                          거부
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 모달 푸터 */}
            <div className="p-6 border-t border-slate-200">
              <button
                onClick={() => setShowJoinRequestsModal(false)}
                className="w-full py-3 px-4 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl font-semibold transition-colors"
              >
                닫기
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 기도 목표량 설정 모달 */}
      {showPrayerGoalModal && createPortal(
        <PrayerGoalModal
          isOpen={showPrayerGoalModal}
          onClose={() => setShowPrayerGoalModal(false)}
          onGoalUpdated={handlePrayerGoalUpdated}
        />,
        document.body
      )}

      {/* 버전 정보 (우측 하단) - 묵상 페이지에서는 위치 조정 */}
      <div className={`fixed right-4 z-10 ${showMeditation ? 'bottom-4' : 'bottom-16'}`}>
        <div className="bg-slate-800/80 text-white text-xs px-2 py-1 rounded-lg backdrop-blur-sm">
          <div className="flex flex-col items-end">
            <span className="font-semibold">v3.6.1</span>
            <span className="text-[10px] text-slate-300 opacity-70">2025.10.06 22:00</span>
          </div>
        </div>
      </div>

      {/* 서비스 안정화 공지 팝업 */}
      <MaintenanceNotice />
    </div>
  );
};

export default ChurchApp;